import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import QRCode from 'qrcode';
import ForceGraph2D from 'react-force-graph-2d';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import './styles.css';

// --- SYSTEM STABILITY: Suppress WebSocket errors and KILL the error overlay ---
if (typeof window !== 'undefined') {
  console.log("🛡️ SAFEGUARD APP INITIALIZING...");
  
  // 1. Intercept WebSocket errors
  window.addEventListener('error', (e) => {
    if (e.message && (e.message.includes('WebSocket') || e.message.includes('SecurityError'))) {
      console.warn("⚠️ Suppressed WebSocket connection error to prevent UI block.");
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });

  // 2. Kill the React Error Overlay (Blocking screen)
  const style = document.createElement('style');
  style.innerHTML = `
    iframe[style*="z-index: 2147483647"] { display: none !important; }
    #webpack-dev-server-client-overlay { display: none !important; }
  `;
  document.head.appendChild(style);
}

// --- UI CONTEXT ---
type PromptOpts = { placeholder?: string; defaultValue?: string; confirmText?: string; requireExact?: string; danger?: boolean };
type ConfirmOpts = { confirmText?: string; danger?: boolean };
const UIContext = React.createContext<{
  toast: (msg: string, type?: 'success' | 'error' | 'info') => void;
  confirm: (title: string, msg: string, onConfirm: () => void, opts?: ConfirmOpts) => void;
  alert: (title: string, msg: string) => void;
  prompt: (title: string, msg: string, onSubmit: (value: string) => void, opts?: PromptOpts) => void;
} | null>(null);

const useUI = () => {
  const context = React.useContext(UIContext);
  if (!context) throw new Error("useUI must be used within a UIProvider");
  return context;
};

const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<{ id: number; msg: string; type: string }[]>([]);
  const [modal, setModal] = useState<any>(null);
  const [field, setField] = useState('');

  const toast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  };

  const confirm = (title: string, msg: string, onConfirm: () => void, opts: ConfirmOpts = {}) => {
    setModal({ title, msg, type: 'confirm', onConfirm, ...opts });
  };
  const alert = (title: string, msg: string) => setModal({ title, msg, type: 'alert' });
  const prompt = (title: string, msg: string, onSubmit: (value: string) => void, opts: PromptOpts = {}) => {
    setField(opts.defaultValue || '');
    setModal({ title, msg, type: 'prompt', onSubmit, ...opts });
  };

  const close = () => setModal(null);
  const submit = () => {
    if (modal?.type === 'prompt') {
      if (modal.requireExact && field !== modal.requireExact) return; // guarded confirm text
      if (modal.onSubmit) modal.onSubmit(field);
    } else if (modal?.onConfirm) {
      modal.onConfirm();
    }
    close();
  };
  const primaryDisabled = modal?.type === 'prompt' && (
    (modal.requireExact && field !== modal.requireExact) || (!modal.requireExact && !field.trim())
  );

  return (
    <UIContext.Provider value={{ toast, confirm, alert, prompt }}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast ${t.type}`}>
            <div className="toast-message">{t.msg}</div>
            <div className="toast-close" onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}>×</div>
          </div>
        ))}
      </div>
      {modal && (
        <div className="ui-modal-overlay" onClick={close}>
          <div className="ui-modal" onClick={e => e.stopPropagation()}>
            <div className="ui-modal-header">
              <h3>{modal.title}</h3>
              <div style={{cursor:'pointer', fontSize:'1.5rem'}} onClick={close}>×</div>
            </div>
            <div className="ui-modal-body" style={{whiteSpace:'pre-line'}}>
              {modal.msg}
              {modal.type === 'prompt' && (
                <input autoFocus value={field} placeholder={modal.placeholder || ''}
                       onChange={e => setField(e.target.value)}
                       onKeyDown={e => { if (e.key === 'Enter' && !primaryDisabled) submit(); }}
                       style={{ width:'100%', marginTop:12, padding:'10px', borderRadius:8, border:'1px solid #cfd6dd' }} />
              )}
            </div>
            <div className="ui-modal-footer">
              {(modal.type === 'confirm' || modal.type === 'prompt') && (
                <button className="btn-secondary" onClick={close}>Cancel</button>
              )}
              <button onClick={submit} disabled={primaryDisabled}
                      style={modal.danger ? { background:'var(--danger, #c0392b)' } : undefined}>
                {modal.confirmText || (modal.type === 'alert' ? 'OK' : 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </UIContext.Provider>
  );
};

const API_URL = process.env.REACT_APP_PFB_ALLERGENS_API_URL || 'http://localhost:3955';
const CORE_URL = 'http://localhost:3954';

axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axios.interceptors.response.use(res => res, err => {
  if (err.response?.status === 401) { localStorage.removeItem('token'); window.location.reload(); }
  return Promise.reject(err);
});

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXTUAL HELP
// Users (RDNs, technicians and C-levels) told us the system is powerful but dense
// and it's easy to get lost. Every screen carries a plain-language help banner
// (what it is + what you can do here + who it's for) and the sidebar has hover
// tooltips. Content lives in ONE place so it stays consistent.
// ─────────────────────────────────────────────────────────────────────────────
const SCREEN_HELP: Record<string, { nav: string; title: string; what: string; who?: string; do?: string[] }> = {
  dash: { nav: 'Program overview at a glance',
    title: 'Dashboard', who: 'Everyone',
    what: 'Your starting point: a snapshot of the whole program — how many patients and products are loaded, how many active safety rules exist, and recent activity.',
    do: ['Read the top numbers to gauge overall scale and health.', 'Use the sidebar on the left to jump to any area.'] },
  op_intake: { nav: 'Suggest a new allergen/condition for RDN review',
    title: 'Suggest a Medical Item', who: 'Operations',
    what: 'Propose a new allergen or condition. It goes to the RDN review queue — an RDN approves, adjusts or rejects it before it affects any safety decision. Products, ingredients and preferences you can create directly (no RDN needed).',
    do: ['Type the name; the system flags duplicates/typos before you send.', 'Send it — an RDN takes it from there.'] },
  alert_routing: { nav: 'Configure which condition alerts which team, at what severity',
    title: 'Alert Routing', who: 'Admin',
    what: 'The table that decides, for each operational condition SafeGuard detects, which team gets paged and at what severity. Team names are validated against the incident manager, so a typo can’t silently drop alerts. Thresholds themselves live in the engine (not edited here yet).',
    do: ['Change a team or severity with the dropdowns.', 'Add a condition, or remove one with ×.', 'Save — unknown teams are rejected. critical/high page; medium/low/info are logged only.'] },
  diets: { nav: 'Manage composite diet/policy lists (Vegan, etc.)',
    title: 'Diets & Policy Lists', who: 'RDN & Admin',
    what: 'Define composite exclusion lists like Vegan — one selection that stands for many allergens. A patient assigned a diet is blocked from every allergen in the list AND all of its derivatives (via the allergen hierarchy: excluding Milk also covers cheese, whey, butter). Best-effort seed lists exist; RDNs curate them here.',
    do: ['Open a diet to see what it excludes.', 'Add or remove an allergen with the picker / chip ×.', 'Create a new diet with “New Diet”. Every change is audited.'] },
  tests: { nav: 'Run a safety check on demand',
    title: 'Operational Checks', who: 'Operations & RDNs',
    what: 'Run a safety check yourself, right now: “Can this patient safely receive this product (or this whole shipment)?” The engine checks allergens, medical conditions and preferences.',
    do: ['Pick a patient and a product/shipment to test before you ship.', 'A RED result means block; review the reason shown.'] },
  members: { nav: 'Patients and their clinical profiles',
    title: 'Patients', who: 'RDNs & intake',
    what: 'The people you serve. Each patient’s allergies, medical conditions and food preferences are what every safety decision is based on.',
    do: ['Add or edit a patient and their restrictions.', 'Import many patients at once from a CSV.', 'Open a patient to see their full clinical profile.'] },
  skus: { nav: 'Your food product catalog',
    title: 'Products (SKU)', who: 'Operations & RDNs',
    what: 'Your food catalog. Each product’s ingredients and nutrition are matched against patient restrictions to decide what is safe.',
    do: ['Add a product and its ingredients.', 'Use the 🪄 button to let AI estimate ingredients & nutrition.', 'Sort/search the catalog; “Last Updated / By” shows who changed each.'] },
  partners: { nav: 'Suppliers / partner organizations',
    title: 'Partners', who: 'Operations',
    what: 'The suppliers and partner organizations that provide the products in your catalog.',
    do: ['Add or edit a partner and its contact details.'] },
  explorer: { nav: 'Trace WHY something is (un)safe',
    title: 'Clinical Checks (Explorer)', who: 'RDNs',
    what: 'Explore the reasoning behind a decision: trace the exact path — patient → allergy → ingredient → product — that makes something safe or unsafe.',
    do: ['Look up a patient or a product to see its connections.', 'Use this to explain or double-check a block.'] },
  sync: { nav: 'Approve new/AI-found clinical data',
    title: 'Clinical Review Queue', who: 'RDNs (approver)',
    what: 'New or AI-discovered allergens, ingredients and conditions wait HERE for a dietitian to approve. Nothing here affects safety decisions until you approve it — that’s the safety gate.',
    do: ['Review each item; quality flags warn of possible duplicates/negations.', 'Approve to make it live, or Reject.', '“External Sync” (same area) pulls data from connected systems.'] },
  ingredients: { nav: 'Ingredients and their allergens',
    title: 'Ingredients', who: 'RDNs',
    what: 'The building blocks of products. Each ingredient is linked to the allergens it contains — cleaner ingredient data means more accurate safety matching.',
    do: ['Add/edit an ingredient and the allergens it carries.', 'The system warns you if a name looks like a duplicate.'] },
  allergens: { nav: 'The controlled allergen vocabulary',
    title: 'Allergens', who: 'RDNs',
    what: 'The master list of things patients react to. Keeping it clean is critical — as you type, the system blocks duplicates, negations (“No Milk”) and compound entries (“Milk and Soy”).',
    do: ['Add a new allergen (bad/duplicate names are blocked automatically).', 'Click a column header to sort (Name, Priority, Last Updated, By).', 'Search by name OR by who last changed it.'] },
  conditions: { nav: 'Medical conditions & nutrient limits',
    title: 'Conditions', who: 'RDNs',
    what: 'Medical conditions and the nutritional limits they impose (e.g., maximum sodium for kidney disease). These drive nutrition-based blocking.',
    do: ['Set per-condition limits (sodium, carbs, potassium, etc.).', 'Link restricted ingredients to a condition.'] },
  ai_lab: { nav: 'Ask AI to analyze an ingredient',
    title: 'AI Ingredient Lab', who: 'RDNs',
    what: 'A sandbox to ask the AI to analyze an ingredient and suggest the allergens it likely contains, before you add it to the catalog.',
    do: ['Enter an ingredient and review the AI’s suggested allergens.', 'Send good results to the review queue.'] },
  ai_graph: { nav: 'Find/merge duplicates, repair the graph',
    title: 'AI Graph Engineering', who: 'Admins / power RDNs',
    what: 'Advanced clean-up tools: find and merge duplicate nodes and repair gaps in the knowledge graph. Powerful — every change is audited.',
    do: ['Scan for duplicate clusters and merge the real ones.', 'Prefer the guided “Data Quality” screen for everyday clean-up.'] },
  ai_sentinel: { nav: 'Daily automated audit (Sentinel)',
    title: 'AI Sentinel (Daily Audit)', who: 'Admins / Auditors',
    what: 'An automated watchdog that reviews the last 24 hours of activity every day and flags anomalies for you.',
    do: ['Read the latest daily report.', 'Trigger a run manually if needed.'] },
  ai_audit: { nav: 'AI review of clinical data quality',
    title: 'AI Clinical Audit', who: 'RDNs / Auditors',
    what: 'An AI pass over clinical data and decisions to surface quality issues and inconsistencies.',
    do: ['Review flagged items and act on the recommendations.'] },
  graph: { nav: 'Visual map of the safety graph',
    title: 'Knowledge Graph', who: 'Everyone (visual)',
    what: 'A visual map of how patients, allergens, conditions, ingredients and products connect. This graph is the “source of truth” the safety engine reasons over.',
    do: ['Toggle node types on/off and explore the connections.'] },
  governance: { nav: 'Clinical identity governance',
    title: 'Identity Governance', who: 'Admins',
    what: 'Manage clinical identities and how records are consolidated and traced across the system.',
    do: ['Review identity lineage and governance actions.'] },
  compliance: { nav: 'Regulatory / HIPAA posture',
    title: 'Compliance', who: 'C-levels / Admins',
    what: 'A summary of the system’s regulatory and HIPAA-oriented controls and posture — useful for leadership and audits.',
    do: ['Review the controls in place and their status.'] },
  logs: { nav: 'Raw tamper-evident action log',
    title: 'Audit Logs', who: 'Admins / Auditors',
    what: 'The raw, tamper-evident log of every action in the system. For a friendlier view of who edited clinical data, use “Change Log”.',
    do: ['Scan recent actions.', 'For before→after detail, open Change Log.'] },
  change_log: { nav: 'Who changed what, and before→after',
    title: 'Change Log', who: 'RDNs / Admins / Auditors',
    what: 'Who changed which allergen, ingredient, condition or product — when, and the exact before → after. Your first stop to investigate a bad or accidental edit.',
    do: ['Filter by entity, user, action or date.', 'Expand any row to see the before/after values.'] },
  safety_audit: { nav: 'Every safety check the engine ran',
    title: 'Safety-Check Forensics', who: 'Admins / Auditors',
    what: 'A searchable record of every safety check the engine performed — who asked, for which patient, what was asked and what was answered. Use it to reconstruct exactly what happened in an incident.',
    do: ['Filter by patient, requester, endpoint or decision.', 'Use “Unknown-member checks” to find the dangerous “resolved to nobody” cases.'] },
  data_quality: { nav: 'Find & fix duplicate/bad data safely',
    title: 'Data Quality', who: 'RDNs / Admins',
    what: 'Find and safely fix messy data: duplicate or malformed allergens/ingredients, and — most importantly — patients whose restrictions were mis-typed so they silently do nothing (a real safety risk).',
    do: ['Start with the red “at-risk patients” section.', 'Use Relink/Merge for guided, audited fixes.', 'Fuzzy matches are review-only — they include distinct allergens.'] },
  notifications: { nav: 'Alerts and how they’re routed',
    title: 'Notifications', who: 'Admins',
    what: 'System alerts and the rules that route them to the right people and teams.',
    do: ['Configure teams and where alerts are delivered.'] },
  lifecycle: { nav: 'Retention & PHI purge (right-to-forget)',
    title: 'Data Lifecycle', who: 'Admins',
    what: 'Data retention and privacy tools, including permanent PHI purge for “right to be forgotten” requests. These actions are irreversible and audited.',
    do: ['Purge a patient’s PHI when legally required (double-confirmed).'] },
  users: { nav: 'Create staff logins & roles',
    title: 'User Access', who: 'Admins',
    what: 'Create logins for your staff and assign a role that controls what they can see and do (Admin, RDN, Auditor, User, Operations). Operations is query-only: it runs checks and suggests new allergens/conditions for RDN review, with no access to patient records (PHI).',
    do: ['Add a user and set their role.', 'Reset a password by entering a new one.'] },
  service_accounts: { nav: 'Machine-to-machine (integration) access',
    title: 'Service Accounts', who: 'Admins / IT',
    what: 'Non-human access for external systems that integrate with SafeGuard (with IP allow-listing). These are for machines and integrations, not people.',
    do: ['Register a system, copy its secret once, and set IP whitelisting.', 'Watch the expiry traffic-light so integrations don’t break.'] },
  mfa_setup: { nav: 'Turn on 2-factor for your login',
    title: 'My Security (MFA)', who: 'Everyone',
    what: 'Turn on two-factor authentication (a code from your phone) for your own account to keep it secure.',
    do: ['Scan the QR code with an authenticator app and confirm.'] },
  settings: { nav: 'System configuration & API keys',
    title: 'Settings', who: 'Admins',
    what: 'System-wide configuration, including AI provider keys and thresholds. Changes here affect the whole platform.',
    do: ['Update configuration values carefully.'] },
  help: { nav: 'Guides and reference',
    title: 'System Help', who: 'Everyone',
    what: 'Guides and reference material for using SafeGuard.',
    do: ['Browse the topics, or open the full System Manual from the sidebar.'] },
};

// Small inline “ⓘ” with a hover tooltip — drop next to any label or control.
const HelpTip = ({ text }: { text: string }) => (
  <span title={text} style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, borderRadius: '50%', border: '1px solid #b9c2cc',
    color: '#7b8794', fontSize: 11, fontWeight: 700, cursor: 'help', marginLeft: 6, userSelect: 'none'
  }}>i</span>
);

// Per-screen help banner. Collapsible; the collapsed state is remembered per user.
const ScreenHelp = ({ active }: { active: string }) => {
  const h = SCREEN_HELP[active];
  const [open, setOpen] = useState(true);
  useEffect(() => {
    setOpen(localStorage.getItem(`help_hidden_${active}`) !== '1');
  }, [active]);
  if (!h) return null;
  const toggle = () => {
    const next = !open; setOpen(next);
    localStorage.setItem(`help_hidden_${active}`, next ? '0' : '1');
  };
  return (
    <div style={{
      background: 'linear-gradient(180deg,#f2f8ff,#eef4fb)', border: '1px solid #d6e4f5',
      borderRadius: 12, padding: open ? '14px 16px' : '8px 16px', marginBottom: 16
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#1f4b73' }}>
          <span>💡 {h.title}</span>
          {h.who && <span style={{ fontSize: '0.68rem', fontWeight: 600, background: '#dbe9fb', color: '#2c5f8f', padding: '2px 8px', borderRadius: 20 }}>For: {h.who}</span>}
        </div>
        <button onClick={toggle} className="btn-secondary" style={{ fontSize: '0.72rem', padding: '3px 10px', whiteSpace: 'nowrap' }}>
          {open ? 'Hide help' : 'What is this?'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#31465c', lineHeight: 1.5 }}>
          <div>{h.what}</div>
          {h.do && h.do.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {h.do.map((d, i) => <li key={i} style={{ marginBottom: 2 }}>{d}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SafeGuard Assistant (Ceres Assistant Blueprint) — Phase 1 web UI.
// A launcher on every screen + a drawer. It reads live data and changes nothing.
// =============================================================================
const asstRenderContent = (text: string, onNav: (route: string) => void) => {
  const nodes: any[] = []; let k = 0;
  const pushText = (s: string) => s.split(/(\*\*[^*]+\*\*)/g).forEach((part) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) nodes.push(<strong key={k++}>{part.slice(2, -2)}</strong>);
    else if (part) nodes.push(<span key={k++}>{part}</span>);
  });
  const re = /\[([^\]]+)\]\((\/[a-z0-9_\/-]+)\)/gi;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) pushText(text.slice(last, m.index));
    const label = m[1], route = m[2];
    nodes.push(<a key={k++} href="#" onClick={(e) => { e.preventDefault(); onNav(route); }} style={{ color: '#6b5bd6', fontWeight: 600 }}>{label}</a>);
    last = re.lastIndex;
  }
  if (last < text.length) pushText(text.slice(last));
  return <span style={{ whiteSpace: 'pre-wrap' }}>{nodes}</span>;
};

const AssistantSettingsCard = () => {
  const [s, setS] = useState<any>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { axios.get(`${API_URL}/assistant/settings`).then(r => setS(r.data)).catch(() => { }); }, []);
  if (!s) return null;
  const set = (k: string, v: any) => { setS({ ...s, [k]: v }); setSaved(false); };
  const roles = ['Admin', 'RDN', 'Auditor', 'User', 'Operations'];
  const toggleRole = (r: string) => {
    const cur = s.allowed_roles || [];
    set('allowed_roles', cur.includes(r) ? cur.filter((x: string) => x !== r) : [...cur, r]);
  };
  const save = async () => { await axios.put(`${API_URL}/assistant/settings`, s); setSaved(true); };
  return (
    <div className="card" style={{ borderTop: '4px solid #6b5bd6' }}>
      <h3 style={{ margin: 0 }}>🤖 SafeGuard Assistant</h3>
      <p style={{ fontSize: '0.75rem', color: '#666' }}>
        An in-app assistant that IS the manual and reads live data as the person asking. It changes nothing —
        no writing tools, no SQL. Every conversation is stored and reviewable by compliance.
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
        <input type="checkbox" checked={!!s.enabled} onChange={e => set('enabled', e.target.checked)} />
        <strong>Enabled</strong> — shows the “Ask the assistant” button on every screen
      </label>
      <div className="form-group">
        <label>Who may use it — {(!s.allowed_roles || s.allowed_roles.length === 0) ? 'Everyone with a session' : 'only the checked roles'}</label>
        <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
          {roles.map(r => (
            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
              <input type="checkbox" checked={(s.allowed_roles || []).includes(r)} onChange={() => toggleRole(r)} /> {r}
            </label>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className="form-group"><label>Model (Azure deployment, blank = default)</label>
          <input value={s.model || ''} onChange={e => set('model', e.target.value)} placeholder="gpt-4o" /></div>
        <div className="form-group"><label>Max steps per question (1–20)</label>
          <input type="number" value={s.max_steps} onChange={e => set('max_steps', parseInt(e.target.value) || 8)} /></div>
        <div className="form-group"><label>Daily token budget per person</label>
          <input type="number" value={s.daily_token_cap} onChange={e => set('daily_token_cap', parseInt(e.target.value) || 300000)} /></div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0', fontSize: '0.85rem' }}>
        <input type="checkbox" checked={!!s.phi_tools_enabled} onChange={e => set('phi_tools_enabled', e.target.checked)} />
        PHI tools (patient lookup &amp; summary — audited, Admin/RDN only)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0', fontSize: '0.85rem' }}>
        <input type="checkbox" checked={!!s.change_requests} onChange={e => set('change_requests', e.target.checked)} />
        Change requests → OpsCenter tickets (see the OpsCenter card below)
      </label>
      <div className="form-group"><label>House rules (appended to the assistant’s prompt)</label>
        <textarea value={s.persona || ''} onChange={e => set('persona', e.target.value)} rows={2} /></div>
      <button className="btn-primary" onClick={save}>Save</button>
      {saved && <span style={{ marginLeft: 10, color: '#28a745' }}>✓ Saved</span>}
    </div>
  );
};

const OpsCenterSettingsCard = () => {
  const [c, setC] = useState<any>(null);
  const [key, setKey] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const load = () => axios.get(`${API_URL}/assistant/opscenter`).then(r => setC(r.data)).catch(() => { });
  useEffect(() => { load(); }, []);
  if (!c) return null;
  const set = (k: string, v: any) => setC({ ...c, [k]: v });
  const save = async () => {
    const body: any = { base_url: c.base_url, change_request_event_type: c.change_request_event_type, enabled: c.enabled };
    if (key) body.api_key = key;
    await axios.put(`${API_URL}/assistant/opscenter`, body); setKey(''); setMsg('✓ Saved'); load();
  };
  const test = async () => {
    setMsg('Testing…');
    if (key || c.base_url) { const body: any = { base_url: c.base_url, change_request_event_type: c.change_request_event_type, enabled: c.enabled }; if (key) body.api_key = key; await axios.put(`${API_URL}/assistant/opscenter`, body); setKey(''); }
    try { const r = await axios.post(`${API_URL}/assistant/opscenter/test`); setMsg((r.data.ok ? '✓ ' : '✗ ') + r.data.message); load(); }
    catch (e: any) { setMsg('✗ ' + (e?.response?.data?.detail || 'test failed')); }
  };
  return (
    <div className="card" style={{ borderTop: '4px solid #2c7be5' }}>
      <h3 style={{ margin: 0 }}>🎫 OpsCenter (change-request tickets)</h3>
      <p style={{ fontSize: '0.75rem', color: '#666' }}>
        Where the assistant’s confirmed change requests become tickets for the dev team. No PHI is ever sent.
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
        <input type="checkbox" checked={!!c.enabled} onChange={e => set('enabled', e.target.checked)} /> Enabled
      </label>
      <div className="form-group"><label>Base URL (without /api/v1 — we append the webhook path)</label>
        <input value={c.base_url || ''} onChange={e => set('base_url', e.target.value)} placeholder="https://opscenter.projectfoodbox.org" /></div>
      <div className="form-group"><label>API key (X-PFB-Api-Key)</label>
        <input type="password" value={key} onChange={e => setKey(e.target.value)} placeholder={c.api_key_set ? '•••••••• (leave blank to keep)' : 'paste key'} /></div>
      <div className="form-group"><label>Event type for change requests</label>
        <input value={c.change_request_event_type || ''} onChange={e => set('change_request_event_type', e.target.value)} placeholder="safeguard.change_request" /></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn-primary" onClick={save}>Save</button>
        <button className="btn-secondary" onClick={test}>Test (no case created)</button>
        {msg && <span style={{ fontSize: '0.8rem', color: msg.startsWith('✗') ? '#c0392b' : '#28a745' }}>{msg}</span>}
      </div>
      {c.last_test_result && <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 6 }}>Last test: {c.last_test_result} — {c.last_test_message} ({c.last_test_at})</div>}
    </div>
  );
};

const AssistantLauncher = ({ activeTab, onNav }: any) => {
  const [avail, setAvail] = useState(false);
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);
  const [threads, setThreads] = useState<any[]>([]);
  const [crs, setCrs] = useState<any[]>([]);
  const [dot, setDot] = useState(false);
  const scrollRef = React.useRef<any>(null);

  useEffect(() => { axios.get(`${API_URL}/assistant/availability`).then(r => setAvail(!!r.data.available)).catch(() => setAvail(false)); }, []);
  // The dot: poll updates while the drawer is closed.
  useEffect(() => {
    if (!avail) return;
    const key = 'assistant_updates_seen';
    const poll = () => {
      if (open) return;
      const since = localStorage.getItem(key) || '';
      axios.get(`${API_URL}/assistant/updates`, { params: since ? { since } : {} })
        .then(r => setDot((r.data.updates || []).length > 0)).catch(() => { });
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => clearInterval(id);
  }, [avail, open]);
  const refetchCrs = (tid?: string) => {
    if (!tid) { setCrs([]); return; }
    axios.get(`${API_URL}/assistant/change-requests`, { params: { thread: tid } }).then(r => setCrs(r.data || [])).catch(() => { });
  };
  useEffect(() => {
    if (open) {
      setDot(false);
      localStorage.setItem('assistant_updates_seen', new Date().toISOString());
      if (!thread) {
        axios.get(`${API_URL}/assistant/threads/active`).then(r => {
          if (r.data.thread) { setThread(r.data.thread); setMessages(r.data.messages || []); refetchCrs(r.data.thread.uuid); }
        }).catch(() => { });
      }
    }
  }, [open]); // eslint-disable-line
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

  if (!avail) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const tmpId = 'tmp' + Date.now();
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text, uuid: tmpId }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/assistant/turn`, { message: text, threadUuid: thread?.uuid, screen: '/' + activeTab });
      setThread(r.data.thread);
      setMessages(prev => [...prev.filter(x => x.uuid !== tmpId), ...r.data.messages]);
      refetchCrs(r.data.thread?.uuid);
    } catch (e: any) {
      setMessages(m => [...m, { role: 'assistant', content: e?.response?.data?.detail || 'Error contacting the assistant.', uuid: 'err' + Date.now() }]);
    } finally { setBusy(false); }
  };
  const newConversation = async () => {
    if (thread?.uuid) { try { await axios.post(`${API_URL}/assistant/threads/${thread.uuid}/archive`); } catch { } }
    setThread(null); setMessages([]); setCrs([]); setShowList(false);
  };
  const openThreads = async () => { const r = await axios.get(`${API_URL}/assistant/threads`); setThreads(r.data || []); setShowList(true); };
  const loadThread = async (uuid: string) => { const r = await axios.get(`${API_URL}/assistant/threads/${uuid}`); setThread(r.data.thread); setMessages(r.data.messages || []); refetchCrs(uuid); setShowList(false); };
  const confirmCr = async (u: string) => { await axios.post(`${API_URL}/assistant/change-requests/${u}/confirm`); refetchCrs(thread?.uuid); };
  const discardCr = async (u: string) => { await axios.post(`${API_URL}/assistant/change-requests/${u}/discard`); refetchCrs(thread?.uuid); };
  const removeThread = async (uuid: string, e: any) => {
    e.stopPropagation();
    await axios.post(`${API_URL}/assistant/threads/${uuid}/delete`);
    setThreads(ts => ts.filter(t => t.uuid !== uuid));
    if (thread?.uuid === uuid) { setThread(null); setMessages([]); }
  };
  const nav = (route: string) => { onNav(route.replace(/^\//, '').split('/')[0]); setOpen(false); };
  const visible = messages.filter(m => m.role !== 'tool');

  return (
    <>
      <button title="Ask the SafeGuard assistant" onClick={() => setOpen(o => !o)} style={{
        position: 'fixed', top: 14, right: 20, zIndex: 1000, background: '#6b5bd6', color: '#fff',
        border: 'none', borderRadius: 24, padding: '9px 16px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
      }}>🤖 Assistant{dot && <span title="A request of yours changed status" style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#ff5a7a', marginLeft: 7, verticalAlign: 'middle' }} />}</button>
      {open && (
        <div style={{ position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(420px,100vw)', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)', zIndex: 1001, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 14px', background: '#6b5bd6', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={showList ? () => setShowList(false) : openThreads} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 700, flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {showList ? '← Back' : ((thread?.title || 'Ask the assistant') + ' ▾')}
            </button>
            <button onClick={newConversation} title="New conversation (the current one is kept)" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, cursor: 'pointer', padding: '4px 8px', fontSize: '0.78rem' }}>New</button>
            <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          {showList ? (
            <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
              {threads.length === 0 && <div style={{ color: '#888', padding: 20, textAlign: 'center' }}>No conversations yet.</div>}
              {threads.map(t => (
                <div key={t.uuid} onClick={() => loadThread(t.uuid)} style={{ padding: '8px 10px', borderBottom: '1px solid #eee', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title || '(untitled)'} {t.status === 'archived' && <em style={{ color: '#aaa' }}>· archived</em>}</span>
                  <button title="Remove from your list (kept for compliance)" onClick={(e) => removeThread(t.uuid, e)} style={{ background: 'transparent', border: 'none', color: '#c00', cursor: 'pointer' }}>×</button>
                </div>
              ))}
            </div>
          ) : (
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#f7f8fb' }}>
              {visible.length === 0 && <div style={{ color: '#888', textAlign: 'center', marginTop: 30, fontSize: '0.9rem' }}>Ask me how a screen works, or about the program’s numbers. I read live data and change nothing.</div>}
              {visible.map((m, i) => {
                if (m.role === 'assistant' && m.tool_name && !m.content) {
                  return <div key={i} style={{ fontSize: '0.72rem', color: '#7b8794', margin: '4px 0' }}>✓ checked <strong>{m.tool_name}</strong>{m.contains_phi && <span style={{ color: '#d9822b' }}> · PHI</span>}</div>;
                }
                if (m.role === 'assistant' && m.screen === 'system') {
                  return <div key={i} style={{ background: '#eaf7ee', border: '1px solid #b7e0c2', borderRadius: 10, padding: '8px 11px', margin: '6px 0', fontSize: '0.82rem', color: '#1e7a38' }}>🔔 {m.content}</div>;
                }
                const mine = m.role === 'user';
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
                    <div style={{ maxWidth: '85%', padding: '8px 11px', borderRadius: 12, fontSize: '0.86rem', lineHeight: 1.45, background: mine ? '#6b5bd6' : '#fff', color: mine ? '#fff' : '#222', border: mine ? 'none' : '1px solid #e2e6ee' }}>
                      {mine ? m.content : asstRenderContent(m.content || '', nav)}
                    </div>
                  </div>
                );
              })}
              {crs.map((c) => (
                <div key={c.uuid} style={{ background: '#fff8ec', border: '1px solid #f0d9a8', borderRadius: 10, padding: 11, margin: '8px 0', fontSize: '0.82rem' }}>
                  <div style={{ fontWeight: 700, color: '#8a6d1f' }}>📝 {c.title}</div>
                  {c.problem && <div style={{ marginTop: 4 }}><strong>Problem:</strong> {c.problem}</div>}
                  {c.proposal && <div style={{ marginTop: 2 }}><strong>Proposal:</strong> {c.proposal}</div>}
                  {c.acceptance && <div style={{ marginTop: 2 }}><strong>Done when:</strong> {c.acceptance}</div>}
                  {(c.screens && c.screens.length > 0) && <div style={{ marginTop: 2, color: '#777' }}>Screens: {c.screens.join(', ')}</div>}
                  {c.status === 'proposed' ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      <button onClick={() => confirmCr(c.uuid)} className="btn-primary" style={{ fontSize: '0.78rem', padding: '4px 10px' }}>Create ticket</button>
                      <button onClick={() => discardCr(c.uuid)} className="btn-secondary" style={{ fontSize: '0.78rem', padding: '4px 10px' }}>Discard</button>
                    </div>
                  ) : (
                    <div style={{ marginTop: 6, fontSize: '0.76rem', color: '#666' }}>
                      {c.status === 'dispatched' && <>✅ Ticket created{c.ops_case_number ? ` — ${c.ops_case_number}` : ''}.</>}
                      {c.status === 'confirmed' && <>⏳ Confirmed{c.dispatch_error ? ` — ${c.dispatch_error}` : ' — sending to OpsCenter…'}</>}
                      {c.status === 'resolved' && <>✔️ Resolved{c.ops_case_number ? ` (${c.ops_case_number})` : ''}.</>}
                      {c.status === 'failed' && <span style={{ color: '#c0392b' }}>⚠️ Failed — {c.dispatch_error}</span>}
                      {c.status === 'discarded' && <>Discarded.</>}
                    </div>
                  )}
                </div>
              ))}
              {busy && <div style={{ color: '#6b5bd6', fontSize: '0.8rem', margin: '6px 2px' }}>Working…</div>}
            </div>
          )}
          {!showList && (
            <div style={{ borderTop: '1px solid #e2e6ee', padding: 8 }}>
              <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask the assistant…  (Enter to send)" rows={2} disabled={busy}
                style={{ width: '100%', resize: 'none', border: '1px solid #ccc', borderRadius: 8, padding: 8, fontSize: '0.86rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              <div style={{ fontSize: '0.66rem', color: '#9aa5b1', padding: '2px 2px 0' }}>Conversations are stored and reviewable by compliance. SafeGuard and its connected systems only.</div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const GraphViz = () => {
  const [data, setData] = useState({ nodes: [], links: [] });
  const [showLabels, setShowLabels] = useState<Record<string, boolean>>({
    Member: true, SKU: true, Ingredient: true, Allergen: true, Condition: true
  });

  useEffect(() => { 
    axios.get(`${API_URL}/graph/viz`).then(res => setData(res.data)).catch(() => {}); 
  }, []);

  const toggleLabel = (label: string) => {
    setShowLabels(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="card" style={{ height: '80vh', padding: 0, overflow: 'hidden', background: '#ffffff', position: 'relative', border: '1px solid #ddd' }}>
      <div style={{ position: 'absolute', zIndex: 10, padding: '20px', color: '#333', pointerEvents: 'none', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', boxSizing: 'border-box' }}>
        <div>
          <h2 style={{ color: '#222', border: 'none', margin: 0 }}>Knowledge Graph Explorer</h2>
          <div style={{ pointerEvents: 'auto', display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
            {Object.keys(showLabels).map(label => (
              <label key={label} title={`Show or hide ${label} nodes in the map. Turn off types you don't need to declutter the view.`} style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', background: 'rgba(255,255,255,0.8)', padding: '4px 8px', borderRadius: '4px', border: '1px solid #eee' }}>
                <input type="checkbox" checked={showLabels[label]} onChange={() => toggleLabel(label)} />
                {label}s
              </label>
            ))}
          </div>
        </div>
      </div>
      <ForceGraph2D
        graphData={data}
        nodeLabel="name"
        nodeAutoColorBy="label"
        linkLabel="type"
        linkDirectionalArrowLength={5}
        linkDirectionalArrowRelPos={1}
        linkColor={() => '#999999'}
        backgroundColor="#ffffff"
        nodeCanvasObject={(node: any, ctx, globalScale) => {
          const label = node.name || node.id;
          const fontSize = 12 / globalScale;
          const shouldShowLabel = showLabels[node.label] || false;
          
          // Draw Circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, 5, 0, 2 * Math.PI, false);
          ctx.fillStyle = node.color || '#444';
          ctx.fill();
          
          if (shouldShowLabel) {
            ctx.font = `${fontSize}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            ctx.fillText(label, node.x, node.y + 10);
          }
        }}
      />
    </div>
  );
};

const AIGraphEngineering = () => {
  const { toast, alert, confirm } = useUI();
  const [jobs, setJobs] = useState<any[]>([]);
  const [repairing, setRepairing] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [systemConfig, setSystemConfig] = useState<any>(null);

  // Filters
  const [filterType, setFilterType] = useState('all'); 
  const [filterId, setFilterId] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(true);
  const [aiModel, setAiModel] = useState('');
  const [maxNodes, setMaxNodes] = useState(50); // CONTROLLED BATCH SIZE

  const [options, setOptions] = useState<{partners:any[], skus:any[], conditions:any[], ingredients:any[]}>({
    partners: [], skus: [], conditions: [], ingredients: []
  });

  const fetchJobs = useCallback(() => {
    axios.get(`${API_URL}/graph/repair/jobs`).then(res => {
      setJobs(res.data.items || []);
      const anyRunning = (res.data.items || []).some((j:any) => j.status === 'RUNNING');
      if (anyRunning) setTimeout(fetchJobs, 3000);
    });
  }, []);

  useEffect(() => { 
    fetchJobs();
    axios.get(`${API_URL}/system/settings`).then(res => {
       setSystemConfig(res.data);
       const provider = 'AZURE';
       const defaultModel = res.data.AZURE_OPENAI_DEPLOYMENT_FAST;
       setAiModel(defaultModel || '');
    });
    axios.get(`${API_URL}/partners`).then(res => setOptions(prev => ({...prev, partners: res.data.items || []})));
    axios.get(`${API_URL}/skus`).then(res => setOptions(prev => ({...prev, skus: res.data.items || []})));
    axios.get(`${API_URL}/conditions`).then(res => setOptions(prev => ({...prev, conditions: res.data.items || []})));
    axios.get(`${API_URL}/ingredients`).then(res => setOptions(prev => ({...prev, ingredients: res.data.items || []})));
  }, [fetchJobs]);

  const runAiRepair = async () => {
    setRepairing(true);
    const payload: any = { 
      unmapped_only: unmappedOnly,
      ai_model: aiModel,
      limit: maxNodes
    };
    if (filterType !== 'all' && filterId) {
      if (filterType === 'partner') payload.partner_id = filterId;
      if (filterType === 'sku') payload.sku_id = filterId;
      if (filterType === 'condition') payload.condition_id = filterId;
      if (filterType === 'ingredient') payload.ingredient_id = filterId;
    }

    try {
      await axios.post(`${API_URL}/graph/ai-repair`, payload);
      alert("Job Started", "Graph repair job started! You can track it in the task monitor.");
      setShowConfig(false);
      fetchJobs();
    } catch {
      toast("Failed to initiate graph engineering job.", "error");
    }
    setRepairing(false);
  };

  const getModelOptions = () => {
    if (!systemConfig) return [];
    const provider = 'AZURE';
    if (provider === 'GEMINI') {
      return [
        { value: systemConfig.GEMINI_MODEL_FAST, label: 'Fast / Automated Model (Batch Processing)' },
        { value: systemConfig.GEMINI_MODEL_CLINICAL, label: 'Clinical / High-Reasoning Model (MTM)' }
      ];
    } else {
      return [
        { value: systemConfig.AZURE_OPENAI_DEPLOYMENT_FAST, label: 'Fast Deployment (Low-Latency)' },
        { value: systemConfig.AZURE_OPENAI_DEPLOYMENT_CLINICAL, label: 'Clinical Deployment (High-Reasoning)' }
      ];
    }
  };
  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1 style={{margin:0}}>AI Graph Engineering & Maintenance</h1>
        <button onClick={() => setShowConfig(true)} style={{background:'var(--pfb-sidebar)'}} title="Start a background job that uses AI to repair the knowledge graph (fill missing links, fix mappings). You choose the scope on the next step. All changes are audited.">🚀 Launch New Maintenance Job</button>
      </div>

      {showConfig && (
        <div className="card" style={{border:'2px solid var(--pfb-green)', background:'#fcfdfc'}}>
          <div style={{display:'flex', justifyContent:'space-between'}}>
             <h3>Configuration: Targeted Graph Repair</h3>
             <div style={{display:'flex', gap:'10px'}}>
               <button 
                  className="btn-secondary" 
                  onClick={() => confirm("Analyze unlinked ingredients?",
                    "This runs AI clinical analysis on ALL ingredients that currently have NO allergen links.",
                    async () => {
                      try {
                        await axios.post(`${API_URL}/sync/analyze`, { mode: 'ORPHANS' });
                        toast("Orphan analysis started. Check the Clinical Review Queue in a few minutes.", "success");
                        setShowConfig(false);
                      } catch { toast("Failed to start analysis.", "error"); }
                    })}
                  style={{borderColor:'orange', color:'#d35400'}}
                >
                  🧬 Analyze Unlinked Ingredients
                </button>
               <button className="btn-secondary" onClick={()=>setShowConfig(false)}>Cancel</button>
             </div>
          </div>

          <div className="stats-grid" style={{marginTop:'20px'}}>
            <div className="form-group">
              <label>Maintenance Scope</label>
              <select value={filterType} onChange={e => {setFilterType(e.target.value); setFilterId('');}}>
                <option value="all">Entire Knowledge Graph</option>
                <option value="partner">By Clinical Partner (Client)</option>
                <option value="sku">By Specific SKU</option>
                <option value="condition">By Medical Condition</option>
                <option value="ingredient">Specific Ingredient</option>
              </select>
            </div>

            {filterType !== 'all' && (
              <div className="form-group">
                <label>Select Target {filterType.toUpperCase()}</label>
                <select value={filterId} onChange={e => setFilterId(e.target.value)}>
                  <option value="">-- Select --</option>
                  {filterType === 'partner' && options.partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  {filterType === 'sku' && options.skus.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  {filterType === 'condition' && options.conditions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  {filterType === 'ingredient' && options.ingredients.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Intelligence Model</label>
              <select value={aiModel} onChange={e => setAiModel(e.target.value)}>
                {getModelOptions().map(opt => (
                   <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{display:'flex', gap:'20px', alignItems:'center', background:'#fff', padding:'15px', borderRadius:'8px', border:'1px solid #eee'}}>
             <input type="checkbox" checked={unmappedOnly} onChange={e=>setUnmappedOnly(e.target.checked)} style={{width:'20px', height:'20px'}} />
             <label style={{margin:0}}><strong>Safety Lock:</strong> Only process nodes without existing clinical connections (Protect manual RDN data)</label>
          </div>

          <button 
            style={{marginTop:'20px', width:'100%', fontSize:'1.1rem'}} 
            onClick={runAiRepair} 
            disabled={repairing || (filterType !== 'all' && !filterId)}
          >
            {repairing ? '⚙️ Initializing Job...' : '🚀 Execute Graph Engineering Job'}
          </button>
        </div>
      )}

      <div className="card">
        <h3>Graph Engineering Job Registry</h3>
        <table style={{marginTop:'15px'}}>
          <thead>
            <tr style={{background:'#f8f9fa'}}>
              <th>ID</th>
              <th>Status</th>
              <th>Scope</th>
              <th>Intelligence</th>
              <th>Progress</th>
              <th>Results</th>
              <th>Launched By</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => (
              <tr key={j.id}>
                <td><code>#{j.id}</code></td>
                <td>
                  <span className={`badge ${j.status === 'COMPLETED' ? 'green' : (j.status === 'FAILED' ? 'red' : 'yellow')}`}>
                    {j.status}
                  </span>
                </td>
                <td><span style={{fontSize:'0.8rem'}}><strong>{j.scope_type}</strong>: {j.scope_id || 'Global'}</span></td>
                <td><span style={{fontSize:'0.7rem', color:'#666'}}>{j.ai_model}</span></td>
                <td style={{width:'200px'}}>
                  <div style={{width:'100%', background:'#eee', height:'12px', borderRadius:'6px', overflow:'hidden'}}>
                     <div style={{
                       width:`${j.total_nodes > 0 ? (j.processed_nodes/j.total_nodes)*100 : 0}%`, 
                       background:'var(--pfb-green)', 
                       height:'100%',
                       transition:'width 0.5s ease'
                     }}></div>
                  </div>
                  <div style={{fontSize:'0.65rem', textAlign:'center', marginTop:'4px'}}>{j.processed_nodes} / {j.total_nodes} nodes</div>
                </td>
                <td>
                   <div style={{fontSize:'0.8rem'}}>
                      <strong>{j.created_relationships}</strong> suggestions sent to Queue
                      {j.error_message && <div style={{color:'red', fontSize:'0.7rem'}}>{j.error_message}</div>}
                   </div>
                </td>
                <td><span style={{fontSize:'0.8rem'}}>{j.launched_by}</span></td>
                <td style={{fontSize:'0.7rem'}}>
                   <div>S: {new Date(j.started_at).toLocaleString()}</div>
                   {j.finished_at && <div>F: {new Date(j.finished_at).toLocaleTimeString()}</div>}
                </td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={8} style={{textAlign:'center', padding:'40px', color:'#999'}}>No maintenance jobs recorded.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Lightbox = ({ src, onClose }: { src: string; onClose: () => void }) => (
  <div className="manual-overlay" style={{ zIndex: 3000, background: 'rgba(0,0,0,0.9)' }} onClick={onClose}>
    <img src={src} style={{ maxHeight: '90vh', maxWidth: '90vw', margin: 'auto', display: 'block' }} alt="Maximized" />
  </div>
);

const SystemManual = ({ onClose, initialSection }: { onClose: () => void, initialSection?: string }) => {
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  
  useEffect(() => {
    if (initialSection) {
      setTimeout(() => {
        const el = document.getElementById(initialSection);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [initialSection]);

  const sections = [
    { id: 'intro', title: '1. Executive Overview' },
    { id: 'arch', title: '2. System Architecture' },
    { id: 'gov', title: '3. Governance & Security' },
    { id: 'supply', title: '4. Supply Chain & Sync' },
    { id: 'csare', title: '5. The CSARE Engine' },
    { id: 'ai', title: '6. AI Ingredient Auditor' },
    { id: 'compliance', title: '7. Regulatory Compliance' },
    { id: 'flows', title: '8. Operational Flows' },
    { id: 'users', title: '9. Role-Based Access' },
    { id: 'learning', title: '10. Clinical Data Lifecycle' },
    { id: 'guardrails', title: '11. AI Governance & Guardrails' },
    { id: 'ingestion', title: '12. Massive Data Ingestion' },
    { id: 'massive-audit', title: '13. Massive Shipment Auditing' },
    { id: 'technical-specs', title: '14. Design Team Technical Specs' },
    { id: 'api-ref', title: '15. Technical API Reference' },
    { id: 'ai-sentinel-manual', title: '16. AI Sentinel: Autonomous Guardian' },
    { id: 'identity-governance', title: '17. Clinical Identity Governance' },
    { id: 'audit-integrity', title: '18. Audit Trail & HIPAA Compliance' },
    { id: 'risk-hierarchy', title: '19. Clinical Risk Hierarchy' },
    { id: 'user-preferences', title: '20. User Food Preferences' },
    { id: 'explorer-manual', title: '21. Clinical Traceability Explorer' },
  ];
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <div className="manual-overlay">
      {lightboxImg && <Lightbox src={lightboxImg} onClose={() => setLightboxImg(null)} />}
      <div className="manual-content">
        <aside className="manual-sidebar">
          <h3 style={{color: 'var(--pfb-sidebar)', marginBottom: '20px'}}>SafeGuard Manual</h3>
          <ul style={{listStyle: 'none', padding: 0}}>
            {sections.map(s => (
              <li key={s.id} onClick={() => scrollTo(s.id)} style={{cursor:'pointer', padding:'8px 0', borderBottom: '1px solid #eee', fontSize: '0.9rem'}}>
                {s.title}
              </li>
            ))}
          </ul>
          <button onClick={onClose} style={{width:'100%', marginTop:'30px'}}>Exit Manual</button>
        </aside>
        <main className="manual-body" style={{padding: '40px 60px'}}>
          <h1 id="intro" style={{color: 'var(--pfb-green-dark)', borderBottom: '2px solid var(--pfb-green)'}}>PFB SafeGuard Clinical Manual</h1>
          <p>PFB SafeGuard is a high-performance <strong>Clinical Decision Support System (CDSS)</strong> designed to ensure food safety for Medically Tailored Meal (MTM) programs. It provides an automated, human-in-the-loop framework to cross-reference patient clinical data against complex food supply chain ingredients, satisfying high-regulatory requirements such as <strong>CalAIM (California)</strong> and the <strong>1115 Waiver (New York)</strong>.</p>

          <h2 id="arch">2. System Architecture</h2>
          <p>The system utilizes a <strong>Polyglot Persistence</strong> strategy to handle different data complexities:</p>
          <ul>
            <li><strong>PostgreSQL (Relational):</strong> Stores transactional data, user profiles, system settings, and persistent audit logs.</li>
            <li><strong>Neo4j (Graph):</strong> Powering the CSARE engine, it models the recursive N-Tier food hierarchy (SKU &rarr; Recipe &rarr; Ingredient) for O(1) safety traversals.</li>
            <li><strong>Redis (Cache):</strong> Optimized for high-speed clinical rule lookups and session management.</li>
          </ul>
          <div className="account-info" style={{background: '#e9ecef', border: '1px solid #ced4da'}}>
             <strong>Architecture Visualization:</strong> The system is deployed as a modular containerized environment (Docker), isolating the <em>Core Reasoning Engine</em> from the <em>RDN Management API</em> to ensure clinical data integrity and high availability.
          </div>

          <h2 id="gov">3. Governance & Security</h2>
          <p>Built for <strong>HIPAA</strong> and <strong>HITRUST</strong> alignment, SafeGuard enforces strict governance protocols:</p>
          <ul>
            <li><strong>21 CFR Part 11 Compliance:</strong> All clinical approvals and overrides require a digital signature, ensuring non-repudiation of Registered Dietitian (RDN) decisions.</li>
            <li><strong>Multi-Factor Authentication (MFA):</strong> Mandatory TOTP-based 2FA for all administrative and clinical roles to prevent unauthorized access to Patient Health Information (PHI).</li>
            <li><strong>Immutable Audit Trails:</strong> Every transaction is hashed using SHA-256 and stored in an append-only log, creating a tamper-proof evidence chain for federal audits.</li>
          </ul>

          <h2 id="supply">4. Supply Chain & Master Data Sync</h2>
          <p>To eliminate manual data entry errors, SafeGuard maintains real-time synchronization with industry-standard registries using a <strong>Tiered Ingestion Strategy</strong>. A mission-critical <strong>Clinical Review Queue</strong> ensures that no data—whether from external sync or manual entry—enters production without RDN validation.</p>
          <ul>
            <li><strong>Level 1: Taxonomy Foundation:</strong> Ingests US regulatory allergens (FALCPA), Extended Clinical risks (Mustard, Corn), and Chemical Sensitivities (MSG, Nitrates) to establish the safety baseline.</li>
            <li><strong>Level 2: Clinical Conditions (CMS ICD-10):</strong> Synchronizes with official diagnostic codes, utilizing AI to automatically suggest evidence-based nutritional limits for Sodium, Potassium, Phosphorus, and Carbs.</li>
            <li><strong>Level 3: Food Registry (FDA GSRS / USDA FDC):</strong> Ingests thousands of ingredients and additives, mapping them back to the Level 1 taxonomy using restricted AI semantic analysis.</li>
            <li><strong>Level 4: Manual & Bulk Entries:</strong> All manual CSV imports and individual entry creations are intercepted and routed to the Review Queue for AI-assisted clinical auditing.</li>
          </ul>

          <h2 id="csare">5. CSARE: Clinical Safety & Allergen Reasoning Engine</h2>
          <p>CSARE is the "intelligence" of PFB SafeGuard, performing real-time recursive graph traversal across three critical safety layers to ensure patient safety.</p>
          
          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', borderLeft: '4px solid var(--pfb-green)', marginBottom: '15px'}}>
            <h4 style={{margin: '0 0 10px 0'}}>Layer 1: Direct Allergy Collision</h4>
            <p style={{margin: 0, fontSize: '0.9rem'}}>Detects explicit conflicts between a Member's recorded allergies and the SKU's ingredient list. If a direct match is found (e.g., Peanut Allergy vs. Peanut Flour), the SKU is blocked.</p>
            <code style={{display:'block', marginTop:'10px', fontSize:'0.8rem'}}>(Member)-[:HAS_ALLERGY]- &gt; (Allergen) &lt; -[:MAPS_TO]-(Ingredient) &lt; -[:CONTAINS]-(SKU)</code>
          </div>

          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', borderLeft: '4px solid var(--pfb-orange)', marginBottom: '15px'}}>
            <h4 style={{margin: '0 0 10px 0'}}>Layer 2: Clinical Inferences</h4>
            <p style={{margin: 0, fontSize: '0.9rem'}}>Automatically blocks allergens based on medical conditions (ICD-10). It infers risks even if the member hasn't manually reported an allergy. For example, a Celiac condition automatically triggers a block for all Gluten-containing ingredients.</p>
            <code style={{display:'block', marginTop:'10px', fontSize:'0.8rem'}}>(Member)-[:HAS_CONDITION]- &gt; (Condition)-[:BLOCKS_ALLERGEN]- &gt; (Allergen)</code>
          </div>

          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', borderLeft: '4px solid var(--danger)', marginBottom: '15px'}}>
            <h4 style={{margin: '0 0 10px 0'}}>Layer 3: Nutritional Thresholds (Per Meal)</h4>
            <p style={{margin: 0, fontSize: '0.9rem'}}>Validates SKU macro and micro-nutrients against medical limits established for the patient's conditions. <strong>All thresholds are calculated per meal.</strong> The system checks 11 parameters: Calories, Sodium, Carbs, Potassium, Protein, Fat, Fiber, Sugar, Cholesterol, Iron, and Calcium. If the SKU exceeds the strictest limit allowed for any of the member's active conditions, it is blocked.</p>
            <code style={{display:'block', marginTop:'10px', fontSize:'0.8rem'}}>SKU.sodium_mg &gt; Condition.max_sodium_mg (STRICTEST_LIMIT)</code>
          </div>

          <img src="csare_engine.png" style={{maxWidth:'100%', cursor:'pointer', borderRadius: '10px', boxShadow: '0 5px 15px rgba(0,0,0,0.1)', margin: '20px 0'}} onClick={()=>setLightboxImg('csare_engine.png')} alt="CSARE Engine Architecture" />

          <h4 style={{marginTop: '20px'}}>Data Foundations & Provenance</h4>
          <ul style={{fontSize: '0.9rem'}}>
            <li><strong>Clinical Data (HL7 FHIR):</strong> Source for Member allergies and ICD-10 conditions ingested from health plans.</li>
            <li><strong>Supply Chain (GS1 GDSN):</strong> Source for detailed SKU ingredients, recipes, and nutritional values from food partners.</li>
            <li><strong>Medical Knowledge Base:</strong> RDN-curated rules mapping conditions to forbidden allergens and quantitative thresholds based on CDC/FDA guidelines.</li>
          </ul>
          <p style={{fontSize: '0.8rem', color: '#666'}}><em>Note for Visualization: Designers should illustrate the Member and SKU entering a "Triple-Gate" filtration system, where each layer represents a progressively finer safety mesh.</em></p>

          <h2 id="ai">6. AI-Powered Clinical Auditor</h2>
          <p>SafeGuard integrates <strong>Google Gemini</strong> to act as an automated clinical supervisor with high-precision reasoning:</p>
          <ul>
            <li><strong>Medical-Grade Entity Mapping:</strong> Automatically identifies parent allergens (e.g., Pecan Flour &rarr; Tree Nut) and assigns a <strong>Safety Grade (A-F)</strong> based on clinical risk.</li>
            <li><strong>Exhaustive Condition Analysis:</strong> Extracts evidence-based limits for 15+ parameters including Electrolytes (Na, K, P, Ca, Mg), Macronutrients, and sensitivities like Gluten, Lactose, and Purines.</li>
            <li><strong>Risk Explanation:</strong> Generates natural language justifications for safety blocks, citing specific clinical guidelines from AHA, ADA, and FDA.</li>
          </ul>

          <h2 id="compliance">7. Regulatory Compliance</h2>
          <p>The platform is engineered for multi-state regulatory adherence:</p>
          <ul>
            <li><strong>California (SB 68):</strong> Provides automated allergen transparency via QR codes for every delivered box.</li>
            <li><strong>New York (A6558/S5381):</strong> Generates compliant labels for Grab-and-Go items with clear allergen declarations.</li>
            <li><strong>Medicaid Reimbursement:</strong> Automates the generation of <strong>EDI 837 Professional Claims</strong> using HCPCS code S5170 with the U6 modifier for Therapeutic Meals.</li>
          </ul>

          <h2 id="flows">8. Operational Flows</h2>
          <p>The system manages the end-to-end lifecycle of a Medically Tailored Meal:</p>
          <ol>
            <li><strong>Clinical Ingest:</strong> Patient data is synced via FHIR APIs.</li>
            <li><strong>Safety Analysis:</strong> CSARE evaluates the patient against the available menu.</li>
            <li><strong>RDN Review:</strong> Flagged orders are reviewed and electronically signed by a Dietitian.</li>
            <li><strong>Production Gate:</strong> Labels only print once "Cleaning Gates" (ATP tests) are verified for the production run.</li>
          </ol>
          <img src="PFBSafeGuard_Flows.png" style={{maxWidth:'100%', cursor:'pointer', borderRadius: '10px', margin: '20px 0'}} onClick={()=>setLightboxImg('PFBSafeGuard_Flows.png')} alt="Flows" />

          <h2 id="users">9. Role-Based Access Control (RBAC)</h2>
          <p>Access is granularly controlled via clinical roles:</p>
          <ul>
             <li><strong>Registered Dietitian (RDN):</strong> Authorized to sign clinical approvals and perform safety overrides with justification.</li>
             <li><strong>Safety Auditor:</strong> Read-only access to all audit logs and AI safety reports.</li>
             <li><strong>System Admin:</strong> Manages infrastructure, user access, and global AI governance settings.</li>
             <li><strong>Operations:</strong> Query-only. Runs safety checks (including batch files) and suggests new allergens/conditions into the RDN review queue — every suggestion records who submitted it. No access to patient records (PHI) and no write access to the catalog.</li>
          </ul>
          <img src="PFB_SafeGuard_Users.png" style={{maxWidth:'100%', cursor:'pointer', borderRadius: '10px'}} onClick={()=>setLightboxImg('PFB_SafeGuard_Users.png')} alt="Users" />

          <h2 id="learning">10. Clinical Data Lifecycle (Discovery & Validation)</h2>
          <p>The system maintains a dynamic Knowledge Graph that grows as new products are introduced. The process follows a strict <strong>"Taxonomy-First"</strong> protocol:</p>
          <div className="stats-grid">
            <div className="card" style={{borderLeft: '4px solid var(--pfb-green)'}}>
              <h4>1. Foundation Build</h4>
              <p style={{fontSize: '0.85rem'}}>The RDN establishes the master taxonomy of allergens and sensitivities. This "Steel Thread" ensures consistency and prevents data fragmentation across the entire clinical network.</p>
            </div>
            <div className="card" style={{borderLeft: '4px solid var(--pfb-orange)'}}>
              <h4>2. AI Discovery (Restricted)</h4>
              <p style={{fontSize: '0.85rem'}}>New items are analyzed by Google Gemini. For ingredients, the AI is <strong>forced</strong> to use the existing taxonomy. For conditions, it suggests evidence-based Na, K, and CHO limits.</p>
            </div>
            <div className="card" style={{borderLeft: '4px solid var(--pfb-sidebar)'}}>
              <h4>3. Human-in-the-Loop & Editing</h4>
              <p style={{fontSize: '0.85rem'}}>Clinical data NEVER enters the graph without an RDN's signature. RDNs can use the <strong>Clinical Edit Modal</strong> to manually override AI suggestions for thresholds, safety grades, and names before final production approval.</p>
            </div>
          </div>
          
          <h2 id="guardrails">11. AI Governance & Guardrails</h2>
          <p>SafeGuard implements clinical-grade guardrails to ensure AI outputs remain safe, consistent, and explainable:</p>
          
          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', borderLeft: '4px solid var(--pfb-sidebar)', marginBottom: '15px'}}>
            <h4>Strict Taxonomy Enforcement</h4>
            <p style={{fontSize: '0.85rem'}}>The AI Semantic Mapper is restricted by a <strong>Live Taxonomy</strong> of your system's master allergens. This prevents clinical data fragmentation and ensures every ingredient mapping is 100% interoperable with patient profiles.</p>
          </div>

          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', borderLeft: '4px solid var(--pfb-green)', marginBottom: '15px'}}>
            <h4>Automated Clinical Threshold Discovery</h4>
            <p style={{fontSize: '0.85rem'}}>Condition-based safety is evidence-driven. AI extracts quantitative limits for Electrolytes, Macros, and Sensitivities. Upon RDN approval (with optional manual adjustments), these are injected as permanent reasoning nodes into the safety graph.</p>
          </div>

          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', borderLeft: '4px solid var(--pfb-orange)'}}>
            <h4>Authoritative Audit Trail (21 CFR Part 11)</h4>
            <p style={{fontSize: '0.85rem'}}>Every action approved by an RDN—including manual edits to AI suggestions—is hashed and stored in the <strong>Immutable Audit Log</strong>. This captures the full state (ID, timestamp, and RDN identity), providing legally defensible evidence for audits.</p>
          </div>

          <h2 id="ingestion">12. Massive Data Ingestion (CSV & Snowflake)</h2>
          <p>PFB SafeGuard is designed for high-scale clinical operations, supporting the ingestion of millions of member records and complex clinical profiles through two main channels:</p>
          
          <ul>
            <li><strong>Mass CSV Import:</strong> Optimized background streaming allows for the ingestion of up to 1,000,000 records. 
                The system supports dual identification: <strong>ID</strong> (Internal) and <strong>External ID</strong> (from source system). 
                The <em>Intelligent Clinical Resolver</em> automatically categorizes terms found in 'Allergies', 'Conditions', or mixed clinical profile columns.</li>
            <li><strong>Snowflake Data Connector:</strong> Direct integration with the corporate "Source of Truth". Administrators can define custom SQL queries to join member data with clinical attributes. The system handles unified profiles, extracting and mapping allergens and conditions recursively. Use <code>EXT_ID</code> for external mapping.</li>
            <li><strong>Smart Clinical Resolver:</strong> This engine caches the Master Taxonomy at the start of every import to correctly route data even if provided in mixed columns (e.g., a single 'Profile' column containing both 'Peanut' and 'Diabetes').</li>
          </ul>

          <div className="account-info" style={{background: '#fff9f0', border: '1px solid #ffeeba', color: '#856404'}}>
            <strong>Compliance Note:</strong> While ingestion is automated, any <em>newly discovered</em> clinical terms (Allergens or Conditions not in the Master Taxonomy) are automatically routed to the <strong>Review Queue</strong> for RDN validation to maintain medical integrity.
          </div>

          <h2 id="massive-audit">13. Massive Shipment Auditing (UltraPort V2)</h2>
          <p>The <strong>Clinical Batch Auditor V2</strong> (<code>clinical_batch_test2.py</code>) is a high-velocity tool designed to perform a final safety check on tens of thousands of shipments before distribution. It bridges the gap between logistical shipment files and SafeGuard's clinical intelligence.</p>
          
          <div className="stats-grid">
            <div className="card">
              <h4>Parallel Batch Engine</h4>
              <p style={{fontSize: '0.85rem'}}>The <code>/api/check/forbidden-skus/batch</code> endpoint utilizes a <strong>Matrix-Reduction</strong> strategy. It fetches the entire SKU allergen map into memory once per request, performing clinical intersections in O(N) time.</p>
            </div>
            <div className="card">
              <h4>V2 Potency Features</h4>
              <p style={{fontSize: '0.85rem'}}><strong>Local LRU Deduplication:</strong> Identifies redundant patients in massive files to reduce API load by up to 80%. <strong>Clinical Justification:</strong> Injects an <code>EXCLUDED_REASONS</code> column explaining <em>why</em> each SKU was blocked.</p>
            </div>
          </div>

          <h2 id="technical-specs">14. Design Team Technical Specifications (Functional Workflow)</h2>
          <div style={{background: '#f4f7f6', padding: '25px', borderRadius: '10px', border: '1px solid #ddd'}}>
            <h3 style={{marginTop: 0, color: 'var(--pfb-sidebar)'}}>SYSTEM FUNCTIONAL FLOW (ENGLISH)</h3>
            
            <p><strong>1. Source of Truth Synchronization (Snowflake &rarr; SafeGuard)</strong></p>
            <p style={{fontSize: '0.9rem', color: '#444'}}>
              SafeGuard maintains an asynchronous synchronization with <strong>Snowflake</strong>. It pulls Member IDs, Names, and raw Clinical Notes. 
              The <strong>AI Normalization Layer (Azure OpenAI)</strong> parses unstructured notes (e.g., "NO CHEESE") and maps them to the <strong>Clinical Master Taxonomy</strong>. 
              These relationships are persisted in the <strong>Neo4j Graph Database</strong> as <code>(Member)-[:HAS_ALLERGY]{"->"}(Allergen)</code> nodes.
            </p>

            <p><strong>2. Product Intelligence Hierarchy (SKU &rarr; Ingredients &rarr; Allergens)</strong></p>
            <p style={{fontSize: '0.9rem', color: '#444'}}>
              Every food item (SKU) is decomposed into its hierarchical components. An AI <strong>Recipe Estimator</strong> analyzes SKU names to derive a recursive ingredient list. 
              Each Ingredient is then mapped to its constituent <strong>Allergens</strong> through the Clinical Review Queue. This creates a multi-hop safety path: 
              <code>(SKU)-[:CONSISTS_OF]{"->"}(Ingredient)-[:CONTAINS_ALLERGEN]{"->"}(Allergen)</code>.
            </p>

            <p><strong>3. The Batch Auditing Process (Logistics Input &rarr; API &rarr; Output)</strong></p>
            <ul style={{fontSize: '0.9rem', color: '#444'}}>
              <li><strong>Input:</strong> Logistics provides a massive CSV containing <code>EXTERNAL_CUSTOMER_ID</code> and <code>PACKAGE_NOTES</code>.</li>
              <li><strong>Orchestration:</strong> The <code>clinical_batch_test.py</code> script chunks the CSV into batches of 50 records.</li>
              <li><strong>API Execution:</strong> The script calls the <code>/batch</code> endpoint. The engine performs a <strong>Mass Intersection</strong>: it identifies the restricted set for each member (including dynamic notes from the CSV) and cross-references it against the <strong>In-Memory SKU Map</strong>.</li>
              <li><strong>Logical Result:</strong> If a member's restricted set (Allergies + Conditions) overlaps with a SKU's allergen list or nutritional profile, that SKU is marked as <strong>EXCLUDED</strong>.</li>
              <li><strong>Output Injection:</strong> The script writes a new CSV, preserving all original columns and populating the <code>EXCLUDED_MEALS</code> column with a pipe-separated list of restricted items.</li>
            </ul>

            <div style={{marginTop: '20px', padding: '15px', background: '#fff', borderLeft: '5px solid var(--pfb-green)', fontSize: '0.85rem', fontStyle: 'italic'}}>
              "Visualization Directive: Illustrate a funnel where Shipment Data and Clinical Knowledge Graph converge into a high-speed Batch Processor, resulting in an Enriched Logistics CSV."
            </div>
          </div>

          <h2 id="api-ref">15. Technical API Reference (Security & Endpoints)</h2>
          <p>SafeGuard exposes a secure machine-to-machine (M2M) API for logistics and health plan integrations.</p>
          
          <div style={{background: '#2c3e50', color: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px'}}>
             <h4 style={{marginTop: 0, color: '#3498db'}}>POST /check/forbidden-skus</h4>
             <p style={{fontSize: '0.85rem'}}>Performs a real-time safety audit for a single member.</p>
             <p style={{fontSize: '0.85rem'}}><strong>AI Allergen Normalization:</strong> Any <code>extra_allergies</code> term the graph does not recognize is normalized with a fast Azure OpenAI model (synonyms, plurals, case, misspellings) before matching, so it is never silently dropped. Terms that cannot be confidently resolved are returned in <code>unrecognized_allergens</code> and a <code>notice</code> field: <em>"POSSIBLE NEW ALLERGEN FOUND, CHECK WITH RDNs THIS: ..."</em>.</p>
             <code style={{color: '#2ecc71', fontSize: '0.8rem'}}>Payload: {"{"} "member_id": "string", "extra_allergies": ["list"] {"}"}</code>
          </div>

          <div style={{background: '#2c3e50', color: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px'}}>
             <h4 style={{marginTop: 0, color: '#3498db'}}>POST /check/forbidden-skus/batch</h4>
             <p style={{fontSize: '0.85rem'}}><strong>High-Performance:</strong> Audits up to 100 members in a single sub-second call. Essential for logistics pre-flight checks.</p>
             <code style={{color: '#2ecc71', fontSize: '0.8rem'}}>Payload: {"{"} "members": [{"{"} "member_id": "...", "extra_allergies": [] {"}"}] {"}"}</code>
          </div>

          <div style={{background: '#2c3e50', color: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px'}}>
             <h4 style={{marginTop: 0, color: '#9b59b6'}}>POST /check/allergen-impact</h4>
             <p style={{fontSize: '0.85rem'}}><strong>Member-independent.</strong> Given a single allergen, returns every SKU in the catalog impacted by it. If the allergen is not found, the same AI normalization maps it onto the known taxonomy (e.g. <code>"soya" &rarr; Soy</code>). A genuinely-new allergen returns <code>status: "PENDING_REVIEW"</code> with the <em>"POSSIBLE NEW ALLERGEN..."</em> notice and is logged to the RDN review queue.</p>
             <code style={{color: '#2ecc71', fontSize: '0.8rem'}}>Payload: {"{"} "allergen_name": "string" {"}"}  (or "allergen_id")</code>
          </div>

          <div style={{background: '#2c3e50', color: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px'}}>
             <h4 style={{marginTop: 0, color: '#9b59b6'}}>POST /check/note-impact</h4>
             <p style={{fontSize: '0.85rem'}}><strong>Member-independent free-text auditor.</strong> Accepts a raw dietary note and splits it into individual terms (handling negation like <code>"NO SOY"</code> and multi-term cells like <code>"NO SOY NO TOFU NO NUTS"</code>). Each term is classified as a clinical <strong>ALLERGEN</strong> (mapped to the taxonomy &rarr; hard block, returns impacted SKUs) or a non-clinical <strong>PREFERENCE</strong> (soft, never a hard block). New allergens are surfaced via <code>new_allergens</code> + <code>notice</code>.</p>
             <p style={{fontSize: '0.85rem'}}><strong>Audit mode by default:</strong> new allergens are NOT written to the RDN queue unless <code>register_new: true</code> is passed.</p>
             <code style={{color: '#2ecc71', fontSize: '0.8rem'}}>Payload: {"{"} "note": "NO SOY NO BROCCOLI", "register_new": false {"}"}</code>
             <br/>
             <code style={{color: '#95a5a6', fontSize: '0.8rem'}}>Returns: {"{"} forbidden_skus, allergen_blocks, preferences, new_allergens, notice {"}"}</code>
          </div>

          <div style={{background: '#2c3e50', color: '#fff', padding: '20px', borderRadius: '8px'}}>
             <h4 style={{marginTop: 0, color: '#3498db'}}>POST /token</h4>
             <p style={{fontSize: '0.85rem'}}>OAuth2 Client Credentials flow. Required for all technical integrations.</p>
             <code style={{color: '#2ecc71', fontSize: '0.8rem'}}>Form Data: client_id, client_secret</code>
          </div>

          <h2 id="ai-sentinel-manual">16. AI Sentinel: Autonomous Guardian</h2>
          <p><strong>What is AI Sentinel?</strong></p>
          <p style={{fontSize: '0.9rem'}}>AI Sentinel is an intelligent, autonomous audit system that leverages <strong>Azure OpenAI (HIPAA Compliant)</strong> to supervise the health and integrity of the entire SafeGuard ecosystem once a day (or whenever triggered).</p>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>Sentinel Capabilities:</h4>
          <ol style={{fontSize: '0.9rem', color: '#444'}}>
             <li>
               <strong>360° Comprehensive Audit:</strong> The system analyzes 6 critical verticals:
               <ul>
                 <li><strong>Patients:</strong> Detects duplicates, missing clinical data, or suspicious identification formats.</li>
                 <li><strong>Products (SKU):</strong> Identifies products without ingredients or missing mandatory nutritional values (Calories, Sodium, etc.).</li>
                 <li><strong>Ingredients:</strong> Locates "orphan" ingredients (unlinked to allergens) or naming inconsistencies.</li>
                 <li><strong>Allergens:</strong> Detects taxonomic overlaps, missing hierarchical relationships (IS_A), or duplicate nodes with different IDs.</li>
                 <li><strong>Graph:</strong> Performs a deep connectivity audit ensuring every SKU has a valid safety path to its constituent allergens.</li>
                 <li><strong>Logs:</strong> Scans system and audit logs for high-frequency errors, unauthorized access attempts, or suspicious data patterns.</li>
               </ul>
             </li>
             <li style={{marginTop:'10px'}}>
               <strong>Customizable Category Governance:</strong>
               <ul>
                 <li><strong>Custom AI Instructions:</strong> Administrators can adjust the <strong>AI Prompt</strong> for each vertical to refine detection logic (e.g., stricter patient clinical validation vs. ingredient naming consistency).</li>
                 <li><strong>Dynamic Webhooks:</strong> Each category can be mapped to a specific webhook (e.g., Slack/Teams) to alert the relevant team (Clinical, Security, or Logistics) instantly upon discovery.</li>
               </ul>
             </li>
             <li style={{marginTop:'10px'}}>
               <strong>Intelligent Reporting:</strong>
               <ul>
                 <li>Every execution generates a comprehensive report with a status (<strong>Healthy, Warning, or Critical</strong>).</li>
                 <li>Reports include specific clinical/technical findings and a detailed <strong>AI-generated Recommendation</strong> to expedite system repair.</li>
               </ul>
             </li>
          </ol>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>How to use it:</h4>
          <ul style={{fontSize: '0.9rem', color: '#444'}}>
             <li>1. Navigate to the new menu tab: <strong>🤖 AI Sentinel (Daily Audit)</strong>.</li>
             <li>2. Review or update the configuration for the 6 modules, including Prompts and Webhooks. Click <strong>Save</strong>.</li>
             <li>3. Click <strong>🚀 Run Manual Audit Now</strong> to trigger an immediate check.</li>
             <li>4. Review the <strong>Recent Audit Reports</strong> on the sidebar to inspect AI findings and recommendations.</li>
          </ul>

          <div className="account-info" style={{background: '#f0f7ff', borderLeft: '4px solid #3498db'}}>
             <strong>Automation Note:</strong> AI Sentinel is programmed to execute automatically every day at <strong>midnight (00:00)</strong> to ensure a safe environment before logistics operations begin.
          </div>

          <h2 id="identity-governance">17. Clinical Identity Governance</h2>
          <p><strong>What is this module for?</strong></p>
          <p style={{fontSize: '0.9rem'}}>Clinical Identity Governance is an advanced graph maintenance tool designed to prevent data fragmentation. In a high-scale environment, the same medical concept (e.g., "Organic Wheat" and "Wheat") might enter the system via different sources. This module detects these "Identity Clusters" using shared clinical identifiers.</p>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>Core Functionalities:</h4>
          <ul style={{fontSize: '0.9rem', color: '#444'}}>
             <li><strong>Rescan Graph:</strong> Triggers a real-time discovery of clusters sharing the same UNII, FDA, or ICD-10 codes.</li>
             <li><strong>Surgical Merge:</strong> Allows Registered Dietitians (RDNs) to select a single "Canonical Record" and absorb all redundant data into it.</li>
             <li><strong>Automatic Link Preservation:</strong> When merging, the system automatically moves all member allergies/conditions and all SKU ingredient links to the canonical node before purging the duplicates.</li>
          </ul>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>Operational Steps:</h4>
          <ol style={{fontSize: '0.85rem', color: '#666'}}>
             <li>Select the entity type (Ingredients or Conditions).</li>
             <li>Inspect the detected clusters in the list.</li>
             <li>Click a cluster to compare technical codes and names side-by-side.</li>
             <li>Select the most accurate record and click <strong>"Set as Canonical & Merge"</strong>.</li>
          </ol>

          <h2 id="audit-integrity">18. Audit Trail & HIPAA Compliance</h2>
          <p><strong>System Integrity & Non-Repudiation</strong></p>
          <p style={{fontSize: '0.9rem'}}>To satisfy <strong>HIPAA Security Rule (§ 164.312(b))</strong>, SafeGuard maintains a comprehensive audit trail that records all access to Patient Health Information (PHI) and all data modifications.</p>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>Immutable Evidence Chain:</h4>
          <ul style={{fontSize: '0.9rem', color: '#444'}}>
             <li><strong>Cryptographic Hashing:</strong> Every audit entry is automatically signed using a <strong>SHA-256 Hash</strong> that includes the user, timestamp, action, and data payload. Any manual tampering with the database would break the hash chain, alerting auditors.</li>
             <li><strong>Session Auditing:</strong> The system logs every successful login, failed attempt (including MFA failures), and unauthorized access attempts to clinical modules.</li>
             <li><strong>Retention Policy:</strong> In alignment with federal requirements, audit logs are stored in a high-durability relational store and are protected from deletion for a minimum of <strong>7 years</strong>.</li>
          </ul>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>Data Minimization:</h4>
          <p style={{fontSize: '0.9rem'}}>For maximum privacy, technical system logs automatically mask PHI. Only the dedicated <strong>Audit Log</strong> module (accessible only by Admins) contains the high-fidelity history required for clinical forensics.</p>

          <h2 id="risk-hierarchy">19. Clinical Risk Hierarchy</h2>
          <p style={{fontSize: '0.9rem'}}>SafeGuard implements a multi-tiered safety model to balance medical necessity with personal choice:</p>
          <ul style={{fontSize: '0.9rem', color: '#444'}}>
             <li><strong style={{color:'var(--danger)'}}>RED (BLOCK):</strong> Clinical constraints (Allergies and Conditions). These are mandatory medical exclusions. Plated meals violating these rules are strictly rejected.</li>
             <li><strong style={{color:'var(--pfb-orange)'}}>YELLOW (CAUTION):</strong> Personal preferences. These represent user choices (e.g., "No mushrooms"). The system flags these as CAUTION, allowing for flexibility while keeping the user informed.</li>
             <li><strong style={{color:'var(--pfb-green-dark)'}}>GREEN (APPROVED):</strong> Item is both clinically safe and aligns with user preferences.</li>
          </ul>

          <h2 id="user-preferences">20. User Food Preferences</h2>
          <p style={{fontSize: '0.9rem'}}>Unlike Conditions and Allergies, which require <strong>RDN Authorization</strong>, Personal Food Preferences can be managed by standard users or intake coordinators. The system uses AI to normalize raw text input (e.g., "I hate eggs and want low salt") into standardized entities that the safety engine can understand. Preferences are always secondary to medical requirements.</p>

          <h4 style={{color: 'var(--pfb-sidebar)'}}>Automatic Allergen vs. Preference Classification</h4>
          <p style={{fontSize: '0.9rem'}}>Free-text restriction notes mix clinical allergens with personal dislikes. The <code>/check/note-impact</code> API parses each note, strips negation (<code>"NO SOY"</code> &rarr; Soy), splits multi-term entries (<code>"NO SOY NO TOFU NO BROCCOLI"</code>), and classifies every term:</p>
          <ul style={{fontSize: '0.9rem', color: '#444'}}>
             <li><strong style={{color:'var(--danger)'}}>ALLERGEN</strong> (e.g. Soy, Shellfish, Tree Nuts, Lactose) &rarr; mapped to the taxonomy and applied as a <strong>RED hard block</strong>. Items not in the taxonomy are surfaced for RDN review, never silently dropped.</li>
             <li><strong style={{color:'var(--pfb-orange)'}}>PREFERENCE</strong> (e.g. Broccoli, Cilantro, Spicy Food, Burritos) &rarr; treated as a <strong>YELLOW soft preference</strong>, never escalated to a clinical block.</li>
          </ul>
          <p style={{fontSize: '0.85rem', fontStyle: 'italic'}}>This preserves the governance boundary: an automated import or intake coordinator can never create an RDN-grade clinical allergy. Allergen candidates only enter production after explicit RDN approval in the Clinical Review Queue.</p>

          <h2 id="explorer-manual">21. Clinical Traceability Explorer</h2>
          <p style={{fontSize: '0.9rem'}}>The Traceability Explorer is a transparency tool for RDNs. It provides three primary audit modes:</p>
          <ul style={{fontSize: '0.9rem', color: '#444'}}>
             <li><strong>Patient Audit:</strong> Visualizes the full risk chain for a patient, showing every ingredient in the system that could trigger a conflict.</li>
             <li><strong>Product Audit:</strong> Recursively deconstructs a product (SKU) into its ingredients and sub-ingredients, identifying allergens at every level of the hierarchy.</li>
             <li><strong>Patient + Product (Visual Trace):</strong> Generates an interactive sub-graph showing the exact "Clinical Bridge" or path connecting a specific patient to a specific product conflict.</li>
          </ul>
          
          <div style={{marginTop: '50px', textAlign: 'center', color: '#999', fontSize: '0.8rem'}}>
            © 2026 PFB SafeGuard Clinical Systems | Confidential Document
          </div>
        </main>
      </div>
    </div>
  );
};

const CatalogUploader = ({ onComplete }: { onComplete: () => void }) => {
  const [partners, setPartners] = useState<any[]>([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/partners`).then(res => setPartners(res.data.items || []));
  }, []);

  const handleUpload = async () => {
    if (!file) return alert("Select a CSV file");
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/bulk-import/skus`, formData, {
        params: { partner_id: selectedPartner },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert("Catalog processed successfully");
      onComplete();
    } catch {
      alert("Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="card" style={{border: '1px dashed var(--pfb-sidebar)', background: '#f9f9f9'}}>
      <h3>Internal Tool: Bulk Catalog Import</h3>
      <p style={{fontSize:'0.8rem', color:'#666'}}>Use this to import SKUs from partners for clinical testing.</p>
      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginTop:'15px'}}>
        <div className="form-group">
          <label>Assign to Partner (Optional)</label>
          <select value={selectedPartner} onChange={e => setSelectedPartner(e.target.value)}>
            <option value="">-- No Partner --</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Select CSV Catalog</label>
          <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)} />
        </div>
      </div>
      <button 
        style={{marginTop:'15px', background: 'var(--pfb-sidebar)'}} 
        onClick={handleUpload} 
        disabled={uploading}
      >
        {uploading ? 'Processing Clinical Data...' : '📤 Import Catalog'}
      </button>
    </div>
  );
};

const SearchableSelector = ({ type, label, onSelect, placeholder, initialValue = '' }: any) => {
  const [search, setSearch] = useState(initialValue);
  const [results, setResults] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (search.length > 1 && !selected) {
      const delayDebounceFn = setTimeout(() => {
        console.log(`[SearchableSelector] Searching for ${type}: "${search}"`);
        axios.get(`${API_URL}/${type}`, { params: { search, limit: 10 } })
          .then(res => {
            console.log(`[SearchableSelector] Found ${res.data.items?.length || 0} results for ${type}`);
            setResults(res.data.items || []);
          })
          .catch(err => {
            console.error(`[SearchableSelector] Search failed for ${type}:`, err);
          });
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setResults([]);
    }
  }, [search, type, selected]);

  const handleSelect = (item: any) => {
    console.log(`[SearchableSelector] Item selected in ${type}:`, item);
    setSelected(item);
    setSearch(item.name || item.id);
    setResults([]);
    setShowResults(false);
    onSelect(item);
  };

  const clear = () => {
    setSelected(null);
    setSearch('');
    setResults([]);
    onSelect(null);
  };

  return (
    <div className="form-group" style={{position:'relative', flex: 1}}>
      {label && <label style={{display:'block', marginBottom:'5px'}}>{label}</label>}
      <div style={{position:'relative'}}>
        <input 
          style={{width:'100%', padding:'10px', paddingRight:'40px', borderRadius:'4px', border:'1px solid #ddd'}}
          placeholder={placeholder}
          value={search}
          onChange={e => { setSearch(e.target.value); if(selected) {setSelected(null); onSelect(null);} setShowResults(true); }}
          onFocus={() => setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
        />
        {(selected || search) && (
          <div 
            onClick={clear}
            style={{position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', cursor:'pointer', color:'#999', fontSize:'1.2rem'}}
          >×</div>
        )}
      </div>
      {showResults && results.length > 0 && (
        <div className="card" style={{position:'absolute', top:'100%', left:0, width:'100%', zIndex:1000, padding:0, boxShadow:'0 10px 30px rgba(0,0,0,0.2)', maxHeight:'300px', overflowY:'auto', background:'white'}}>
          {results.map(r => (
            <div key={r.id} onClick={() => handleSelect(r)} style={{padding:'12px', cursor:'pointer', borderBottom:'1px solid #eee'}}>
              <div style={{fontWeight:'bold'}}>{r.name}</div>
              <div style={{fontSize:'0.8rem', color:'#666'}}>{r.id} {r.external_id ? `| ${r.external_id}` : ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SkuChecker = () => {
  const { toast } = useUI();
  const [mId, setMId] = useState('');
  const [sId, setSId] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Safety Match Flags
  const [chkAllergens, setCheckAllergens] = useState(true);
  const [chkConditions, setCheckConditions] = useState(true);
  const [chkPreferences, setCheckPreferences] = useState(true);

  const check = async () => {
    if (!mId || !sId) { toast("Select both Member and SKU", "info"); return; }
    console.log(`[CSARE] Initiating Safety Check: Member=${mId}, SKU=${sId}`);
    console.log(`[CSARE] Rules: Allergens=${chkAllergens}, Conditions=${chkConditions}, Preferences=${chkPreferences}`);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/check`, { 
        member_id: mId, 
        sku_id: sId,
        check_allergens: chkAllergens,
        check_conditions: chkConditions,
        check_preferences: chkPreferences
      });
      console.log(`[CSARE] Result: Status=${res.data.status}, Risk=${res.data.risk_level}`);
      setResult(res.data);
    } catch (err: any) { 
      console.error("[CSARE] Safety check error:", err);
      toast("Safety check failed", "error"); 
    }
    setLoading(false);
  };

  return (
    <div className="card">
      <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px'}}>
        <span style={{fontSize:'1.5rem'}}>🛡️</span>
        <h2 style={{margin:0}}>Clinical Safety Simulator (CSARE)</h2>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px'}}>
        <SearchableSelector 
          type="members" 
          label="Target Member (Patient)" 
          placeholder="Search patient name or ID..." 
          onSelect={(m: any) => setMId(m ? m.id : '')} 
        />
        <SearchableSelector 
          type="skus" 
          label="Target SKU (Product)" 
          placeholder="Search SKU name or ID..." 
          onSelect={(s: any) => setSId(s ? s.id : '')} 
        />
      </div>
      <div className="form-group" style={{marginBottom:'20px', background:'#f8f9fa', padding:'15px', borderRadius:'8px', border:'1px solid #eee'}}>
        <label style={{display:'block', marginBottom:'10px', fontWeight:'bold', color:'var(--pfb-sidebar)'}}>Safety Match Options:</label>
        <div style={{display:'flex', gap:'25px'}}>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={chkAllergens} onChange={e => setCheckAllergens(e.target.checked)} />
            <span>Check Allergens</span>
          </label>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={chkConditions} onChange={e => setCheckConditions(e.target.checked)} />
            <span>Check Conditions</span>
          </label>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={chkPreferences} onChange={e => setCheckPreferences(e.target.checked)} />
            <span>Check Preferences</span>
          </label>
        </div>
      </div>

      <button onClick={check} style={{width: '100%', marginBottom: '20px', padding:'15px', fontSize:'1rem'}} disabled={loading}>
        {loading ? 'Analyzing Clinical Graph...' : '🚀 Run Comprehensive Safety Analysis'}
      </button>

      {result && (
        <div style={{border: `2px solid ${result.risk_level === 'RED' ? '#e74c3c' : '#2ecc71'}`, borderRadius: '8px', overflow:'hidden'}}>
          <div style={{padding: '15px', background: result.risk_level === 'RED' ? '#e74c3c' : '#2ecc71', color: 'white', display:'flex', justifyContent:'space-between'}}>
            <strong style={{fontSize:'1.2rem'}}>{result.status}</strong>
            <span className="badge" style={{background:'rgba(255,255,255,0.2)', color:'white'}}>{result.risk_level}</span>
          </div>
          <div style={{padding: '20px', background:'#fff'}}>
             <h4 style={{marginTop:0, borderBottom:'1px solid #eee', paddingBottom:'10px'}}>Clinical Reasoning Trace</h4>
             {result.trace.length > 0 ? result.trace.map((t: any, i: number) => (
               <div key={i} style={{marginBottom:'15px', borderLeft: `4px solid ${t.impact === 'BLOCK' ? '#e74c3c' : '#f1c40f'}`, paddingLeft: '15px'}}>
                 <div style={{fontWeight:'bold', color: t.impact === 'BLOCK' ? '#c0392b' : '#d35400'}}>[{t.category}] {t.finding}</div>
                 <div style={{fontSize:'0.9rem', color:'#555', marginTop:'4px'}}>{t.evidence}</div>
               </div>
             )) : <p style={{color:'#27ae60'}}>✅ No clinical conflicts identified for this combination.</p>}
          </div>
        </div>
      )}

      <div style={{marginTop:'40px', paddingTop:'20px', borderTop:'1px dashed #ccc'}}>
        <h4 style={{margin:0, color:'#666', display:'flex', alignItems:'center', gap:'8px'}}>
          <span style={{fontSize:'1.2rem'}}>🔌</span> Developer API Integration
        </h4>
        <div style={{background:'#2c3e50', color:'#fff', padding:'15px', borderRadius:'8px', marginTop:'10px', fontSize:'0.8rem', overflowX:'auto'}}>
          <strong>Endpoint:</strong> <code style={{color:'#3498db'}}>POST /check</code><br/>
          <strong>Command:</strong>
          <pre style={{margin:'10px 0 0', color:'#2ecc71'}}>
{`curl -X POST "${window.location.protocol}//${window.location.host.replace('3956', '3955')}/check" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "member_id": "${mId || 'M-001'}",
    "sku_id": "${sId || 'SKU-001'}"
  }'`}
          </pre>
        </div>
      </div>
    </div>
  );
};

const BatchAnalysis = () => {
  const [fileData, setFileData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [memberCol, setMemberCol] = useState('');
  const [skuCol, setSkuCol] = useState('');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [progress, setProgress] = useState(0);
  const [sharepointUrl, setSharepointUrl] = useState('');

  // Safety Match Flags
  const [chkAllergens, setCheckAllergens] = useState(true);
  const [chkConditions, setCheckConditions] = useState(true);
  const [chkPreferences, setCheckPreferences] = useState(true);

  const handleFileUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.name.endsWith('.csv')) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          setFileData(results.data);
          if (results.data.length > 0) {
            const h = Object.keys(results.data[0]);
            setHeaders(h);
            autoDetectColumns(h);
          }
        }
      });
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        setFileData(data);
        if (data.length > 0) {
          const h = Object.keys(data[0] as object);
          setHeaders(h);
          autoDetectColumns(h);
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  const autoDetectColumns = (h: string[]) => {
    const mMatch = h.find(col => /member|patient|user|client|id_member/i.test(col));
    const sMatch = h.find(col => /sku|product|item|code/i.test(col));
    if (mMatch) setMemberCol(mMatch);
    if (sMatch) setSkuCol(sMatch);
  };

  const runBatch = async () => {
    if (!memberCol || !skuCol) return alert("Please map Member and SKU columns");
    setProcessing(true);
    setResults([]);
    const total = fileData.length;
    const batchResults = [];

    for (let i = 0; i < total; i++) {
      const row = fileData[i];
      const mId = row[memberCol];
      const sId = row[skuCol];

      if (mId && sId) {
        try {
          const res = await axios.post(`${API_URL}/check`, { 
            member_id: String(mId), 
            sku_id: String(sId),
            check_allergens: chkAllergens,
            check_conditions: chkConditions,
            check_preferences: chkPreferences
          });
          batchResults.push({
            ...row,
            'SafeGuard Status': res.data.status,
            'SafeGuard Risk': res.data.risk_level,
            'SafeGuard Reasoning': res.data.trace.map((t: any) => `[${t.category}] ${t.finding}`).join('; ')
          });
        } catch {
          batchResults.push({ ...row, 'SafeGuard Status': 'ERROR', 'SafeGuard Risk': 'UNKNOWN', 'SafeGuard Reasoning': 'API Error' });
        }
      } else {
        batchResults.push({ ...row, 'SafeGuard Status': 'SKIPPED', 'SafeGuard Risk': 'N/A', 'SafeGuard Reasoning': 'Missing ID' });
      }
      setProgress(Math.round(((i + 1) / total) * 100));
    }

    setResults(batchResults);
    setProcessing(false);
  };

  const downloadResults = () => {
    const ws = XLSX.utils.json_to_sheet(results);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Safety Analysis");
    XLSX.writeFile(wb, "SafeGuard_Batch_Results.xlsx");
  };

  const connectSharepoint = () => {
    if (!sharepointUrl) return alert("Please enter Sharepoint/Excel URL");
    alert("Simulated: Connecting to Microsoft Graph API... \\nFound 'Safety_Checklist_May.xlsx'. \\nImporting rows...");
    // Mock data for demo
    const mockData = [
      { 'Patient Name': 'John Doe', 'Member_ID': 'M-001', 'Product_Code': 'SKU-001' },
      { 'Patient Name': 'Jane Smith', 'Member_ID': 'M-002', 'Product_Code': 'SKU-002' },
      { 'Patient Name': 'Bob Wilson', 'Member_ID': 'M-003', 'Product_Code': 'SKU-003' }
    ];
    setFileData(mockData);
    setHeaders(Object.keys(mockData[0]));
    autoDetectColumns(Object.keys(mockData[0]));
  };

  return (
    <div className="card">
      <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px'}}>
        <span style={{fontSize:'1.5rem'}}>📊</span>
        <h2 style={{margin:0}}>Batch Safety Analysis (Excel / CSV)</h2>
      </div>

      <div className="stats-grid" style={{marginBottom:'20px'}}>
        <div className="card" style={{background:'#f8f9fa'}}>
          <h3>Option A: Upload File</h3>
          <input type="file" accept=".csv, .xlsx, .xls" onChange={handleFileUpload} style={{width:'100%', padding:'10px', border:'1px dashed #ccc'}} />
          <p style={{fontSize:'0.8rem', color:'#666', marginTop:'10px'}}>Supported formats: .csv, .xlsx</p>
        </div>
        <div className="card" style={{background:'#f0f7ff'}}>
          <h3>Option B: Connect MS Excel / Sharepoint</h3>
          <div style={{display:'flex', gap:'10px'}}>
            <input 
              placeholder="https://sharepoint.com/:x:/s/..." 
              value={sharepointUrl} 
              onChange={e => setSharepointUrl(e.target.value)}
              style={{flex:1}}
            />
            <button className="btn-secondary" onClick={connectSharepoint}>Connect</button>
          </div>
          <p style={{fontSize:'0.8rem', color:'#666', marginTop:'10px'}}>Requires Microsoft 365 Enterprise Authorization</p>
        </div>
      </div>

      {fileData.length > 0 && (
        <div className="card" style={{border:'1px solid var(--pfb-green)'}}>
          <h3 style={{marginTop:0}}>Step 2: Map Columns</h3>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px'}}>
            <div className="form-group">
              <label>Member ID Column</label>
              <select value={memberCol} onChange={e => setMemberCol(e.target.value)} style={{width:'100%', padding:'10px'}}>
                <option value="">-- Select Column --</option>
                {headers.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>SKU / Product ID Column</label>
              <select value={skuCol} onChange={e => setSkuCol(e.target.value)} style={{width:'100%', padding:'10px'}}>
                <option value="">-- Select Column --</option>
                {headers.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          </div>
          
          <div style={{background:'#e8f5e9', padding:'15px', borderRadius:'8px', marginBottom:'20px'}}>
            <strong>Ready to process {fileData.length} rows.</strong> SafeGuard will analyze each Member against each SKU.
          </div>

          {!processing ? (
            <>
              <div className="form-group" style={{marginBottom:'20px', background:'#f8f9fa', padding:'15px', borderRadius:'8px', border:'1px solid #eee'}}>
                <label style={{display:'block', marginBottom:'10px', fontWeight:'bold', color:'var(--pfb-sidebar)'}}>Safety Match Options:</label>
                <div style={{display:'flex', gap:'25px'}}>
                  <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                    <input type="checkbox" checked={chkAllergens} onChange={e => setCheckAllergens(e.target.checked)} />
                    <span>Check Allergens</span>
                  </label>
                  <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                    <input type="checkbox" checked={chkConditions} onChange={e => setCheckConditions(e.target.checked)} />
                    <span>Check Conditions</span>
                  </label>
                  <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                    <input type="checkbox" checked={chkPreferences} onChange={e => setCheckPreferences(e.target.checked)} />
                    <span>Check Preferences</span>
                  </label>
                </div>
              </div>

              <button onClick={runBatch} style={{width:'100%', padding:'15px', fontSize:'1.1rem'}}>
                🚀 Run Full Batch Analysis
              </button>
            </>
          ) : (
            <div style={{width:'100%'}}>
              <div style={{height:'20px', background:'#eee', borderRadius:'10px', overflow:'hidden', marginBottom:'10px'}}>
                <div style={{height:'100%', width:`${progress}%`, background:'var(--pfb-green)', transition:'width 0.3s'}}></div>
              </div>
              <p style={{textAlign:'center'}}>Processing: {progress}% complete...</p>
            </div>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className="card" style={{marginTop:'20px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
            <h3 style={{margin:0}}>Analysis Results</h3>
            <button onClick={downloadResults} className="btn-secondary">📥 Download Enriched Excel</button>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'#f4f7f6'}}>
                  <th style={{padding:'10px', textAlign:'left'}}>Row</th>
                  <th style={{padding:'10px', textAlign:'left'}}>Member</th>
                  <th style={{padding:'10px', textAlign:'left'}}>SKU</th>
                  <th style={{padding:'10px', textAlign:'left'}}>Status</th>
                  <th style={{padding:'10px', textAlign:'left'}}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {results.slice(0, 10).map((r, i) => (
                  <tr key={i} style={{borderBottom:'1px solid #eee'}}>
                    <td style={{padding:'10px'}}>{i+1}</td>
                    <td style={{padding:'10px'}}>{r[memberCol]}</td>
                    <td style={{padding:'10px'}}>{r[skuCol]}</td>
                    <td style={{padding:'10px'}}>
                      <span className={`badge ${r['SafeGuard Status'] === 'APPROVED' ? 'green' : 'red'}`}>
                        {r['SafeGuard Status']}
                      </span>
                    </td>
                    <td style={{padding:'10px'}}>{r['SafeGuard Risk']}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {results.length > 10 && <p style={{fontSize:'0.8rem', color:'#666', marginTop:'10px'}}>Showing first 10 of {results.length} rows.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const ForbiddenSkuFinder = () => {
  const { toast } = useUI();
  const [mId, setMId] = useState('');
  const [extraAllergies, setExtraAllergies] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  // Safety Match Flags
  const [chkAllergens, setCheckAllergens] = useState(true);
  const [chkConditions, setCheckConditions] = useState(true);
  const [chkPreferences, setCheckPreferences] = useState(true);

  const runFinder = async () => {
    if (!mId) { toast("Select Member/Patient first", "info"); return; }
    setLoading(true);
    setResults(null);
    try {
      const algs = extraAllergies.split(',').map(a => a.trim()).filter(Boolean);
      const res = await axios.post(`${API_URL}/check/forbidden-skus`, {
        member_id: mId,
        extra_allergies: algs,
        check_allergens: chkAllergens,
        check_conditions: chkConditions,
        check_preferences: chkPreferences
      });
      setResults(res.data);
    } catch { toast("Finder failed. Verify Member ID.", "error"); }
    setLoading(false);
  };

  return (
    <div className="card">
      <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px'}}>
        <span style={{fontSize:'1.5rem'}}>🚫</span>
        <h2 style={{margin:0}}>Forbidden SKU Finder</h2>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'30px'}}>
        <SearchableSelector 
          type="members" 
          label="Search Patient (ID or Name)" 
          placeholder="Type patient name..." 
          onSelect={(m: any) => setMId(m ? m.id : '')} 
        />
        <div className="form-group">
          <label>Simulate Extra Allergies (Comma separated)</label>
          <input 
            placeholder="e.g. Eggs, Gluten, L-102..." 
            value={extraAllergies} 
            onChange={e => setExtraAllergies(e.target.value)} 
            style={{width:'100%', padding:'12px'}}
          />
          <small style={{color:'#666'}}>Add temporary restrictions for this simulation (Name or Code).</small>
        </div>
      </div>

      <div className="form-group" style={{marginBottom:'20px', background:'#f8f9fa', padding:'15px', borderRadius:'8px', border:'1px solid #eee'}}>
        <label style={{display:'block', marginBottom:'10px', fontWeight:'bold', color:'var(--pfb-sidebar)'}}>Safety Match Options:</label>
        <div style={{display:'flex', gap:'25px'}}>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={chkAllergens} onChange={e => setCheckAllergens(e.target.checked)} />
            <span>Check Allergens</span>
          </label>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={chkConditions} onChange={e => setCheckConditions(e.target.checked)} />
            <span>Check Conditions</span>
          </label>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={chkPreferences} onChange={e => setCheckPreferences(e.target.checked)} />
            <span>Check Preferences</span>
          </label>
        </div>
      </div>

      <button onClick={runFinder} disabled={loading} style={{width:'100%', padding:'15px', fontSize:'1.1rem', background:'var(--pfb-sidebar)'}}>
        {loading ? '🔍 Scanning Catalog...' : '🚀 Generate Forbidden SKU List'}
      </button>

      {results && (
        <div style={{marginTop:'30px', animation: 'fadeIn 0.5s'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
             <h3 style={{margin:0}}>Results for: <span style={{color:'var(--pfb-green)'}}>{results.member_name}</span></h3>
             <div className="badge red" style={{fontSize:'1rem', padding:'10px 20px'}}>
                {results.forbidden_count} Restricted Products Identified
             </div>
          </div>

          {results.items.length === 0 ? (
            <div className="card" style={{textAlign:'center', padding:'40px', background:'#f0fff4', border:'1px solid #c6f6d5'}}>
               <div style={{fontSize:'3rem', marginBottom:'10px'}}>✅</div>
               <p>No products are restricted for this profile. All items in the catalog are safe to receive.</p>
            </div>
          ) : (
            <div style={{maxHeight:'500px', overflowY:'auto'}}>
              <table style={{width:'100%', borderCollapse:'collapse'}}>
                <thead style={{position:'sticky', top:0, background:'#f8f9fa', zIndex:1}}>
                  <tr>
                    <th style={{textAlign:'left', padding:'12px'}}>SKU ID</th>
                    <th style={{textAlign:'left', padding:'12px'}}>Product Name</th>
                    <th style={{textAlign:'left', padding:'12px'}}>Primary Reason for Block</th>
                    <th style={{textAlign:'center', padding:'12px'}}>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {results.items.map((item: any, idx: number) => (
                    <tr key={idx} style={{borderBottom:'1px solid #eee', background: idx % 2 === 0 ? '#fff' : '#fafafa'}}>
                      <td style={{padding:'12px'}}><code>{item.sku_id}</code></td>
                      <td style={{padding:'12px'}}><strong>{item.sku_name}</strong></td>
                      <td style={{padding:'12px', color:'#c0392b', fontSize:'0.9rem'}}>{item.reason}</td>
                      <td style={{padding:'12px', textAlign:'center'}}>
                        <span className="badge red">{item.risk_level}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div style={{marginTop:'40px', paddingTop:'20px', borderTop:'1px dashed #ccc'}}>
        <h4 style={{margin:0, color:'#666', display:'flex', alignItems:'center', gap:'8px'}}>
          <span style={{fontSize:'1.2rem'}}>🔌</span> Developer API Integration
        </h4>
        <div style={{background:'#2c3e50', color:'#fff', padding:'15px', borderRadius:'8px', marginTop:'10px', fontSize:'0.8rem', overflowX:'auto'}}>
          <strong>Endpoint:</strong> <code style={{color:'#3498db'}}>POST /check/forbidden-skus</code><br/>
          <strong>Command:</strong>
          <pre style={{margin:'10px 0 0', color:'#2ecc71'}}>
{`curl -X POST "${window.location.protocol}//${window.location.host.replace('3956', '3955')}/check/forbidden-skus" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "member_id": "${mId || 'M-001'}",
    "extra_allergies": ${JSON.stringify(extraAllergies.split(',').map(a => a.trim()).filter(Boolean))}
  }'`}
          </pre>
        </div>
      </div>
    </div>
  );
};

const AllergenImpactCheck = () => {
  const { toast } = useUI();
  const [selectedAlg, setSelectedAlg] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  const runCheck = async () => {
    if (!selectedAlg) { toast("Select an Allergen first", "info"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/check/allergen-impact`, {
        allergen_id: selectedAlg.id
      });
      setResults(res.data);
    } catch { toast("Impact check failed", "error"); }
    setLoading(false);
  };

  return (
    <div className="card">
      <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px'}}>
        <span style={{fontSize:'1.5rem'}}>🥜</span>
        <h2 style={{margin:0}}>Allergen Impact Check</h2>
      </div>

      <div style={{marginBottom:'30px'}}>
        <SearchableSelector 
          type="allergens" 
          label="Search Allergen (ID or Name)" 
          placeholder="Type allergen name..." 
          onSelect={(a: any) => setSelectedAlg(a)} 
        />
        {selectedAlg && (
          <div style={{marginTop:'10px', fontSize:'0.8rem', color:'#666'}}>
            Selected: <strong>{selectedAlg.name}</strong> ({selectedAlg.id})
          </div>
        )}
      </div>

      <button onClick={runCheck} disabled={loading} style={{width:'100%', padding:'15px', fontSize:'1.1rem', background:'var(--pfb-sidebar)'}}>
        {loading ? '🔍 Analyzing Impact...' : '🚀 Find Impacted SKUs'}
      </button>

      {results && (
        <div style={{marginTop:'30px', animation: 'fadeIn 0.5s'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
             <h3 style={{margin:0}}>Impact for: <span style={{color:'var(--pfb-orange)'}}>{results.allergen_name}</span></h3>
             <div className="badge red" style={{fontSize:'1rem', padding:'10px 20px'}}>
                {results.impacted_count} Impacted Products
             </div>
          </div>
          
          {results.items.length > 0 ? (
            <div style={{maxHeight:'500px', overflowY:'auto'}}>
              <table className="registry-table">
                <thead>
                  <tr>
                    <th>SKU ID</th>
                    <th>Product Name</th>
                    <th>Reason for Exclusion</th>
                  </tr>
                </thead>
                <tbody>
                  {results.items.map((it: any) => (
                    <tr key={it.sku_id}>
                      <td style={{fontWeight:'bold'}}>{it.sku_id}</td>
                      <td>{it.sku_name}</td>
                      <td><span className="badge" style={{background:'#fceaea', color:'#e74c3c'}}>{it.reason}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card" style={{textAlign:'center', padding:'40px'}}>
               <p style={{fontSize:'1.2rem', color:'#999'}}>No products found containing this allergen.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Operational Checks — member-centric batch upload (EXCLUDED_MEALS format, like
// clinical_batch_test2.py). The browser parses the file; the server runs an async job
// with AI input/output validation and stores every result (filterable in Forensics).
const BatchFileCheckView = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [memberCol, setMemberCol] = useState('');
  const [notesCols, setNotesCols] = useState<string[]>([]);
  // auto_discover is ON by default: a new allergen appearing in a file should reach the RDN
  // queue by itself (attributed to this file + uploader), not wait for someone to notice it.
  // Queue items are suggestions only — never the graph — and NEEDS_RDN_REVIEW still lists
  // every unresolved term either way. Uncheck for a pure audit run on a noisy logistics file.
  const [flags, setFlags] = useState({ allergens: true, conditions: true, preferences: true, auto_discover: true });
  const [jobId, setJobId] = useState<number | null>(null);
  const [job, setJob] = useState<any>(null);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<any>(null);
  // Finished runs stay downloadable. The job lives on the server, but this component's
  // state does not survive a reload or a re-login — and a long run easily outlasts the
  // inactivity logout, which is exactly when someone comes back for the file.
  const [recent, setRecent] = useState<any[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);

  const detectMember = (h: string[]) => h.find(c => /member|patient|mrn|external|client|id/i.test(c)) || '';
  // Columns carrying free-text dietary/allergen wording (mirrors the batch scripts' convention).
  const NOTE_EXACT = ['allergen', 'allergens', 'allergen_name', 'allergen_text', 'package_notes', 'notes'];
  const detectNotes = (h: string[]) => {
    const low: any = {}; h.forEach(c => { low[String(c).toLowerCase()] = c; });
    const picked: string[] = NOTE_EXACT.filter(c => low[c]).map(c => low[c]);
    h.forEach(c => {
      if (!picked.includes(c) && /(allerg|note|restriction|dietar|intoleran|preference_meals|contact_notes|comments)/i.test(c)) picked.push(c);
    });
    return picked;
  };
  // The AI reads headers + a sample and PICKS the columns — the operator shouldn't have to.
  // The name-based guess above is only the instant placeholder while this runs, and the
  // fallback if the reviewer is unavailable.
  const runAnalyze = async (all: any[]) => {
    setAnalyzing(true); setAnalysis(null);
    try {
      const r = await axios.post(`${API_URL}/check/batch-file/analyze`, { rows: all.slice(0, 10) });
      const a = r.data || {};
      setAnalysis(a);
      if (a.member_column) setMemberCol(a.member_column);
      if (a.notes_columns && a.notes_columns.length) setNotesCols(a.notes_columns);
    } catch (e: any) {
      setAnalysis({ verdict: 'warn', ai_available: false,
        reason: 'Could not analyse the file automatically — the columns below are a name-based guess, please check them.' });
    } finally { setAnalyzing(false); }
  };

  const onFile = (e: any) => {
    const file = e.target.files?.[0]; if (!file) return;
    setFileName(file.name); setJob(null); setJobId(null); setErr(''); setAnalysis(null);
    const done = (data: any[]) => {
      // keep only rows that actually carry a value (drops trailing blank lines)
      const clean = (data || []).filter((r: any) => r && Object.keys(r).some(k => String(r[k] ?? '').trim() !== ''));
      setRows(clean);
      const h = clean.length ? Object.keys(clean[0]) : []; setHeaders(h); setMemberCol(detectMember(h)); setNotesCols(detectNotes(h));
      if (clean.length) runAnalyze(clean);   // AI decides the columns for real
      if (!clean.length) {
        setErr('Could not read any rows from that file. Check the encoding/delimiter — an Excel “Unicode Text” (UTF-16) export or an unusual separator parses as empty. Re-save as UTF-8 CSV, or upload the .xlsx directly.');
      } else if (h.length === 1) {
        setErr(`Only one column was detected (“${h[0]}”) — the delimiter is probably wrong. Re-save as a comma/semicolon-separated UTF-8 CSV, or upload the .xlsx.`);
      }
    };
    if (file.name.toLowerCase().endsWith('.csv')) {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: (r: any) => done(r.data),
        error: (e: any) => setErr(`Could not parse the CSV: ${e?.message || e}`),
      });
    } else {
      const rd = new FileReader();
      rd.onload = (ev: any) => { const wb = XLSX.read(ev.target.result, { type: 'binary' }); done(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])); };
      rd.readAsBinaryString(file);
    }
  };

  // Readable errors: a proxy 413 arrives as an HTML page with no `detail`, which is why a
  // whole-file upload used to fail with a useless "Upload failed."
  const describeErr = (e: any) => {
    const st = e?.response?.status;
    if (st === 413) return 'The server rejected the request as too large. Try a smaller file, or ask the team to raise the proxy body limit.';
    if (st === 401) return 'Your session expired — sign in again and retry.';
    if (st === 403) return 'Your role is not allowed to run batch checks.';
    const d = e?.response?.data;
    const detail = typeof d === 'string' ? d.slice(0, 200) : (d?.detail || d?.message);
    return detail ? `${st ? `HTTP ${st}: ` : ''}${detail}` : `Upload failed${st ? ` (HTTP ${st})` : ''}: ${e?.message || 'network error'}`;
  };

  // Rows go up in CHUNKS sized by BYTES (not row count): one body with a whole file exceeds
  // the 1 MB proxy limit and is rejected (413) before reaching the API, and a wide file can
  // blow a fixed row-count chunk too. The last chunk starts the job.
  const MAX_CHUNK_BYTES = 500 * 1024;
  const chunkRows = (all: any[]) => {
    const out: any[][] = []; let cur: any[] = []; let bytes = 0;
    for (const r of all) {
      const b = JSON.stringify(r).length + 1;
      if (cur.length && (bytes + b > MAX_CHUNK_BYTES || cur.length >= 1000)) { out.push(cur); cur = []; bytes = 0; }
      cur.push(r); bytes += b;
    }
    if (cur.length) out.push(cur);
    return out;
  };

  const run = async () => {
    if (!rows.length) return setErr('Upload a file first.');
    if (!memberCol && !notesCols.length) return setErr('Pick a patient-id column and/or at least one dietary-notes column.');
    setErr(''); setJob(null); setJobId(null); setUploadPct(0); setUploading(true);
    try {
      const chunks = chunkRows(rows);
      let uid: string | null = null; let sent = 0;
      for (let c = 0; c < chunks.length; c++) {
        const isFinal = c === chunks.length - 1;
        const r = await axios.post(`${API_URL}/check/batch-file`, {
          upload_id: uid, rows: chunks[c], member_col: memberCol, notes_cols: notesCols,
          flags, filename: fileName, final: isFinal,
        });
        uid = r.data.upload_id || uid;
        sent += chunks[c].length;
        setUploadPct(Math.round((sent / rows.length) * 100));
        if (isFinal) { setJobId(r.data.job_id); setJob({ status: 'PENDING', message: 'Queued…' }); }
      }
    } catch (e: any) {
      setErr(describeErr(e));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!jobId) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API_URL}/check/batch-file/${jobId}`);
        if (stop) return;
        setJob(r.data);
        if (r.data.status === 'COMPLETED' || r.data.status === 'FAILED') { loadRecent(); return; }
      } catch { }
      if (!stop) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [jobId]);

  const writeSheet = (j: any, name: string) => {
    // Honour the server's explicit column order (original columns first, ours appended at the
    // right). Without it the sheet follows object-key order, which Postgres JSONB scrambles.
    const cols = j.result_columns_all && j.result_columns_all.length ? j.result_columns_all : undefined;
    const ws = XLSX.utils.json_to_sheet(j.result_rows || [], cols ? { header: cols } : undefined);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Results');
    XLSX.writeFile(wb, (String(name || 'batch').replace(/\.(csv|xlsx|xls)$/i, '') || 'batch') + '_SafeGuard.xlsx');
  };

  const download = () => writeSheet(job, fileName || job.source_file);

  /** Re-fetch a finished run by id and save it — used by the Recent runs list. */
  const downloadById = async (id: number, name: string) => {
    setBusyId(id); setErr('');
    try {
      const r = await axios.get(`${API_URL}/check/batch-file/${id}`);
      if (!r.data?.result_rows?.length) { setErr('That run has no stored results to download.'); return; }
      writeSheet(r.data, name || r.data.source_file);
    } catch (e: any) { setErr(describeErr(e)); } finally { setBusyId(null); }
  };

  const loadRecent = async () => {
    try {
      const r = await axios.get(`${API_URL}/system/jobs`, { params: { limit: 40 } });
      setRecent((r.data || []).filter((j: any) => j.job_type === 'BATCH_FILE_CHECK').slice(0, 8));
    } catch { /* the list is a convenience; never block the screen on it */ }
  };
  useEffect(() => { loadRecent(); }, []);

  const Verdict = ({ v, label }: any) => {
    if (!v) return null;
    const col = v.verdict === 'reject' ? '#b42318' : v.verdict === 'warn' ? '#9a5b00' : '#067647';
    return (
      <div style={{ borderLeft: `4px solid ${col}`, background: '#fafbfc', padding: '8px 12px', margin: '6px 0', fontSize: '0.84rem' }}>
        <b style={{ color: col }}>{label}: {String(v.verdict).toUpperCase()}</b>
        {!v.ai_available && <em style={{ color: '#999' }}> (AI reviewer unavailable)</em>}
        {v.reason && <div style={{ color: '#555' }}>{v.reason}</div>}
        {(v.issues || v.anomalies || []).slice(0, 5).map((x: string, i: number) => <div key={i} style={{ color: '#777' }}>• {x}</div>)}
      </div>
    );
  };

  const running = job && !['COMPLETED', 'FAILED'].includes(job.status);
  return (
    <div className="card" style={{ marginTop: 16, borderTop: '4px solid #0f766e' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: '1.4rem' }}>📥</span>
        <h2 style={{ margin: 0 }}>Batch File Check (patients → excluded meals)</h2>
      </div>
      <p style={{ fontSize: '0.82rem', color: '#666', marginTop: 0 }}>
        Upload a CSV/Excel of patients; SafeGuard appends three columns at the right: <code>EXCLUDED_MEALS</code> (the SKU codes not to send),
        <code>CLEAN_PACKAGE_NOTES</code> (the delivery instructions, PHI removed) and <code>ALLERGENS</code> (normalized allergens detected).
        An AI reviews the file's format on the way in and the results on the way out; every row is stored and auditable in Safety-Check Forensics.
      </p>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><label style={{ fontSize: '0.75rem', color: '#555' }}>File (.csv, .xlsx)</label><br />
          <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} /></div>
        {headers.length > 0 && <div><label style={{ fontSize: '0.75rem', color: '#555' }}>Patient-id column <span style={{ color: '#999' }}>(auto-detected — change only if wrong)</span></label><br />
          <select value={memberCol} onChange={e => setMemberCol(e.target.value)}>
            <option value="">— none —</option>{headers.map(h => <option key={h} value={h}>{h}</option>)}
          </select></div>}
        {headers.length > 0 && <div><label style={{ fontSize: '0.75rem', color: '#555' }}>Dietary / allergen notes column(s) <span style={{ color: '#999' }}>(auto-detected)</span></label><br />
          <select multiple size={4} value={notesCols} style={{ minWidth: 230 }}
            onChange={e => setNotesCols(Array.from((e.target as any).selectedOptions, (o: any) => o.value))}>
            {headers.map(h => <option key={h} value={h}>{h}</option>)}
          </select></div>}
        {rows.length > 0 && <span style={{ fontSize: '0.8rem', color: '#777' }}>{rows.length} rows</span>}
      </div>
      {analyzing && <p style={{ fontSize: '0.82rem', color: '#0f766e', margin: '8px 0 0' }}>🤖 Analysing the file’s format and choosing the columns…</p>}
      {analysis && !analyzing && (
        <div style={{
          margin: '8px 0 0', padding: '10px 12px', borderRadius: 10, fontSize: '0.82rem',
          background: analysis.verdict === 'reject' ? '#fbeae8' : '#eef6f4',
          border: `1px solid ${analysis.verdict === 'reject' ? '#e7b7b0' : '#cfe6e0'}`,
        }}>
          <b style={{ color: analysis.verdict === 'reject' ? '#b42318' : '#0f766e' }}>
            {analysis.verdict === 'reject' ? '⛔ File rejected — return it to its origin' : '🤖 File analysed'}
          </b>
          {analysis.ai_available === false && <em style={{ color: '#999' }}> (AI reviewer unavailable — name-based guess)</em>}
          <div style={{ marginTop: 4 }}>
            Patient id: <b>{memberCol || '— none —'}</b> &nbsp;·&nbsp; Dietary notes: <b>{notesCols.join(', ') || '— none —'}</b>
          </div>
          {analysis.reason && <div style={{ color: '#555', marginTop: 2 }}>{analysis.reason}</div>}
          {(analysis.issues || []).slice(0, 4).map((x: string, i: number) => <div key={i} style={{ color: '#777' }}>• {x}</div>)}
          <div style={{ color: '#888', marginTop: 4, fontSize: '0.75rem' }}>
            Exclusions use the patient's profile (when the id is known to SafeGuard) <b>and</b> the dietary notes in the file.
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, margin: '10px 0', fontSize: '0.84rem', flexWrap: 'wrap' }}>
        {(['allergens', 'conditions', 'preferences'] as const).map(k => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={(flags as any)[k]} onChange={e => setFlags({ ...flags, [k]: e.target.checked })} /> {k}
          </label>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={flags.auto_discover} onChange={e => setFlags({ ...flags, auto_discover: e.target.checked })} />
          <span title="New allergens found in this file are suggested to the RDN review queue, recorded with the file name and your username. They never enter the graph without RDN approval.">
            report unknown allergens to RDNs (attributed to you)
          </span>
        </label>
      </div>
      <button className="btn-primary" onClick={run}
        disabled={uploading || analyzing || !!running || !rows.length || analysis?.verdict === 'reject'}>
        {uploading ? `Uploading… ${uploadPct}%` : analyzing ? 'Analysing…' : running ? 'Processing…' : '▶ Run batch check'}
      </button>
      {uploading && (
        <div style={{ background: '#eee', borderRadius: 6, height: 8, margin: '8px 0', overflow: 'hidden' }}>
          <div style={{ width: `${uploadPct}%`, height: '100%', background: '#0f766e', transition: 'width .2s' }} /></div>
      )}
      {err && <div style={{ color: '#b42318', marginTop: 8, fontSize: '0.84rem' }}>⚠ {err}</div>}

      {job && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Status: {job.status} {job.message ? `— ${job.message}` : ''}</div>
          {running && typeof job.progress_pct === 'number' && (
            <div style={{ background: '#eee', borderRadius: 6, height: 8, margin: '6px 0', overflow: 'hidden' }}>
              <div style={{ width: `${job.progress_pct}%`, height: '100%', background: '#0f766e' }} /></div>
          )}
          {job.rejected && <div style={{ background: '#fbeae8', border: '1px solid #e7b7b0', borderRadius: 8, padding: 10, margin: '8px 0', color: '#b42318' }}>
            ⛔ <b>Rejected — return to origin.</b> {job.error || job.input_validation?.reason}</div>}
          <Verdict v={job.input_validation} label="Input review" />
          <Verdict v={job.output_validation} label="Output review" />
          {job.status === 'COMPLETED' && job.result_rows && (
            <button className="btn-secondary" style={{ marginTop: 8 }} onClick={download}>⬇ Download results ({job.result_rows.length} rows)</button>
          )}
        </div>
      )}

      {recent.length > 0 && (
        <div style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <b style={{ fontSize: '0.85rem' }}>Your recent runs</b>
            <button className="btn-secondary" style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={loadRecent}>↻</button>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#777', margin: '4px 0 8px' }}>
            A finished run stays here — you can close the page, log out and come back for the file.
          </p>
          <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
            <tbody>
              {recent.map((j: any) => {
                const f = j.metadata?.source_file || '(file)';
                const when = j.started_at ? new Date(j.started_at).toLocaleString() : '';
                const ok = j.status === 'COMPLETED' && !j.metadata?.rejected;
                return (
                  <tr key={j.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 8px 6px 0' }}>📄 {f}</td>
                    <td style={{ padding: '6px 8px', color: '#777', whiteSpace: 'nowrap' }}>{when}</td>
                    <td style={{ padding: '6px 8px', color: '#777' }}>{j.total_items || 0} rows</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{ color: ok ? '#067647' : j.status === 'FAILED' ? '#b42318' : '#9a5b00' }}>
                        {j.metadata?.rejected ? 'REJECTED' : j.status}
                      </span>
                    </td>
                    <td style={{ padding: '6px 0', textAlign: 'right' }}>
                      {ok && <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                        disabled={busyId === j.id} onClick={() => downloadById(j.id, f)}>
                        {busyId === j.id ? 'Preparing…' : '⬇ Download'}
                      </button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TestsView = ({ userRole }: { userRole?: string }) => {
  // The Simulator and the Forbidden SKU Finder both start by SEARCHING the patient roster,
  // which Operations may not do (no PHI). Its file-driven checks — Batch Analysis and the
  // Batch File Check below — still work, since those files come with the operator.
  const canSeePatients = userRole !== 'Operations';
  const [activeSubTab, setActiveSubTab] = useState(canSeePatients ? 'simulator' : 'impact');

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1 style={{margin:0}}>Clinical Safety Testing & Batch Analysis</h1>
        <div style={{display:'flex', gap:'10px'}}>
          {canSeePatients && <button
            className={activeSubTab === 'simulator' ? 'btn-primary' : 'btn-secondary'}
            title="Check ONE patient against ONE product and see the full safety reasoning. Best for a quick, one-off question."
            onClick={() => setActiveSubTab('simulator')}
          >
            🛡️ Single Simulator (CSARE)
          </button>}
          <button
            className={activeSubTab === 'batch' ? 'btn-primary' : 'btn-secondary'}
            title="Upload a spreadsheet of many patient↔product pairs and check them all at once. Best before a large shipment."
            onClick={() => setActiveSubTab('batch')}
          >
            📊 Batch Analysis (Excel)
          </button>
          {canSeePatients && <button
            className={activeSubTab === 'forbidden' ? 'btn-primary' : 'btn-secondary'}
            title="Pick a patient and get EVERY product in the catalog they cannot receive, with the reason for each."
            onClick={() => setActiveSubTab('forbidden')}
          >
            🚫 Forbidden SKU Finder
          </button>}
          <button
            className={activeSubTab === 'impact' ? 'btn-primary' : 'btn-secondary'}
            title="Pick an allergen and see every product that contains it — no patient needed. Useful to audit the catalog or a recall."
            onClick={() => setActiveSubTab('impact')}
          >
            🥜 Allergen Impact Check
          </button>
        </div>
      </div>

      {activeSubTab === 'simulator' && canSeePatients && <SkuChecker />}
      {activeSubTab === 'batch' && <BatchAnalysis />}
      {activeSubTab === 'forbidden' && canSeePatients && <ForbiddenSkuFinder />}
      {activeSubTab === 'impact' && <AllergenImpactCheck />}
    </div>
  );
};

interface User { username: string; is_active: number; mfa_enabled: number; last_login?: string; }
const IngredientEvidenceView = ({ ingredient, onBack }: { ingredient: any, onBack: () => void }) => {
  const [aiMatch, setAiMatch] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const getAiMapping = async () => {
    setAiLoading(true);
    try {
      const res = await axios.get(`${API_URL}/ai/map-ingredient`, { params: { name: ingredient.name } });
      setAiMatch(res.data);
    } catch { alert("AI Search Failed"); }
    setAiLoading(false);
  };

  return (
    <div className="card">
      <div style={{display:'flex', gap:'15px', alignItems:'center', marginBottom:'20px'}}>
        <button className="btn-secondary" onClick={onBack}>← Back to List</button>
        <h2 style={{margin:0}}>Ingredient Intelligence: {ingredient.name}</h2>
      </div>

      <div className="stats-grid">
        <div className="card">
          <h3>Current Clinical Metadata</h3>
          <p><strong>FDA UNII:</strong> {ingredient.unii_code || 'Not mapped'}</p>
          <p><strong>FDC ID:</strong> {ingredient.fdc_id || 'N/A'}</p>
          <button className="btn-secondary" onClick={getAiMapping} disabled={aiLoading}>
            {aiLoading ? '🤖 AI Thinking...' : '🤖 Run Semantic AI Mapping'}
          </button>
        </div>

        {aiMatch && aiMatch.status === 'SUCCESS' && (
          <div className="card" style={{border:'2px solid #3498db', background:'#f0f7ff'}}>
            <h3 style={{color:'#2980b9'}}>🤖 AI Suggested Mapping</h3>
            <p><strong>Suggested Name:</strong> {aiMatch.match.suggested_name}</p>
            <p><strong>UNII:</strong> {aiMatch.match.unii}</p>
            <p><strong>Confidence:</strong> {aiMatch.match.confidence}%</p>
            <p><strong>Detected Allergens:</strong> {aiMatch.match.allergens.join(', ') || 'None'}</p>
            <button style={{width:'100%'}}>Accept & Update Master Data</button>
          </div>
        )}
      </div>
    </div>
  );
};

const MemberDetail = ({ member, onBack, onEdit }: { member: any, onBack: () => void, onEdit: (m: any) => void }) => {
  const [details, setDetails] = useState<any>(null);
  const [prefText, setPrefText] = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/members/${member.id}`).then(res => {
      setDetails(res.data);
      if (res.data.preferences) {
        setPrefText(res.data.preferences.join(', '));
      }
    });
  }, [member.id]);

  const downloadReport = () => {
    window.open(`${API_URL}/reports/member/${member.id}/safety-summary`, '_blank');
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      await axios.post(`${API_URL}/members/${member.id}/preferences`, { raw_preferences: prefText });
      const res = await axios.get(`${API_URL}/members/${member.id}`);
      setDetails(res.data);
      alert("Personal preferences updated and normalized by AI.");
    } catch { alert("Failed to update preferences."); }
    setSavingPrefs(false);
  };

  if (!details) return <div className="card">Loading patient profile...</div>;

  return (
    <div className="card">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
        <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
          <button className="btn-secondary" onClick={onBack}>← Back</button>
          <h2 style={{margin:0}}>Clinical Profile: {details.name}</h2>
        </div>
        <div style={{display:'flex', gap:'10px'}}>
          <button className="btn-secondary" onClick={() => onEdit(details)} style={{borderColor:'var(--pfb-green)', color:'var(--pfb-green)'}}>✏️ Edit Full Profile</button>
          <button onClick={downloadReport} style={{background: '#f8f9fa', color:'#333', border:'1px solid #ddd'}}>📄 Download Summary</button>
        </div>
      </div>
      
      <div className="stats-grid">
        <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
          <div className="card">
            <h3>Declared Allergies (Clinical)</h3>
            <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
              {(details.allergies || []).map((a: any) => {
                const display = (a && typeof a === 'object') ? (a.name || a.id || JSON.stringify(a)) : String(a);
                return <span key={display} className="badge red">{display}</span>;
              })}
              {(!details.allergies || details.allergies.length === 0) && <p style={{color:'#999', fontSize:'0.9rem'}}>No clinical allergies recorded.</p>}
            </div>
          </div>

          <div className="card" style={{border: '2px solid var(--pfb-orange)', background: '#fffcf9'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'15px'}}>
               <div>
                  <h3 style={{color:'var(--pfb-orange)', margin:0}}>🍴 Personal Food Preferences</h3>
                  <p style={{fontSize:'0.75rem', color:'#666', margin:'5px 0 0'}}>RDN authorization NOT required. These will be added as <strong>soft-blocks</strong> (YELLOW level).</p>
               </div>
               <button onClick={handleSavePrefs} disabled={savingPrefs} style={{background:'var(--pfb-orange)', border:'none', padding:'10px 20px', borderRadius:'8px', fontWeight:'bold', boxShadow:'0 4px 6px rgba(0,0,0,0.1)'}}>
                  {savingPrefs ? '⌛ Normalizing...' : '💾 Save & Normalize'}
               </button>
            </div>

            <textarea 
              style={{width:'100%', height:'100px', padding:'15px', borderRadius:'8px', border:'1px solid #ddd', fontSize:'1rem', boxShadow:'inset 0 2px 4px rgba(0,0,0,0.05)'}}
              placeholder="e.g. No eggs, avoid mushrooms, prefer low salt..."
              value={prefText}
              onChange={e => setPrefText(e.target.value)}
            />

            <div style={{marginTop:'15px'}}>
               <div style={{fontSize:'0.7rem', color:'#999', marginBottom:'8px', textTransform:'uppercase', fontWeight:'bold'}}>Active Preferences (Standardized):</div>
               <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                  {details.preferences?.map((p: string) => <span key={p} className="badge yellow" style={{padding:'5px 12px', fontSize:'0.85rem'}}>{p}</span>)}
                  {(!details.preferences || details.preferences.length === 0) && <em style={{color:'#ccc', fontSize:'0.85rem'}}>No preferences defined yet.</em>}
               </div>
            </div>
          </div>

        </div>

        <div className="card">
          <h3>Medical Conditions (ICD-10)</h3>
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            {(details.conditions || []).map((c: any) => {
              const display = (c && typeof c === 'object') ? `${c.name || c.id}` : String(c);
              const key = (c && typeof c === 'object') ? (c.id || c.name) : String(c);
              return (
                <div key={key} style={{padding:'10px', background: '#f0f7f0', borderRadius:'6px', borderLeft:'4px solid var(--pfb-green)'}}>
                  <div style={{fontWeight:'bold'}}>{display}</div>
                  <small style={{color:'#666'}}>{c.icd10_code || c.code || 'N/A'}</small>
                </div>
              );
            })}
            {(!details.conditions || details.conditions.length === 0) && <p style={{color:'#999', fontSize:'0.9rem'}}>No active medical conditions.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const PartnerSelector = ({ value, onChange }: any) => {
  const [partners, setPartners] = useState<any[]>([]);
  useEffect(() => { axios.get(`${API_URL}/partners`, { params: { limit: 100 } }).then(res => setPartners(res.data.items || [])); }, []);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={{width:'100%', padding:'10px'}}>
      <option value="">-- No Partner --</option>
      {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
};

const IngredientMultiSelector = ({ selectedIds, onChange, disabled = false }: { selectedIds: string[], onChange: (ids: string[]) => void, disabled?: boolean }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  
  useEffect(() => {
    if (search.length > 1) {
      axios.get(`${API_URL}/ingredients`, { params: { search, limit: 5 } }).then(res => setResults(res.data.items || []));
    } else setResults([]);
  }, [search]);

  const toggle = (id: string) => {
    if (disabled) return;
    const next = selectedIds.includes(id) ? selectedIds.filter(i => i !== id) : [...selectedIds, id];
    onChange(next);
  };

  return (
    <div style={{border: '1px solid #ddd', padding: '10px', borderRadius: '4px', background: disabled ? '#f9f9f9' : 'white'}}>
      <div style={{display:'flex', gap:'5px', flexWrap:'wrap', marginBottom:'10px'}}>
        {selectedIds.map((id: any, idx: number) => {
          let displayId: any = id;
          if (id && typeof id === 'object') {
             displayId = id.id || id.name || id.code || JSON.stringify(id);
          }
          const finalDisplay = (displayId && typeof displayId === 'object') ? JSON.stringify(displayId) : String(displayId);
          return (
            <span key={idx} className="badge" style={{background: 'var(--pfb-sidebar)', color: 'white'}}>
              {finalDisplay} {!disabled && <span style={{cursor:'pointer', marginLeft:'5px'}} onClick={() => toggle(id)}>×</span>}
            </span>
          );
        })}
        {selectedIds.length === 0 && <span style={{color:'#999', fontSize:'0.8rem'}}>No items selected.</span>}
      </div>
      {!disabled && (
        <>
          <input placeholder="Type to search..." value={search} onChange={e => setSearch(e.target.value)} style={{marginBottom:'5px'}} />
          {results.length > 0 && (
            <div style={{maxHeight:'150px', overflowY:'auto', border:'1px solid #eee'}}>
              {results.map(r => (
                <div key={r.id} onClick={() => { toggle(r.id); setSearch(''); }} style={{padding:'8px', cursor:'pointer', borderBottom:'1px solid #f9f9f9', fontSize:'0.9rem'}}>
                  <strong>{r.name}</strong> <small style={{color:'#666'}}>{r.id}</small>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const GenericMultiSelector = ({ type, selectedIds, onChange, disabled = false }: { type: string, selectedIds: string[], onChange: (ids: string[]) => void, disabled?: boolean }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  
  useEffect(() => {
    if (search.length > 1) {
      axios.get(`${API_URL}/${type}`, { params: { search, limit: 5 } }).then(res => setResults(res.data.items || []));
    } else setResults([]);
  }, [search, type]);

  const toggle = (id: string) => {
    if (disabled) return;
    const next = selectedIds.includes(id) ? selectedIds.filter(i => i !== id) : [...selectedIds, id];
    onChange(next);
  };

  return (
    <div style={{border: '1px solid #ddd', padding: '10px', borderRadius: '4px', background: disabled ? '#f9f9f9' : 'white'}}>
      <div style={{display:'flex', gap:'5px', flexWrap:'wrap', marginBottom:'10px'}}>
        {selectedIds.map((id: any, idx: number) => {
          let displayId: any = id;
          if (id && typeof id === 'object') {
             displayId = id.id || id.name || id.code || JSON.stringify(id);
          }
          
          // Safety: ensure displayId itself is not an object before rendering
          const finalDisplay = (displayId && typeof displayId === 'object') ? JSON.stringify(displayId) : String(displayId);
          
          return (
            <span key={idx} className="badge" style={{background: 'var(--pfb-sidebar)', color: 'white'}}>
              {finalDisplay} {!disabled && <span style={{cursor:'pointer', marginLeft:'5px'}} onClick={() => toggle(id)}>×</span>}
            </span>
          );
        })}
        {selectedIds.length === 0 && <span style={{color:'#999', fontSize:'0.8rem'}}>No items selected.</span>}
      </div>
      {!disabled && (
        <>
          <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{marginBottom:'5px'}} />
          {results.length > 0 && (
            <div style={{maxHeight:'150px', overflowY:'auto', border:'1px solid #eee'}}>
              {results.map(r => (
                <div key={r.id} onClick={() => { toggle(r.id); setSearch(''); }} style={{padding:'8px', cursor:'pointer', borderBottom:'1px solid #f9f9f9', fontSize:'0.9rem'}}>
                  <strong>{r.name}</strong> <small style={{color:'#666'}}>{r.id}</small>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const IngredientsViewWrapper = ({ userRole, onSelect, onReviewClick }: any) => {
  const [usedOnly, setUsedOnly] = useState(false);
  useEffect(() => {
    const handler = (e: any) => setUsedOnly(e.detail);
    window.addEventListener('pfb-filter-used', handler);
    return () => window.removeEventListener('pfb-filter-used', handler);
  }, []);

  return (
    <TableManager 
      title="Ingredients Master" 
      type="ingredients" 
      userRole={userRole}
      onSelect={onSelect} 
      onReviewClick={onReviewClick}
      extraParams={{ used_only: usedOnly }}
      columns={[
        {key:'id', label:'ID', width: '150px'},
        {key:'name', label:'Name'},
        {key:'unii_code', label:'FDA UNII', width: '100px', help:'FDA Unique Ingredient Identifier — the official code that pins this ingredient to one exact substance. Optional, but it prevents duplicates and mismatches.'},
        {key:'fdc_id', label:'USDA FDC', width: '100px', help:'USDA FoodData Central ID — links this ingredient to the USDA nutrition database. Optional.'},
        {key:'allergens', label:'Linked Allergens', type: 'multi-select-allergens', help:'The allergens this ingredient contains. This is what makes a product carrying it unsafe for an allergic patient — the core of the safety match.'},
        {key:'updated_at', label:'Last Updated', width: '150px', help:'When this ingredient was last changed. Click the header to sort.'},
        {key:'updated_by', label:'By', width: '140px', help:'Who last changed it — a person (👤) or an automated process (⚙️). You can also search by this.'}
      ]}
    />
  );
};

const AllergensViewWrapper = ({ userRole, onReviewClick }: any) => {
  const [scope, setScope] = useState('all');

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
       <div style={{display:'flex', justifyContent:'flex-end'}}>
          <div style={{display:'flex', alignItems:'center', gap:'10px', background:'#fff', padding:'8px 15px', borderRadius:'8px', border:'1px solid #ddd', fontSize:'0.85rem'}}>
             <strong>Focus:</strong>
             <select value={scope} onChange={e => setScope(e.target.value)} style={{border:'none', outline:'none', cursor:'pointer', fontWeight:'bold', color:'var(--pfb-sidebar)'}}>
                <option value="all">Full Catalog (Show All)</option>
                <option value="active_products">Present in active Products (SKUs)</option>
                <option value="no_active_ingredients">No active Ingredient linkage</option>
                <option value="single_ingredient">Tied to exactly 1 ingredient</option>
                <option value="multiple_ingredients">Tied to multiple (&gt;1) ingredients</option>
             </select>
          </div>
       </div>
       <TableManager 
         title="Allergen Catalog" 
         type="allergens" 
         userRole={userRole} 
         onReviewClick={onReviewClick} 
         extraParams={{ scope }}
         showExport={true}
         columns={[
           {key:'id', label:'ID', width: '150px'},
           {key:'name', label:'Name'},
           {key:'regulatory_code', label:'Code', width: '100px', help:'Optional regulatory/labeling code for this allergen (e.g. an FDA/EU major-allergen code). Leave blank if you don’t have one.'},
           {key:'priority', label:'Priority', width: '100px', help:'Clinical severity: “Major” = a serious allergen that blocks products hard; “Trace” = lower-severity. Drives how strictly it’s enforced.'},
           {key:'aliases', label:'Aliases', help:'Alternate valid spellings that mean the SAME allergen (e.g. PNC, Penicilina → Penicillin). Incoming data using an alias auto-matches this allergen — no duplicate, no re-review. Add/remove with the chips (RDN/Admin).'},
           {key:'linked_ingredients', label:'Linked Ingredients', help:'Ingredients known to contain this allergen. More links = the engine catches more products.'},
           {key:'linked_skus', label:'Linked SKUs', help:'Products currently flagged as carrying this allergen.'},
           {key:'updated_at', label:'Last Updated', width: '150px', help:'When this allergen was last changed. Click the header to sort (newest first).'},
           {key:'updated_by', label:'By', width: '140px', help:'Who last changed it — a person (👤) or an automated process (⚙️). You can also search by this name.'}
         ]}
       />
    </div>
  );
};

const TableManager = ({ title, type, columns, onSelect, onReviewClick, canCreate = true, userRole = 'User', refreshKey = 0, extraParams = {}, showExport = false }: any) => {
  const { toast, confirm, alert, prompt } = useUI();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newData, setNewData] = useState<any>({});
  const [isEditing, setIsEditing] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // ── Intake quality guardrail (allergens/ingredients only) ──
  // Live-analyze the proposed name so the RDN sees duplicates/negation/compound
  // BEFORE creating a bad node. The cheapest duplicate is the one never created.
  const GATED = type === 'allergens' || type === 'ingredients';
  const ENTITY_LABEL: any = { allergens: 'Allergen', ingredients: 'Ingredient' };
  const [nameAnalysis, setNameAnalysis] = useState<any>(null);
  const analyzeTimer = React.useRef<any>(null);
  const analyzeName = (val: string) => {
    if (!GATED) return;
    if (analyzeTimer.current) clearTimeout(analyzeTimer.current);
    if (!val || val.trim().length < 2) { setNameAnalysis(null); return; }
    analyzeTimer.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/entities/analyze`, { type: ENTITY_LABEL[type], name: val });
        setNameAnalysis(res.data);
      } catch { setNameAnalysis(null); }
    }, 400);
  };

  // SELECTION FOR CONTROLLED AI JOBS
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [runningAI, setRunningAI] = useState(false);

  const handleExport = async () => {
    try {
      const params = { ...extraParams, search, sort_by: sortBy, sort_dir: sortDir };
      const res = await axios.get(`${API_URL}/export/${type}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pfb_${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast("Export started successfully", "success");
    } catch { toast("Export failed", "error"); }
  };

  const handleEstimateRecipe = async () => {
    if (!newData.name) { toast("Please enter a product name first", "error"); return; }
    setEstimating(true);
    try {
      const res = await axios.post(`${API_URL}/ai/estimate-recipe`, { name: newData.name });
      const data = res.data;
      if (data.status === 'ERROR') throw new Error(data.message);
      
      setNewData({
        ...newData,
        calories: data.nutritional_estimates?.calories ?? newData.calories,
        carbs_g: data.nutritional_estimates?.carbs_g ?? newData.carbs_g,
        sodium_mg: data.nutritional_estimates?.sodium_mg ?? newData.sodium_mg,
        potassium_mg: data.nutritional_estimates?.potassium_mg ?? newData.potassium_mg,
        ingredients: data.estimated_ingredients ?? newData.ingredients
      });
      toast("AI Estimation complete", "success");
    } catch (err: any) { toast("AI Estimation failed: " + (err.message || "Unknown error"), "error"); }
    setEstimating(false);
  };

  const handleSurgicalAI = async () => {
    if (selectedIds.length === 0) return;
    confirm("AI Clinical Discovery", `Initiate targeted AI clinical discovery for ${selectedIds.length} selected ${type}? Results will be sent to the Review Queue.`, async () => {
        setRunningAI(true);
        try {
          await axios.post(`${API_URL}/graph/ai-repair`, {
            batch_ids: selectedIds,
            unmapped_only: false,
            ai_model: 'default'
          });
          toast("Discovery job started. Check the 'AI Graph Engineering' tab for progress.", "success");
          setSelectedIds([]);
        } catch { toast("Failed to start AI job.", "error"); }
        setRunningAI(false);
    });
  };

  const handleManualMerge = () => {
    if (selectedIds.length < 2) return;
    prompt(`Merge ${selectedIds.length} records`,
      `Type the exact ID of the record to KEEP (the canonical one). The others are absorbed into it.\n\nSelected: ${selectedIds.join(', ')}`,
      (canonicalId) => {
        if (!canonicalId) return;
        if (!selectedIds.includes(canonicalId)) {
          toast("That ID isn't in the selection. Type it exactly as shown.", "error");
          return;
        }
        const redundantIds = selectedIds.filter(id => id !== canonicalId);
        confirm("Surgical merge", `This absorbs ${redundantIds.length} record(s) into ${canonicalId}. Their SKUs and clinical identifiers move to it, and the old records are PERMANENTLY DELETED.`, async () => {
        setRunningAI(true);
        try {
          const labelMap: any = { 'ingredients': 'Ingredient', 'members': 'Member', 'conditions': 'Condition', 'allergens': 'Allergen' };
          await axios.post(`${API_URL}/graph/de-duplication/merge`, {
            canonical_id: canonicalId,
            redundant_ids: redundantIds,
            type: labelMap[type] || 'Ingredient'
          });
          toast("Surgical Merge successful. Records consolidated.", "success");
          setSelectedIds([]);
          fetch();
        } catch { toast("Merge operation failed. Check system logs.", "error"); }
        setRunningAI(false);
        });
      }, { placeholder: 'exact ID to keep', confirmText: 'Continue' });
  };

  const isClinical = ['members', 'ingredients', 'conditions', 'allergens'].includes(type);
  // Operational staff (User) manage the non-medical catalog directly: patients,
  // products, partners AND ingredients. Allergens/Conditions are medical -> they can
  // only SUGGEST those via the dedicated Intake screen (RDN approves), never here.
  const canEditOrAdd = userRole === 'Admin' || (userRole === 'RDN' && isClinical) || (userRole === 'User' && (type === 'members' || type === 'ingredients' || !isClinical));
  const canShowActions = type !== 'audit-logs';

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/${type}`, { params: { limit, offset: page * limit, search, sort_by: sortBy, sort_dir: sortDir, ...extraParams } });
      const data = res.data.items || (Array.isArray(res.data) ? res.data : []);
      setItems(data); setTotal(res.data.total || data.length);
    } catch { toast("Failed to fetch registry data", "error"); }
  }, [type, page, limit, search, sortBy, sortDir, JSON.stringify(extraParams), toast]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);

  const toggleSort = (key: string) => {
    if (['id', 'name', 'external_id', 'created_at'].includes(key)) {
      if (sortBy === key) {
        setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(key);
        setSortDir('asc');
      }
      setPage(0);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!canEditOrAdd) { toast("Insufficient permissions", "error"); return; }
    confirm("Delete Confirmation", `Are you sure you want to delete ${id}?`, async () => {
        try {
          await axios.delete(`${API_URL}/${type}/${encodeURIComponent(id)}`);
          toast(`${id} deleted successfully`, "success");
          fetch();
        } catch { toast("Delete failed", "error"); }
    });
  };

  const handleEdit = (e: React.MouseEvent, it: any) => {
    e.stopPropagation();
    if (!canEditOrAdd) { toast("Insufficient permissions", "error"); return; }
    
    // Normalize data: convert objects in arrays (like conditions) to simple IDs for the selectors
    const normalized = {...it};
    if (Array.isArray(normalized.conditions)) {
      normalized.conditions = normalized.conditions.map((c: any) => 
        (c && typeof c === 'object') ? (c.id || c.name) : c
      ).filter(Boolean);
    }
    if (Array.isArray(normalized.ingredients)) {
      normalized.ingredients = normalized.ingredients.map((ig: any) => 
        (ig && typeof ig === 'object') ? (ig.id || ig.name) : ig
      ).filter(Boolean);
    }
    if (Array.isArray(normalized.allergens) || Array.isArray(normalized.allergies)) {
      const source = normalized.allergens || normalized.allergies;
      normalized.allergens = source.map((a: any) => 
        (a && typeof a === 'object') ? (a.id || a.name) : a
      ).filter(Boolean);
      normalized.allergies = normalized.allergens; // Sync both for safety
    }
    if (Array.isArray(normalized.preferences)) {
      normalized.preferences = normalized.preferences.join(', ');
    }

    setNewData(normalized);

    setIsEditing(true);
    setShowAdd(true);
  };

  const handleSave = async (e: React.FormEvent, force = false) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      // Sanitize numeric fields: convert empty strings to null to avoid Pydantic validation errors
      const sanitized = { ...newData };
      columns.forEach((col: any) => {
        if (col.type === 'number' && (sanitized[col.key] === '' || sanitized[col.key] === undefined)) {
          sanitized[col.key] = null;
        }
      });

      const forceQ = force ? '?force=true' : '';
      const res = isEditing
        ? await axios.put(`${API_URL}/${type}/${encodeURIComponent(sanitized.id || sanitized.username)}`, sanitized)
        : await axios.post(`${API_URL}/${type}${forceQ}`, sanitized);

      const msg = res.data.message;
      if (msg) toast(msg, "success");

      setShowAdd(false);
      setIsEditing(false);
      setNewData({});
      setNameAnalysis(null);
      toast("Record saved successfully", "success");
      fetch();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      // Intake guardrail rejection (409): show the specific reason, and for a mere
      // near-duplicate offer an explicit override (genuinely-new terms like Buckwheat).
      if (err.response?.status === 409 && detail && detail.error === 'ENTITY_QUALITY') {
        setNameAnalysis(detail);
        if (detail.verdict === 'warn') {
          confirm('Similar entry already exists',
                  `${detail.suggestion || 'This looks similar to an existing entry.'} Create it anyway as a new, distinct entry?`,
                  () => handleSave(null as any, true));
          return;
        }
        toast(detail.suggestion || 'This name was rejected by the data-quality guardrail.', "error");
        return;
      }
      const message = Array.isArray(detail) ? detail.map((d:any) => d.msg).join(", ") : detail;
      toast(isEditing ? `Update failed: ${message || err.message}` : `Creation failed: ${message || "Check if ID already exists or required fields are missing."}`, "error");
    }
  };

  const renderCell = (it: any, c: any) => {
    const val = it[c.key];
    if (c.key === 'aliases') {
      // Alternate valid spellings that resolve to this same allergen (e.g. PNC -> Penicillin).
      const list = val || [];
      const canManage = userRole === 'Admin' || userRole === 'RDN';
      const addAlias = (e: any) => {
        e.stopPropagation();
        prompt(`Add an alias for “${it.name}”`,
          'Another valid spelling that means the SAME allergen (e.g. PNC, Penicilina). Incoming data using it will auto-match this allergen.',
          async (al) => {
            if (!al.trim()) return;
            try { await axios.post(`${API_URL}/allergens/${encodeURIComponent(it.id)}/aliases`, { alias: al.trim() }); fetch(); }
            catch (err: any) { toast(err.response?.data?.detail || 'Failed to add alias', 'error'); }
          }, { placeholder: 'e.g. PNC', confirmText: 'Add alias' });
      };
      const removeAlias = (e: any, al: string) => {
        e.stopPropagation();
        confirm('Remove alias?', `“${al}” will no longer auto-match ${it.name}. Incoming data using that spelling will be treated as unknown again.`,
          async () => {
            try { await axios.delete(`${API_URL}/allergens/${encodeURIComponent(it.id)}/aliases`, { params: { alias: al } }); fetch(); }
            catch { toast('Failed to remove alias', 'error'); }
          }, { danger: true, confirmText: 'Remove' });
      };
      return (
        <div className="multi-item-scroll">
          {list.map((a: string) => (
            <span key={a} className="badge" style={{ background: '#eef2ff', color: '#3b4a8f' }}>
              {a}{canManage && <span onClick={e => removeAlias(e, a)} title="Remove alias" style={{ cursor: 'pointer', marginLeft: 5, fontWeight: 700 }}>×</span>}
            </span>
          ))}
          {canManage && <span onClick={addAlias} title="Add an alternate spelling that resolves to this allergen" style={{ cursor: 'pointer', fontSize: '0.72rem', color: 'var(--pfb-sidebar)', border: '1px dashed #aab', borderRadius: 12, padding: '1px 8px' }}>＋ alias</span>}
        </div>
      );
    }
    if (c.key === 'conditions' || c.type === 'multi-select-conditions') {
      return (
        <div className="multi-item-scroll">
          {(val || []).map((con: any) => {
            const display = (con && typeof con === 'object') ? (con.name || con.id) : String(con);
            const key = (con && typeof con === 'object') ? con.id : String(con);
            return (
              <span key={key} className="badge" style={{background: 'var(--pfb-green-light)', color: 'var(--pfb-green-dark)'}}>
                {display}
              </span>
            );
          })}
        </div>
      );
    }
    if (c.key === 'allergies' || c.key === 'ingredients' || c.key === 'linked_ingredients' || c.key === 'linked_skus' || c.key === 'preferences' || c.type === 'multi-select-allergens' || c.type === 'multi-select-ingredients') {
      return (
        <div className="multi-item-scroll">
          {(val || []).map((a: any, idx: number) => {
            const display = (a && typeof a === 'object') ? (a.name || a.id) : String(a);
            return (
              <span key={idx} className="badge" style={{background: '#eee', color: '#333'}}>
                {display}
              </span>
            );
          })}
        </div>
      );
    }
    if (c.key === 'created_at' || c.key === 'timestamp' || c.key === 'updated_at') {
      if (!val || val === 'N/A') return <span style={{color:'#bbb'}}>—</span>;
      const d = new Date(val);
      return isNaN(d.getTime()) ? String(val) : <span style={{fontSize:'0.82rem'}}>{d.toLocaleString()}</span>;
    }
    if (c.key === 'updated_by') {
      if (!val) return <span style={{color:'#bbb'}}>—</span>;
      const isSystem = String(val).startsWith('system');
      return <span className="badge" style={{fontSize:'0.72rem', background: isSystem ? '#eef2f7' : '#e8f4ec', color: isSystem ? '#5a6b7a' : '#1e8449'}}>{isSystem ? '⚙️ ' : '👤 '}{val}</span>;
    }
    if (c.key === 'name') {
      return (
        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
          <span style={{fontWeight:'bold'}}>{val}</span>
          {it.ai_suggestion_pending && (
            <span 
              className="badge" 
              style={{background: 'var(--pfb-orange)', color: '#fff', fontSize: '0.6rem', padding: '1px 4px', cursor: 'pointer'}}
              title="AI has suggested clinical updates for this item. Click to review."
              onClick={(e) => { e.stopPropagation(); onReviewClick && onReviewClick(); }}
            >
              🤖 SUGGESTION
            </span>
          )}
        </div>
      );
    }
    return String(val || '');
  };

  return (
    <div className="card">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
        <div>
          <h2 style={{margin:0}}>{title}</h2>
          <span style={{fontSize:'0.8rem', color:'#999'}}>Total Registry: {total}</span>
        </div>
        <div style={{display:'flex', gap:'10px', alignItems: 'center'}}>
          <div style={{display:'flex', alignItems:'center', gap:'5px', fontSize:'0.85rem'}}>
            <span>Show:</span>
            <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(0); }} style={{padding:'4px'}}>
              {[25, 50, 100, 150, 200, 250].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <input placeholder="Search..." value={search} onChange={e=>{setSearch(e.target.value); setPage(0);}} style={{width:'200px'}} />
          
          {selectedIds.length > 0 && isClinical && canEditOrAdd && (
            <div style={{display:'flex', gap:'8px'}}>
              {selectedIds.length > 1 && (
                <button 
                  onClick={handleManualMerge} 
                  disabled={runningAI}
                  style={{background:'var(--pfb-orange)', color:'white', borderColor:'rgba(255,255,255,0.2)'}}
                >
                  🔗 Merge Selected ({selectedIds.length})
                </button>
              )}
              <button 
                onClick={handleSurgicalAI} 
                disabled={runningAI}
                style={{background:'var(--pfb-sidebar)', color:'white', borderColor:'rgba(255,255,255,0.2)'}}
              >
                {runningAI ? '⌛ Processing...' : `⚡ Analyze Selected (${selectedIds.length})`}
              </button>
            </div>
          )}

          {showExport && (
            <button className="btn-secondary" onClick={handleExport}>
              📥 Export CSV
            </button>
          )}

          {canCreate && canEditOrAdd && <button onClick={() => { setIsEditing(false); setNewData({}); setNameAnalysis(null); setShowAdd(true); }}>+ Add New</button>}
        </div>
      </div>

      {showAdd && (
        <form className="card" style={{background: '#f8f9fa', border: '1px solid var(--pfb-green)'}} onSubmit={handleSave}>
          <h3>{isEditing ? 'Edit' : 'Register New'} {title.slice(0, -1)}</h3>
          <div style={{display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
            {columns.filter((c:any) => c.key !== 'created_at' && c.key !== 'timestamp' && c.key !== 'evidence' && c.key !== 'partner_name' && c.key !== 'updated_at' && c.key !== 'updated_by' && c.key !== 'aliases').map((col: any) => (
              <div key={col.key} className="form-group" style={{gridColumn: (col.type && col.type.startsWith('multi-select')) ? 'span 2' : 'auto'}}>
                <label>{col.label}{col.help && <HelpTip text={col.help} />}</label>
                {col.type === 'select-partner' ? (
                  <PartnerSelector 
                    value={newData[col.key]} 
                    onChange={val => setNewData({...newData, [col.key]: val})} 
                  />
                ) : col.type === 'multi-select-ingredients' ? (
                  <IngredientMultiSelector 
                    selectedIds={newData[col.key] || []} 
                    onChange={ids => setNewData({...newData, [col.key]: ids})} 
                  />
                ) : col.type === 'multi-select-conditions' ? (
                  <GenericMultiSelector 
                    type="conditions"
                    selectedIds={newData[col.key] || []} 
                    onChange={ids => setNewData({...newData, [col.key]: ids})} 
                  />
                ) : col.type === 'multi-select-allergens' ? (
                  <GenericMultiSelector 
                    type="allergens"
                    selectedIds={newData[col.key] || []} 
                    onChange={ids => setNewData({...newData, [col.key]: ids})} 
                  />
                ) : col.key === 'role' ? (
                  <select value={newData[col.key] || 'User'} onChange={e => setNewData({...newData, [col.key]: e.target.value})} style={{width:'100%', padding:'10px'}}>
                    <option value="User">User</option>
                    <option value="Operations">Operations</option>
                    <option value="RDN">RDN</option>
                    <option value="Admin">Admin</option>
                    <option value="Auditor">Auditor</option>
                  </select>
                ) : col.type === 'textarea' ? (
                  <textarea 
                    placeholder={col.label} 
                    value={newData[col.key] || ''}
                    onChange={e => setNewData({...newData, [col.key]: e.target.value})}
                    style={{width:'100%', height:'80px', padding:'10px', borderRadius:'4px', border:'1px solid #ddd'}}
                  />
                ) : (
                  <div style={{display:'flex', gap:'5px'}}>
                    <input
                      placeholder={col.label}
                      required={(col.key === 'id' || col.key === 'name') && type !== 'users'}
                      disabled={isEditing && (col.key === 'id' || col.key === 'username')}
                      value={newData[col.key] || ''}
                      type={col.type === 'number' ? 'number' : col.key === 'password' ? 'password' : 'text'}
                      onChange={e => {
                        setNewData({...newData, [col.key]: e.target.value});
                        if (col.key === 'name') analyzeName(e.target.value);
                      }}
                      style={{flex: 1}}
                    />
                    {type === 'skus' && col.key === 'name' && (
                      <button
                        type="button"
                        onClick={handleEstimateRecipe}
                        disabled={estimating}
                        style={{padding: '5px 10px', minWidth: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                        title="Estimate ingredients and nutrition with AI"
                      >
                        {estimating ? '⌛' : '🪄'}
                      </button>
                    )}
                  </div>
                )}
                {GATED && col.key === 'name' && !isEditing && nameAnalysis && nameAnalysis.verdict !== 'ok' && (() => {
                  const v = nameAnalysis.verdict;
                  const palette: any = {
                    block:     { bg: '#fdecea', bd: '#e6b0aa', fg: '#c0392b', icon: '⛔' },
                    duplicate: { bg: '#fdecea', bd: '#e6b0aa', fg: '#c0392b', icon: '🚫' },
                    warn:      { bg: '#fef9e7', bd: '#f5d76e', fg: '#b7791f', icon: '⚠️' },
                  };
                  const p = palette[v] || palette.warn;
                  return (
                    <div style={{marginTop: '8px', background: p.bg, border: `1px solid ${p.bd}`, borderRadius: '8px', padding: '8px 12px', fontSize: '0.82rem', color: p.fg}}>
                      <strong>{p.icon} {nameAnalysis.suggestion || 'Check this entry.'}</strong>
                      {nameAnalysis.canonical && nameAnalysis.canonical.toLowerCase() !== (newData.name || '').toLowerCase() && v !== 'block' && (
                        <div style={{marginTop: '4px', color: '#555'}}>Will be saved as: <strong>{nameAnalysis.canonical}</strong></div>
                      )}
                      {(nameAnalysis.compound_parts || []).length > 1 && (
                        <div style={{marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'}}>
                          <span style={{color:'#555'}}>Create each separately:</span>
                          {nameAnalysis.compound_parts.map((cp: string) => (
                            <button type="button" key={cp} onClick={() => { setNewData({...newData, name: cp, id: `${ENTITY_LABEL[type] === 'Allergen' ? 'ALG' : 'ING'}_${cp.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`}); analyzeName(cp); }}
                                    style={{fontSize:'0.75rem', padding:'2px 8px', background:'#fff', color:'#333', border:`1px solid ${p.bd}`, borderRadius:'12px', cursor:'pointer'}}>{cp}</button>
                          ))}
                        </div>
                      )}
                      {(nameAnalysis.matches || []).length > 0 && v !== 'block' && (
                        <div style={{marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'}}>
                          <span style={{color:'#555'}}>Existing:</span>
                          {nameAnalysis.matches.slice(0, 4).map((m: any) => (
                            <button type="button" key={m.id} onClick={() => { setNewData({...newData, name: m.name, id: m.id}); analyzeName(m.name); }}
                                    style={{fontSize:'0.75rem', padding:'2px 8px', background:'#fff', color:'#333', border:'1px solid #ccc', borderRadius:'12px', cursor:'pointer'}} title={`Use existing ${m.name}`}>{m.name}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
          <div style={{marginTop: '20px', display: 'flex', gap: '10px'}}>
            <button type="submit">Save Record</button>
            <button type="button" className="btn-secondary" onClick={() => { setShowAdd(false); setIsEditing(false); setNewData({}); setNameAnalysis(null); }}>Cancel</button>
          </div>
        </form>
      )}

      <table>
        <thead>
          <tr>
            {isClinical && canEditOrAdd && (
              <th style={{width:'30px'}}>
                <input 
                  type="checkbox" 
                  checked={items.length > 0 && selectedIds.length === items.length} 
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(items.map(it => it.id || it.username));
                    else setSelectedIds([]);
                  }} 
                />
              </th>
            )}
            {columns.map((c:any)=>(
              <th
                key={c.key}
                onClick={() => toggleSort(c.key)}
                title={c.help || ''}
                style={{
                  cursor: ['id', 'name', 'external_id', 'created_at'].includes(c.key) ? 'pointer' : 'default',
                  width: c.width || 'auto'
                }}
              >
                {c.label}{c.help && <HelpTip text={c.help} />} {sortBy === c.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
            ))}
            {canShowActions && <th style={{textAlign: 'right', width: '100px'}}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} onClick={() => onSelect && onSelect(it)} style={{cursor: onSelect ? 'pointer' : 'default'}}>
              {isClinical && canEditOrAdd && (
                <td onClick={e => e.stopPropagation()}>
                  <input 
                    type="checkbox" 
                    checked={selectedIds.includes(it.id || it.username)} 
                    onChange={() => {
                      const id = it.id || it.username;
                      if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(x => x !== id));
                      else setSelectedIds([...selectedIds, id]);
                    }}
                  />
                </td>
              )}
              {columns.map((c:any) => (
                <td 
                  key={c.key} 
                  style={{
                    maxWidth: c.width || 'none', 
                    overflow: c.width ? 'hidden' : 'visible',
                    textOverflow: c.width ? 'ellipsis' : 'clip',
                    whiteSpace: c.width ? 'nowrap' : 'normal'
                  }}
                >
                  {renderCell(it, c)}
                </td>
              ))}
              {canShowActions && (
                <td style={{textAlign: 'right'}}>
                  <div style={{display:'flex', gap:'5px', justifyContent:'flex-end'}}>
                     <button 
                       style={{padding: '4px 8px', fontSize: '0.7rem', background: 'var(--pfb-green)', color: 'white'}} 
                       onClick={(e) => handleEdit(e, it)}
                     >
                       Edit
                     </button>
                     <button 
                       style={{padding: '4px 8px', fontSize: '0.7rem', background: 'var(--danger)'}} 
                       onClick={(e) => handleDelete(e, it.id || it.username)}
                     >
                       Del
                     </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={columns.length + (canShowActions ? 1 : 0)} style={{textAlign:'center', padding:'40px', color:'#999'}}>No results matching your criteria.</td></tr>}
        </tbody>
      </table>
      <div style={{marginTop:'20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <span style={{fontSize:'0.9rem', color:'#666'}}>
          Showing <strong>{items.length}</strong> of <strong>{total}</strong> records (Page {page+1} of {Math.ceil(total/limit) || 1})
        </span>
        <div style={{display:'flex', gap:'5px'}}>
          <button className="btn-secondary" style={{padding:'5px 15px'}} disabled={page === 0} onClick={()=>setPage(p=>p-1)}>{"<"} Prev</button>
          <button className="btn-secondary" style={{padding:'5px 15px'}} disabled={(page+1)*limit >= total} onClick={()=>setPage(p=>p+1)}>Next {">"}</button>
        </div>
      </div>
    </div>
  );
};

const MemberMasterView = ({ userRole, onSelect, onShowManual, onReviewClick }: any) => {
  const { toast, confirm } = useUI();
  const [importStatus, setImportStatus] = useState<any>(null);
  const [sfStatus, setSfStatus] = useState<any>(null);
  const [syncInterval, setSyncInterval] = useState('stopped');
  const [showImport, setShowImport] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchStatus = useCallback(() => {
    axios.get(`${API_URL}/bulk-import/members/progress`).then(res => setImportStatus(res.data));
    axios.get(`${API_URL}/snowflake/progress`).then(res => {
      setSfStatus(res.data);
      if (res.data.status === 'RUNNING') setRefreshKey(k => k + 1);
    });
    axios.get(`${API_URL}/system/settings`).then(res => {
       setSyncInterval(res.data.snowflake_sync_interval || 'stopped');
    });
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 1000); // 1.5s is better for small fast syncs
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleCsvUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/bulk-import/members`, formData);
      alert("Massive import started in background.");
    } catch { alert("Failed to start import"); }
  };

  const handleSfSync = () => {
    confirm("Sync from Snowflake?", "Pulls the latest patient list from the connected Snowflake warehouse. Runs in the background.", async () => {
      try {
        await axios.post(`${API_URL}/snowflake/sync`);
        toast("Snowflake sync started.", "success");
      } catch (err: any) { toast(err.response?.data?.detail || "Sync failed", "error"); }
    });
  };

  const handleSfSchedule = async (interval: string) => {
    try {
      await axios.post(`${API_URL}/snowflake/schedule`, { interval });
      alert(`Auto-sync updated to: ${interval}`);
      // Update local system config if needed
    } catch { alert("Failed to update schedule"); }
  };

  const clearStatus = async (type: 'snowflake' | 'csv') => {
    try {
      const path = type === 'snowflake' ? '/snowflake/progress' : '/bulk-import/members/progress';
      await axios.delete(`${API_URL}${path}`);
      if (type === 'snowflake') setSfStatus(null);
      else setImportStatus(null);
    } catch { alert("Failed to clear status"); }
  };

  const isSyncing = importStatus?.status === 'RUNNING' || sfStatus?.status === 'RUNNING';
  const showProgress = isSyncing || (sfStatus?.status === 'COMPLETED' && sfStatus.processed > 0);

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1 style={{margin:0}}>Patient Master Record</h1>
        <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
          {showProgress && (
            <div style={{width: '300px', position: 'relative'}}>
              <div style={{fontSize:'0.6rem', marginBottom:'3px', display:'flex', justifyContent:'space-between', paddingRight: '20px'}}>
                 <span>{sfStatus?.status === 'COMPLETED' ? '✨ Snowflake Sync Completed' : (sfStatus?.status === 'RUNNING' ? '❄️ Snowflake Syncing...' : '📥 CSV Importing...')}</span>
                 <span>{sfStatus?.status === 'RUNNING' || sfStatus?.status === 'COMPLETED' ? `${sfStatus.processed} items` : `${importStatus.processed}/${importStatus.total}`}</span>
              </div>
              <button 
                onClick={() => clearStatus(sfStatus?.status === 'COMPLETED' || sfStatus?.status === 'ERROR' ? 'snowflake' : 'csv')}
                style={{position:'absolute', right:'0', top:'0', background:'none', border:'none', cursor:'pointer', fontSize:'0.8rem', color:'#999', padding:'0'}}
                title="Dismiss"
              >✕</button>
              <div style={{width:'100%', height:'8px', background:'#eee', borderRadius:'4px', overflow:'hidden'}}>
                <div style={{
                  width: (sfStatus?.status === 'RUNNING' || sfStatus?.status === 'COMPLETED') ? '100%' : `${(importStatus.processed / importStatus.total) * 100}%`,
                  height:'100%',
                  background: sfStatus?.status === 'COMPLETED' ? 'var(--pfb-green)' : '#2196f3',
                  transition:'width 0.3s ease',
                  animation: sfStatus?.status === 'RUNNING' ? 'progress-loading 2s infinite linear' : 'none'
                }}></div>
              </div>
            </div>
          )}
          
          {(importStatus?.status === 'ERROR' || sfStatus?.status === 'ERROR') && (
            <div style={{fontSize:'0.7rem', background:'#ffebee', color:'#c62828', padding:'5px 12px', borderRadius:'15px', border:'1px solid #ef9a9a', display:'flex', alignItems:'center', gap:'5px'}} title={sfStatus?.last_item || importStatus?.last_item}>
              ⚠️ Sync Error: {sfStatus?.status === 'ERROR' ? 'Snowflake Rejected' : 'CSV Failed'}
              <span style={{cursor:'pointer', fontWeight:'bold', marginLeft:'5px'}} onClick={() => clearStatus(sfStatus?.status === 'ERROR' ? 'snowflake' : 'csv')}>✕</span>
            </div>
          )}

          {userRole === 'Admin' && (
            <>
              <div style={{display:'flex', alignItems:'center', gap:'5px', marginRight:'10px', background:'#f0f7ff', padding:'2px 10px', borderRadius:'20px', border:'1px solid #d0e3ff'}}>
                <label style={{fontSize:'0.65rem', fontWeight:'bold', color:'#0056b3', textTransform:'uppercase'}}>Auto-Sync:</label>
                <select 
                  value={syncInterval}
                  onChange={(e) => {
                    handleSfSchedule(e.target.value);
                    setSyncInterval(e.target.value);
                  }}
                  style={{fontSize:'0.75rem', background:'transparent', border:'none', color:'#0056b3', fontWeight:'bold', cursor:'pointer'}}
                >
                  <option value="stopped">Stopped</option>
                  <option value="10m">Every 10 min</option>
                  <option value="1h">Once an hour</option>
                  <option value="6h">Once every 6h</option>
                  <option value="1d">Once a day</option>
                </select>
              </div>
              <button className="btn-secondary" onClick={() => onShowManual('ingestion')} style={{background:'#fff3cd', color:'#856404', borderColor:'#ffeeba', marginRight:'5px'}}>❓ Help</button>
              <button className="btn-secondary" onClick={handleSfSync} disabled={sfStatus?.status === 'RUNNING'} title="Pull the latest patient list from the connected Snowflake data warehouse. Runs in the background.">
                {sfStatus?.status === 'RUNNING' ? '⌛ Syncing...' : '❄️ Trigger Manual Snowflake Sync'}
              </button>
              <button className="btn-secondary" onClick={() => setShowImport(!showImport)} title="Load many patients at once from a CSV file (columns: id, name, allergies, conditions, preferences).">
                {showImport ? 'Close Importer' : '📥 Mass CSV Import'}
              </button>
            </>
          )}
        </div>
      </div>

      {showImport && (
        <div className="card" style={{border:'2px dashed var(--pfb-green)', background:'#f1f8f6'}}>
          <h3>Massive Member Ingest (Up to 1M records)</h3>
          <p style={{fontSize:'0.8rem', color:'#666'}}>Please upload a CSV with 'ID' and 'Name' columns. The system will process this in the background.</p>
          <input type="file" accept=".csv" onChange={e => e.target.files?.[0] && handleCsvUpload(e.target.files[0])} />
        </div>
      )}

      <TableManager 
        title="Active Patients" 
        type="members" 
        userRole={userRole}
        onSelect={onSelect}
        onReviewClick={onReviewClick}
        refreshKey={refreshKey}
        columns={[
          {key:'id', label:'ID', width: '120px'}, 
          {key:'name', label:'Name'}, 
          {key:'external_id', label:'External ID', width: '120px'}, 
          {key:'preferences', label:'Food Preferences (to avoid)', type: 'textarea'},
          {key:'allergies', label:'Allergies (Clinical)', type: 'multi-select-allergens'}, 
          {key:'conditions', label:'Conditions (ICD-10)', type: 'multi-select-conditions'}, 
          {key:'created_at', label:'Date'}
        ]} 
      />
    </div>
  );
};

const SkuMasterView = ({ userRole, handleExport, onReviewClick }: any) => {
  const { toast, confirm } = useUI();
  const [showImport, setShowImport] = useState(false);
  const [status, setStatus] = useState<any>(null);

  const fetchStatus = useCallback(() => {
    axios.get(`${API_URL}/ai/sku-progress`).then(res => {
      setStatus(res.data);
      if (res.data.status === 'RUNNING') setTimeout(fetchStatus, 3000);
    });
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleBulkEnrich = () => {
    confirm("Estimate all empty products?", "Uses AI to estimate ingredients and nutrition for ALL products that are currently empty. Runs in the background.", async () => {
      try {
        await axios.post(`${API_URL}/ai/enrich-skus`);
        toast("Product enrichment started in the background.", "success");
        fetchStatus();
      } catch { toast("Failed to start enrichment", "error"); }
    });
  };

  const isRunning = status?.status === 'RUNNING';

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
       <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <h1 style={{margin:0}}>Products Master Control</h1>
          <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
            {status && (status.status === 'RUNNING' || status.status === 'COMPLETED') && (
              <div style={{fontSize:'0.7rem', background:'#f0f0f0', padding:'5px 10px', borderRadius:'15px', border:'1px solid #ddd'}}>
                {isRunning ? `🪄 AI Enriching: ${status.processed}/${status.total} (${status.last_item})` : `✨ AI Last Run: ${status.processed} processed`}
              </div>
            )}
            
            {(userRole === 'Admin' || userRole === 'User') && (
              <button 
                className="btn-secondary" 
                onClick={handleBulkEnrich}
                disabled={isRunning}
                style={{borderColor: 'var(--pfb-green)', color: 'var(--pfb-green-dark)'}}
              >
                {isRunning ? '⌛ AI Processing...' : '🪄 Bulk AI Enrich'}
              </button>
            )}

            {(userRole === 'Admin' || userRole === 'User') && (
              <button className="btn-secondary" onClick={() => setShowImport(!showImport)}>
                {showImport ? 'Close Import Tool' : '📦 Open Import Tool'}
              </button>
            )}
          </div>
       </div>

       {showImport && <CatalogUploader onComplete={() => { setShowImport(false); window.location.reload(); }} />}

       <TableManager 
         title="Products (SKU Master)" 
         type="skus" 
         userRole={userRole}
         onReviewClick={onReviewClick}
         showExport={true}
         columns={[
           {key:'id', label:'ID', width: '100px'}, 
           {key:'name', label:'Name'}, 
           {key:'partner_id', label:'Partner', type: 'select-partner', help:'The supplier that provides this product. Optional, but useful for tracing a product back to its source.'},
           {key:'calories', label:'Calories', type: 'number', help:'Calories per serving of this product. Matched against each patient’s condition limits (e.g. Max Cal /Meal).'},
           {key:'protein_g', label:'Protein (g)', type: 'number', help:'Protein per serving (grams). Matched against condition protein targets.'},
           {key:'fat_g', label:'Fat (g)', type: 'number', help:'Total fat per serving (grams). Matched against condition fat limits.'},
           {key:'carbs_g', label:'Carbs (g)', type: 'number', help:'Carbohydrates per serving (grams). Key for diabetic patients — matched against Max Carbs /Meal.'},
           {key:'fiber_g', label:'Fiber (g)', type: 'number', help:'Dietary fiber per serving (grams).'},
           {key:'sugar_g', label:'Sugar (g)', type: 'number', help:'Sugar per serving (grams). Matched against condition sugar limits.'},
           {key:'sodium_mg', label:'Sodium (mg)', type: 'number', help:'Sodium (salt) per serving in milligrams. Key for kidney/heart patients — matched against Max Na /Meal.'},
           {key:'potassium_mg', label:'Potassium (mg)', type: 'number', help:'Potassium per serving (mg). Key for kidney (CKD) patients.'},
           {key:'cholesterol_mg', label:'Cholest. (mg)', type: 'number', help:'Cholesterol per serving (mg).'},
           {key:'iron_mg', label:'Iron (mg)', type: 'number', help:'Iron per serving (mg).'},
           {key:'calcium_mg', label:'Calcium (mg)', type: 'number', help:'Calcium per serving (mg).'},
           {key:'ingredients', label:'Ingredients', type: 'multi-select-ingredients', help:'What this product is made of. Each ingredient carries its allergens — this is how the engine knows a product is unsafe for an allergic patient. Use the 🪄 button on Name to auto-fill with AI.'},
           {key:'updated_at', label:'Last Updated', width: '150px'},
           {key:'updated_by', label:'By', width: '140px'}
         ]} 
       />
    </div>
  );
};

const SyncDashboard = ({ onEnterReview, handleExport }: { onEnterReview: () => void, handleExport: (type: string) => void }) => {
  const [status, setStatus] = useState<any[]>([]);
  const [syncing, setSyncing] = useState<string | null>(null);

  const fetchStatus = useCallback(() => {
    axios.get(`${API_URL}/sync/status`).then(res => setStatus(res.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const hasPending = status.some(s => s.pending_updates > 0);

  const triggerSync = async (source: string) => {
    setSyncing(source);
    try {
      const res = await axios.post(`${API_URL}/sync/trigger`, { source });
      alert(res.data.message);
      fetchStatus();
    } catch { alert("Sync trigger failed"); }
    setSyncing(null);
  };

  const handleBulkImport = async (type: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/bulk-import/${type}`, formData);
      alert(`${type} imported successfully`);
      fetchStatus();
    } catch { alert("Import failed"); }
  };

  return (
    <div className="stats-grid">
      <div className="card" style={{gridColumn: 'span 2'}}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
          <h2 style={{margin:0}}>External Data Synchronization</h2>
          <div style={{display:'flex', gap:'10px'}}>
            {hasPending && <button onClick={onEnterReview} style={{background: 'var(--danger)'}}>⚠️ Review Pending Updates</button>}
            <button onClick={fetchStatus} className="btn-secondary">🔄 Refresh Status Now</button>
          </div>
        </div>
        <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '25px'}}>
          SafeGuard connects to federal and clinical registries to identify updates. 
          <strong> "Pending"</strong> indicates new external records that have been identified but not yet merged into our clinical graph.
        </p>
        <div className="stats-grid" style={{gridTemplateColumns: '1fr 1fr'}}>
          {status.map(s => (
            <div key={s.source} className="card" style={{borderLeft: '5px solid var(--pfb-green)', marginBottom:0, background: '#fff'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                <div style={{flex: 1}}>
                  <h3 style={{margin:0}}>{s.source}</h3>
                  <a href={s.external_url} target="_blank" rel="noreferrer" style={{fontSize: '0.7rem', color: 'var(--pfb-green-dark)'}}>Official Source Registry ↗</a>
                  <p style={{fontSize: '0.8rem', color: '#666', marginTop: '10px'}}>{s.description}</p>
                </div>
              </div>
              
              <div style={{display: 'flex', gap: '15px', margin: '20px 0', padding: '15px', background: '#f8f9fa', borderRadius: '8px'}}>
                <div style={{textAlign: 'center', flex: 1}}>
                  <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--pfb-sidebar)'}}>{s.db_count}</div>
                  <div style={{fontSize: '0.65rem', color: '#999', textTransform: 'uppercase'}}>In our DB</div>
                </div>
                <div style={{textAlign: 'center', flex: 1, borderLeft: '1px solid #ddd'}}>
                  <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: s.pending_updates > 0 ? 'var(--danger)' : 'var(--pfb-green)'}}>{s.pending_updates}</div>
                  <div style={{fontSize: '0.65rem', color: '#999', textTransform: 'uppercase'}}>Pending Review</div>
                </div>
              </div>

              <div style={{fontSize: '0.75rem', color: '#999', marginBottom: '10px'}}>Last Successful Contact: {s.last_sync}</div>
              <button 
                style={{width: '100%'}} 
                disabled={syncing === s.source}
                onClick={() => triggerSync(s.source)}
              >
                {syncing === s.source ? '⌛ Contacting API...' : `Identify Deltas in ${s.source.split(' ')[0]}`}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Master Data Management</h2>
        
        <div style={{marginBottom: '30px'}}>
          <h3 style={{fontSize: '0.9rem', color: 'var(--pfb-green-dark)'}}>📤 Export for Clinical Audit</h3>
          <p style={{fontSize: '0.75rem', color: '#666'}}>Download current masters for manual review or secondary backup.</p>
          <div style={{display:'flex', gap:'5px', flexWrap: 'wrap', marginTop: '10px'}}>
            <button onClick={() => handleExport('ingredients')} style={{fontSize: '0.7rem', padding: '8px 12px'}}>Ingredients</button>
            <button onClick={() => handleExport('allergens')} style={{fontSize: '0.7rem', padding: '8px 12px'}}>Allergens</button>
            <button onClick={() => handleExport('conditions')} style={{fontSize: '0.7rem', padding: '8px 12px'}}>Conditions</button>
            <button onClick={() => handleExport('skus')} style={{fontSize: '0.7rem', padding: '8px 12px'}}>Products (SKU)</button>
          </div>
        </div>

        <div>
          <h3 style={{fontSize: '0.9rem', color: 'var(--pfb-green-dark)'}}>📥 Manual Bulk Ingest</h3>
          <p style={{fontSize: '0.75rem', color: '#666', marginBottom: '15px'}}>Upload CSV to override or append master data.</p>
          
          {['Ingredients', 'Allergens', 'Conditions', 'Skus'].map(type => (
            <div key={type} style={{marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
              <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '5px'}}>{type} CSV File</label>
              <input 
                type="file" 
                style={{fontSize: '0.7rem'}} 
                onChange={(e) => e.target.files?.[0] && handleBulkImport(type.toLowerCase(), e.target.files[0])}
              />
            </div>
          ))}
        </div>
        
        <div className="account-info" style={{marginTop: '20px', fontSize: '0.7rem', lineHeight: '1.4'}}>
          <strong>Clinical Safety Note:</strong> Manual ingests bypass AI semantic validation. Ensure UNII and ICD-10 codes are verified against FDA/CMS masters before uploading.
        </div>
      </div>
    </div>
  );
};

const SyncReview = ({ onBack }: { onBack: () => void }) => {
  const { toast, confirm } = useUI();
  const [pending, setPending] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewStatus, setViewStatus] = useState<'PENDING_REVIEW' | 'REJECTED'>('PENDING_REVIEW');
  const [orphans, setOrphans] = useState<any>(null);
  const [showOrphans, setShowOrphans] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>(null);
  const [aiProgress, setAiProgress] = useState<any>(null);
  const [algSearch, setAlgSearch] = useState('');
  const [algResults, setAlgResults] = useState<any[]>([]);
  const fetchPending = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/sync/pending`, { params: { limit, offset: page * limit, search, type: filterType, status: viewStatus } }).then(res => {
      setPending(res.data.items || []);
      setTotal(res.data.total || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page, limit, search, filterType, viewStatus]);

  useEffect(() => {
    axios.get(`${API_URL}/ingredients/orphans`, { params: { limit: 300 } })
      .then(res => setOrphans(res.data)).catch(() => setOrphans(null));
  }, []);

  const fetchProgress = useCallback(() => {
    axios.get(`${API_URL}/sync/progress`).then(res => {
      setAiProgress(res.data);
      if (res.data.status === 'RUNNING') {
        setTimeout(fetchProgress, 2000);
      } else if (res.data.status === 'COMPLETED') {
        fetchPending();
      }
    });
  }, [fetchPending]);

  useEffect(() => { fetchPending(); }, [fetchPending]);
  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  useEffect(() => {
    if (algSearch.length > 1) {
      axios.get(`${API_URL}/allergens`, { params: { search: algSearch, limit: 5 } }).then(res => setAlgResults(res.data.items || []));
    } else setAlgResults([]);
  }, [algSearch]);
  const triggerAI = async (mode: string) => {
    try {
      await axios.post(`${API_URL}/sync/analyze`, { mode, ids: mode === 'MARKED' ? selectedIds : [] });
      fetchProgress();
    } catch (err: any) {
      alert(err.response?.data?.detail || "AI Trigger failed");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.post(`${API_URL}/sync/update`, { 
        id: editingItem.id, 
        local_id: editForm.name, 
        ai_suggestions: editForm.suggestions 
      });
      setEditingItem(null);
      fetchPending();
    } catch { alert("Update failed"); }
  };

  const handleApproveEdit = async () => {
    try {
      // Save current changes first
      await axios.post(`${API_URL}/sync/update`, { 
        id: editingItem.id, 
        local_id: editForm.name, 
        ai_suggestions: editForm.suggestions 
      });
      // Then approve
      await axios.post(`${API_URL}/sync/action`, { ids: [editingItem.id], action: 'APPROVE' });
      setEditingItem(null);
      fetchPending();
      alert("Clinical identity approved and moved to graph.");
    } catch { alert("Final approval failed."); }
  };

  const handleAction = async (ids: number[], action: string) => {

    if (ids.length === 0) return;
    try {
      await axios.post(`${API_URL}/sync/action`, { ids, action });
      setSelectedIds([]);
      fetchPending();
    } catch { alert("Action failed"); }
  };

  const handleClearAll = () => {
    const count = total;
    confirm("Clear the whole queue?", `This permanently removes ALL ${count} item(s) from this queue. It cannot be undone.`, async () => {
      try {
        await axios.post(`${API_URL}/sync/action`, { action: 'CLEAR_ALL', status: viewStatus });
        fetchPending();
      } catch { toast("Clear ALL failed", "error"); }
    }, { danger: true, confirmText: 'Clear all' });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === pending.length) setSelectedIds([]);
    else setSelectedIds(pending.map(p => p.id));
  };

  const toggleSelectOne = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const renderSuggestions = (sugg: any, type: string) => {
    if (!sugg) return <em style={{color:'#999'}}>No AI analysis</em>;
    if (sugg.status === 'ERROR') return <div style={{color:'var(--danger)', fontSize:'0.75rem'}}><strong>AI Error:</strong> {sugg.message}</div>;

    if (type === 'AllergenAlias' || sugg.kind === 'alias_suggestion') {
      return (
        <div style={{fontSize:'0.8rem'}}>
          <span className="badge" style={{background:'#eef2ff', color:'#3b4a8f'}}>alias</span>
          {' '}Treat <strong>“{sugg.alias}”</strong> as another spelling of <strong>“{sugg.target_name}”</strong>.
          <div style={{color:'#888', fontSize:'0.72rem', marginTop:2}}>Approving adds it as an alias; incoming data using “{sugg.alias}” will then auto-match “{sugg.target_name}”. Source: {sugg.source || 'AI auto-discovery'}</div>
        </div>
      );
    }

    if (type === 'Condition') {      const macros = sugg.macronutrients || {};
      const electros = sugg.electrolytes_mg || {};
      const restr = sugg.clinical_restrictions || {};
      
      return (
        <div style={{fontSize: '0.75rem', lineHeight: '1.4'}}>
          <div style={{display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:'5px'}}>
            {electros.max_sodium && <span className="badge" style={{background:'#e3f2fd', color:'#1976d2'}}>Na: {electros.max_sodium}mg</span>}
            {electros.max_potassium && <span className="badge" style={{background:'#f3e5f5', color:'#7b1fa2'}}>K: {electros.max_potassium}mg</span>}
            {electros.max_phosphorus && <span className="badge" style={{background:'#ffebee', color:'#c62828'}}>P: {electros.max_phosphorus}mg</span>}
            {macros.max_carbs_g && <span className="badge" style={{background:'#fff3e0', color:'#e65100'}}>CHO: {macros.max_carbs_g}g</span>}
          </div>
          <div style={{fontSize: '0.7rem', color: '#666', marginBottom: '3px'}}>
            Restrictions: <strong>Gluten: {restr.gluten}</strong> | <strong>Lactose: {restr.lactose}</strong> | <strong>Fiber: {restr.fiber}</strong>
          </div>
          {sugg.forbidden_allergens?.length > 0 && (
            <div style={{color:'var(--danger)', fontWeight:'bold'}}>Blocks: {sugg.forbidden_allergens.join(', ')}</div>
          )}
          {sugg.medical_considerations && (
            <div style={{background: '#fff3e0', borderLeft: '3px solid var(--pfb-orange)', padding: '5px', marginTop: '5px', fontSize: '0.7rem'}}>
              <strong>Medical Considerations:</strong> {sugg.medical_considerations}
            </div>
          )}
          {sugg.clinical_rationale && <div style={{fontStyle:'italic', color:'#666', marginTop:'3px'}}>{sugg.clinical_rationale}</div>}
        </div>
      );
    }

    const allergens = sugg.allergens || [];
    const gradeColor = sugg.safety_grade === 'A' ? '#4da64d' : (sugg.safety_grade === 'F' ? 'var(--danger)' : '#ffa000');
    
    return (
      <div style={{fontSize: '0.75rem'}}>
        <div style={{marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '8px'}}>
          <span style={{background: gradeColor, color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold'}}>Grade {sugg.safety_grade || 'U'}</span>
          <span style={{color: '#666', fontSize: '0.7rem'}}>UNII: {sugg.unii_code}</span>
        </div>
        {allergens.length > 0 ? (
          <div style={{display:'flex', flexWrap:'wrap', gap:'3px'}}>
            {allergens.map((a:any, i:number) => (
              <span key={i} className={`badge ${a.is_new ? 'yellow' : 'red'}`} style={{padding:'2px 5px'}}>
                {a.is_new ? `NEW: ${a.name}` : a.name} {a.severity_level === 'Major' && '⚠️'}
              </span>
            ))}
          </div>
        ) : <span style={{color: 'var(--pfb-green-dark)', fontWeight: 'bold'}}>Medical Grade: Safe</span>}
        {sugg.clinical_justification && <div style={{marginTop: '3px', fontStyle:'italic', color:'#666'}}>{sugg.clinical_justification}</div>}
      </div>
    );
  };

  return (
    <div className="card">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
        <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
          <button className="btn-secondary" onClick={onBack}>← Back</button>
          <h2 style={{margin:0}}>{viewStatus === 'PENDING_REVIEW' ? 'Clinical Data Review Queue' : 'Rejected Items Archive'}</h2>
        </div>
        <div style={{display:'flex', gap:'10px'}}>
           <div className="btn-group" style={{marginRight: '10px'}}>
              <button 
                className={viewStatus === 'PENDING_REVIEW' ? 'active' : ''} 
                onClick={() => {setViewStatus('PENDING_REVIEW'); setPage(0);}} 
                style={{
                  padding: '5px 15px', 
                  borderRadius: '4px 0 0 4px', 
                  border: '1px solid var(--pfb-sidebar)', 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  background: viewStatus === 'PENDING_REVIEW' ? 'var(--pfb-sidebar)' : '#fff',
                  color: viewStatus === 'PENDING_REVIEW' ? '#fff' : 'var(--pfb-sidebar)'
                }}>
                Pending
              </button>
              <button 
                className={viewStatus === 'REJECTED' ? 'active' : ''} 
                onClick={() => {setViewStatus('REJECTED'); setPage(0);}} 
                style={{
                  padding: '5px 15px', 
                  borderRadius: '0 4px 4px 0', 
                  border: '1px solid var(--pfb-sidebar)', 
                  borderLeft: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  background: viewStatus === 'REJECTED' ? 'var(--pfb-sidebar)' : '#fff',
                  color: viewStatus === 'REJECTED' ? '#fff' : 'var(--pfb-sidebar)'
                }}>
                Rejected
              </button>
           </div>
           <select value={filterType} onChange={e => {setFilterType(e.target.value); setPage(0);}} style={{padding:'5px'}}>
              <option value="">All Types</option>
              <option value="Ingredient">Ingredients</option>
              <option value="Condition">Conditions</option>
              <option value="Allergen">Allergens</option>
           </select>
           <input placeholder="Search queue..." value={search} onChange={e => {setSearch(e.target.value); setPage(0);}} style={{width:'200px'}} />
        </div>
      </div>

      {viewStatus === 'PENDING_REVIEW' && (
        <div className="card" style={{marginBottom: '20px', border: '1px solid var(--pfb-orange)', background: '#fff9f4'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div>
              <h4 style={{margin:0, color:'var(--pfb-orange)'}}>🤖 Clinical AI Enrichment Phase</h4>
              <p style={{fontSize:'0.75rem', margin: '5px 0 0'}}>Trigger medical-grade analysis for items in the queue before final RDN approval.</p>
            </div>
            <div style={{display:'flex', gap:'8px'}}>
              <button className="btn-secondary" style={{fontSize:'0.75rem'}} onClick={() => triggerAI('ALL')} disabled={aiProgress?.status === 'RUNNING'}>Re-Analyze ALL</button>
              <button className="btn-secondary" style={{fontSize:'0.75rem'}} onClick={() => triggerAI('NEW')} disabled={aiProgress?.status === 'RUNNING'}>Analyze Only NEW</button>
              <button className="btn-secondary" style={{fontSize:'0.75rem'}} onClick={() => triggerAI('MARKED')} disabled={aiProgress?.status === 'RUNNING' || selectedIds.length === 0}>Analyze Selected ({selectedIds.length})</button>
            </div>
          </div>
          
          {aiProgress && aiProgress.status !== 'IDLE' && (
            <div style={{marginTop: '15px'}}>
              <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.75rem', marginBottom:'5px'}}>
                <span>Status: <strong>{aiProgress.status}</strong> - Processing: <em>{aiProgress.last_item}</em></span>
                <span>{aiProgress.processed} / {aiProgress.total}</span>
              </div>
              <div style={{width:'100%', height:'8px', background:'#eee', borderRadius:'4px', overflow:'hidden'}}>
                <div style={{
                  width: `${(aiProgress.processed / (aiProgress.total || 1)) * 100}%`, 
                  height:'100%', 
                  background:'var(--pfb-orange)',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <strong>{selectedIds.length}</strong> items selected of <strong>{total}</strong> {viewStatus === 'PENDING_REVIEW' ? 'pending' : 'rejected'} records.
        </div>
        <div style={{display:'flex', gap:'10px'}}>
           {viewStatus === 'PENDING_REVIEW' ? (
             <>
               <button disabled={selectedIds.length === 0} onClick={() => handleAction(selectedIds, 'APPROVE')} style={{background:'var(--pfb-sidebar)'}}>Batch Approve</button>
               <button disabled={selectedIds.length === 0} onClick={() => handleAction(selectedIds, 'REJECT')} style={{background:'var(--danger)'}}>Batch Reject</button>
               <button disabled={selectedIds.length === 0} onClick={() => confirm('Clear selected items?', `Remove ${selectedIds.length} selected item(s) from the queue?`, () => handleAction(selectedIds, 'CLEAR'), { confirmText: 'Clear' })} style={{background:'#666'}}>Batch Clear</button>
               <button onClick={handleClearAll} style={{background:'var(--danger)'}}>Clear ALL</button>
             </>
           ) : (
             <>
               <button disabled={selectedIds.length === 0} onClick={() => handleAction(selectedIds, 'RESTORE')} style={{background:'var(--pfb-sidebar)'}}>Batch Restore to Queue</button>
               <button onClick={handleClearAll} style={{background:'var(--danger)'}}>Empty Archive</button>
             </>
           )}
        </div>

      </div>
      
      {loading ? <p>Loading queue...</p> : (
        <>
          <table className="review-table">
            <thead>
              <tr>
                <th style={{width:'30px'}}><input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === pending.length} onChange={toggleSelectAll} /></th>
                <th>Source</th>
                <th>ID</th>
                <th>Proposed Name</th>
                <th>Type</th>
                {viewStatus === 'PENDING_REVIEW' ? <th>Created</th> : (
                  <>
                    <th>Rejected At</th>
                    <th>By Whom</th>
                  </>
                )}
                <th>AI Suggestions (Allergens)</th>
                <th style={{textAlign:'right'}}>Individual Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.map(p => (
                <tr key={p.id}>
                  <td><input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelectOne(p.id)} /></td>
                  <td><span className="badge" style={{background:'#eee', color:'#666'}}>{p.source.split(' ')[0]}</span></td>
                  <td style={{maxWidth: '200px', wordBreak: 'break-all', fontSize: '0.7rem'}}><code>{p.ext_id}</code></td>
                  <td style={{maxWidth: '250px', overflowWrap: 'break-word'}}>
                    <strong>{p.name}</strong>
                    {p.quality && (() => {
                      const v = p.quality.verdict;
                      const st: any = v === 'warn'
                        ? { bg: '#fef9e7', fg: '#b7791f', icon: '⚠️' }
                        : { bg: '#fdecea', fg: '#c0392b', icon: v === 'duplicate' ? '🚫' : '⛔' };
                      return (
                        <div title={p.quality.suggestion || ''} style={{marginTop: '4px', fontSize: '0.68rem', color: st.fg, background: st.bg, borderRadius: '6px', padding: '3px 6px', display: 'inline-block'}}>
                          {st.icon} {v === 'duplicate' ? `Duplicate of “${p.quality.match}”` : v === 'block' ? (p.quality.issues || []).join(', ') : `Similar to “${p.quality.match}”`}
                        </div>
                      );
                    })()}
                  </td>
                  <td><span style={{fontSize:'0.8rem', fontWeight:'bold'}}>{p.type}</span></td>
                  {viewStatus === 'PENDING_REVIEW' ? (
                    <td style={{fontSize:'0.7rem'}}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                  ) : (
                    <>
                      <td style={{fontSize:'0.7rem'}}>{p.action_at ? new Date(p.action_at).toLocaleString() : '-'}</td>
                      <td style={{fontSize:'0.7rem'}}><strong>{p.action_by || 'system'}</strong></td>
                    </>
                  )}
                  <td style={{minWidth: '250px'}}>{renderSuggestions(p.ai_suggestions, p.type)}</td>
                  <td style={{textAlign:'right'}}>
                    <div style={{display:'flex', gap:'5px', justifyContent:'flex-end'}}>
                      {viewStatus === 'PENDING_REVIEW' ? (
                        <>
                          <button style={{padding:'4px 8px', fontSize:'0.7rem', background: 'var(--pfb-sidebar)'}} title="Adjust the name or AI-suggested details before approving." onClick={() => { setEditingItem(p); setEditForm({name: p.name, suggestions: p.ai_suggestions, type: p.type}); }}>Edit</button>
                          <button style={{padding:'4px 8px', fontSize:'0.7rem'}} title="Accept this item into the live graph — from now on it affects safety decisions. Check the quality flag first." onClick={() => handleAction([p.id], 'APPROVE')}>Approve</button>
                          <button style={{padding:'4px 8px', fontSize:'0.7rem', background: 'var(--danger)'}} title="Discard this item — it will NOT enter the graph. Use for duplicates, junk, or wrong entries." onClick={() => handleAction([p.id], 'REJECT')}>Reject</button>
                        </>
                      ) : (
                        <button style={{padding:'4px 8px', fontSize:'0.7rem'}} onClick={() => handleAction([p.id], 'RESTORE')}>Restore</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {pending.length === 0 && <tr><td colSpan={7} style={{textAlign:'center', padding:'40px', color:'#999'}}>No {viewStatus === 'PENDING_REVIEW' ? 'pending' : 'rejected'} updates found for your filters.</td></tr>}
            </tbody>
          </table>

          <div style={{marginTop:'20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
               <span style={{fontSize:'0.8rem', color:'#666'}}>Showing page {page+1} of {Math.ceil(total/limit) || 1} ({total} total)</span>
               <div style={{display:'flex', alignItems:'center', gap:'5px', fontSize:'0.8rem', borderLeft:'1px solid #ddd', paddingLeft:'15px'}}>
                 <label>Show:</label>
                 <select 
                   value={limit} 
                   onChange={e => {setLimit(parseInt(e.target.value)); setPage(0);}}
                   style={{padding:'2px 5px', fontSize:'0.75rem'}}
                 >
                   <option value={50}>50</option>
                   <option value={100}>100</option>
                   <option value={150}>150</option>
                   <option value={200}>200</option>
                 </select>
               </div>
            </div>
            <div style={{display:'flex', gap:'5px'}}>
               <button disabled={page === 0} onClick={()=>setPage(p=>p-1)} className="btn-secondary">Prev</button>
               <button disabled={(page+1)*limit >= total} onClick={()=>setPage(p=>p+1)} className="btn-secondary">Next</button>
            </div>
          </div>
        </>
      )}

      {orphans && orphans.count > 0 && (
        <div className="card" style={{background:'#fff8e6', border:'1px solid #e0a800', marginBottom:'16px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div>
              <strong style={{color:'#856404'}}>
                {orphans.count} approved ingredient(s) are not linked to any allergen
              </strong>
              <div style={{fontSize:'0.85rem', color:'#856404', marginTop:'3px'}}>
                They block nothing. If a patient note names one, it keeps being reported as an unknown term.
                {orphans.in_use_count > 0 && <> <strong>{orphans.in_use_count}</strong> of them are used by real products.</>}
              </div>
            </div>
            <button className="btn-secondary" onClick={()=>setShowOrphans(!showOrphans)}>
              {showOrphans ? 'Hide' : 'Review them'}
            </button>
          </div>
          {showOrphans && (
            <div style={{marginTop:'12px'}}>
              <div style={{maxHeight:'220px', overflowY:'auto', background:'#fff', borderRadius:'6px', padding:'8px'}}>
                {orphans.items.map((o: any) => (
                  <div key={o.id} style={{display:'flex', justifyContent:'space-between', padding:'4px 6px', borderBottom:'1px solid #f0f0f0'}}>
                    <span>{o.name}</span>
                    <small style={{color: o.sku_count > 0 ? '#c0392b' : '#999'}}>
                      {o.sku_count > 0 ? `used by ${o.sku_count} product(s)` : 'not used by any product'}
                    </small>
                  </div>
                ))}
              </div>
              <button style={{marginTop:'10px'}} onClick={() => confirm("Analyze unlinked ingredients?",
                "Runs AI analysis on all ingredients with no allergen link; they come back into this queue with suggested allergens for your review.",
                async () => {
                  try {
                    await axios.post(`${API_URL}/sync/analyze`, { mode: 'ORPHANS' });
                    toast("Analysis started. The items will appear in this queue shortly.", "success");
                  } catch { toast("Failed to start analysis.", "error"); }
                })}>Analyze with AI and send to this queue</button>
            </div>
          )}
        </div>
      )}

      {editingItem && (
        <div style={{position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000}}>
          <div style={{background:'#fff', padding:'30px', borderRadius:'12px', width:'600px', maxHeight:'90vh', overflowY:'auto'}}>
            <h3>Edit Clinical Profile: {editingItem.name}</h3>
            <p style={{fontSize:'0.8rem', color:'#666'}}>Adjust AI suggested thresholds and mappings before final approval.</p>
            
            <div style={{display:'flex', flexDirection:'column', gap:'15px', marginTop:'20px'}}>
              <label>
                <strong>Displayed Name:</strong>
                <input style={{width:'100%', padding:'8px', marginTop:'5px'}} value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
              </label>

              {editingItem.type === 'Condition' && editForm.suggestions && (
                <>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                    <label>
                      <strong>Max Sodium (mg):</strong>
                      <input type="number" style={{width:'100%', padding:'8px'}} value={editForm.suggestions.electrolytes_mg?.max_sodium || ''} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, electrolytes_mg: {...editForm.suggestions.electrolytes_mg, max_sodium: parseFloat(e.target.value)}}})} />
                    </label>
                    <label>
                      <strong>Max Potassium (mg):</strong>
                      <input type="number" style={{width:'100%', padding:'8px'}} value={editForm.suggestions.electrolytes_mg?.max_potassium || ''} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, electrolytes_mg: {...editForm.suggestions.electrolytes_mg, max_potassium: parseFloat(e.target.value)}}})} />
                    </label>
                  </div>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                    <label>
                      <strong>Max Phosphorus (mg):</strong>
                      <input type="number" style={{width:'100%', padding:'8px'}} value={editForm.suggestions.electrolytes_mg?.max_phosphorus || ''} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, electrolytes_mg: {...editForm.suggestions.electrolytes_mg, max_phosphorus: parseFloat(e.target.value)}}})} />
                    </label>
                    <label>
                      <strong>Max Carbs (g):</strong>
                      <input type="number" style={{width:'100%', padding:'8px'}} value={editForm.suggestions.macronutrients?.max_carbs_g || ''} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, macronutrients: {...editForm.suggestions.macronutrients, max_carbs_g: parseFloat(e.target.value)}}})} />
                    </label>
                  </div>
                  <label>
                    <strong>Medical Considerations:</strong>
                    <textarea style={{width:'100%', padding:'8px', height:'60px'}} value={editForm.suggestions.medical_considerations || ''} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, medical_considerations: e.target.value}})} />
                  </label>
                  <label>
                    <strong>Clinical Rationale:</strong>
                    <textarea style={{width:'100%', padding:'8px', height:'60px'}} value={editForm.suggestions.clinical_rationale} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, clinical_rationale: e.target.value}})} />
                  </label>

                </>
              )}

              {editingItem.type === 'Ingredient' && editForm.suggestions && (
                <>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                    <label>
                      <strong>Safety Grade:</strong>
                      <select style={{width:'100%', padding:'8px'}} value={editForm.suggestions.safety_grade} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, safety_grade: e.target.value}})}>
                        <option value="A">A (Safe)</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                        <option value="F">F (Hazardous)</option>
                      </select>
                    </label>
                    <label>
                      <strong>UNII Code:</strong>
                      <input style={{width:'100%', padding:'8px'}} value={editForm.suggestions.unii_code} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, unii_code: e.target.value}})} />
                    </label>
                  </div>

                  {editForm.suggestions.cross_references && (
                    <div style={{marginTop:'15px', background:'#f8f9fa', padding:'10px', borderRadius:'8px', border:'1px solid #eee'}}>
                       <strong style={{fontSize:'0.8rem', color:'#666', textTransform:'uppercase'}}>Universal Cross-References:</strong>
                       <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'10px'}}>
                          {Object.entries(editForm.suggestions.cross_references).map(([provider, codes]: [string, any]) => (
                             <div key={provider}>
                                <small style={{fontWeight:'bold'}}>{provider.toUpperCase()}:</small>
                                <div style={{fontSize:'0.75rem'}}>
                                   {(codes || []).join(', ') || 'None found'}
                                </div>
                             </div>
                          ))}
                       </div>
                    </div>
                  )}

                  <label style={{marginTop:'15px', display:'block'}}>
                    <strong>Clinical Justification:</strong>
                    <textarea style={{width:'100%', padding:'8px', height:'60px'}} value={editForm.suggestions.clinical_justification} onChange={e => setEditForm({...editForm, suggestions: {...editForm.suggestions, clinical_justification: e.target.value}})} />
                  </label>

                  <div style={{marginTop:'10px'}}>
                    <strong>Clinical Allergen Mapping:</strong>
                    <div style={{display:'flex', gap:'5px', flexWrap:'wrap', marginTop:'10px', marginBottom:'10px'}}>
                      {(editForm.suggestions.allergens || []).map((alg: any, idx: number) => (
                        <span key={idx} className="badge" style={{background:'var(--pfb-sidebar)', color:'#fff', padding:'5px 10px', display:'flex', alignItems:'center', gap:'8px'}}>
                          {alg.name} {alg.category && alg.category !== alg.name && <small style={{opacity:0.8}}>({alg.category})</small>}
                          <span style={{cursor:'pointer', fontWeight:'bold'}} onClick={() => {
                            const next = [...editForm.suggestions.allergens];
                            next.splice(idx, 1);
                            setEditForm({...editForm, suggestions: {...editForm.suggestions, allergens: next}});
                          }}>×</span>
                        </span>
                      ))}
                      {(editForm.suggestions.allergens || []).length === 0 && editForm.type === 'Ingredient' && (
                        <div style={{width:'100%', background:'#fff3cd', border:'1px solid #e0a800', borderRadius:'6px', padding:'10px'}}>
                          <strong style={{color:'#856404'}}>No allergen linked — this ingredient will not block any product.</strong>
                          <div style={{fontSize:'0.85rem', color:'#856404', marginTop:'4px'}}>
                            If you approve it like this, a patient note naming it will keep being flagged as an unknown term.
                            Add the allergen below, or approve only if this ingredient is genuinely allergen-free (e.g. water, salt).
                          </div>
                        </div>
                      )}
                      {(editForm.suggestions.allergens || []).length === 0 && editForm.type !== 'Ingredient' && <span style={{fontSize:'0.8rem', color:'#999'}}>No allergens mapped.</span>}
                    </div>
                    <div style={{display:'flex', gap:'5px', flexDirection:'column'}}>
                      <input 
                        placeholder="Search existing allergens..." 
                        style={{width:'100%', padding:'8px'}} 
                        value={algSearch}
                        onChange={e => setAlgSearch(e.target.value)}
                      />
                      {algResults.length > 0 && (
                        <div style={{maxHeight:'120px', overflowY:'auto', border:'1px solid #ddd', borderRadius:'4px', background:'#fff'}}>
                          {algResults.map(r => (
                            <div 
                              key={r.id} 
                              onClick={() => {
                                const next = [...(editForm.suggestions.allergens || []), { name: r.name, id: r.id, is_new: false, severity_level: 'Major' }];
                                setEditForm({...editForm, suggestions: {...editForm.suggestions, allergens: next}});
                                setAlgSearch('');
                              }} 
                              style={{padding:'8px', cursor:'pointer', borderBottom:'1px solid #f9f9f9', fontSize:'0.85rem'}}
                            >
                              <strong>{r.name}</strong> <small style={{color:'#666'}}>{r.id}</small>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{display:'flex', gap:'5px', marginTop:'5px'}}>
                        <button type="button" className="btn-secondary" style={{fontSize:'0.75rem'}} onClick={() => {
                          if (!algSearch) return;
                          const next = [...(editForm.suggestions.allergens || []), { name: algSearch, is_new: true, severity_level: 'Major' }];
                          setEditForm({...editForm, suggestions: {...editForm.suggestions, allergens: next}});
                          setAlgSearch('');
                        }}>+ Add "{algSearch}" as NEW</button>
                      </div>
                    </div>
                    </div>
                    </>
                    )}
                    </div>

                    <div style={{marginTop:'30px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <button className="btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
                    <div style={{display:'flex', gap:'10px'}}>
                    <button className="btn-secondary" onClick={handleSaveEdit} style={{borderColor:'var(--pfb-sidebar)', color:'var(--pfb-sidebar)'}}>Save Draft</button>
                    <button onClick={handleApproveEdit} style={{background:'var(--pfb-sidebar)', color:'white'}}>Approve & Move to Graph</button>
                    </div>
                    </div>

          </div>
        </div>
      )}
    </div>
  );
};

const ComplianceView = () => {
  const standards = [
    {
      title: "HIPAA Security Rule",
      standard: "Technical Safeguards (§ 164.312)",
      usage: "Enforced via automatic session termination (15 min), restricted clinical roles (RDN/Admin), and Azure-exclusive AI environment under MSFT-PFB BAA.",
      location: "🔒 Authentication & Session Middleware"
    },
    {
      title: "Data Minimization & Privacy",
      standard: "PHI Masking & Obfuscation",
      usage: "Technical logs automatically redact patient names, notes, and clinical data. Body masking on PHI-sensitive API paths ensures zero PHI exposure in text logs.",
      location: "🛡️ FullLoggingMiddleware"
    },
    {
      title: "Immutable Audit Trail",
      standard: "SHA-256 Cryptographic Signing",
      usage: "Every system modification and PHI access is signed with a cryptographic hash, creating a non-repudiable evidence chain for federal audits.",
      location: "📋 SQL Audit Logs & Hash Chain"
    },
    {
      title: "Brute Force Protection",
      standard: "Redis-backed Throttling",
      usage: "Strict rate limiting on /token (10 req/min) and API endpoints (200 req/min) to prevent automated credential attacks and DDoS degradation.",
      location: "⚡ RateLimitMiddleware"
    },
    {
      title: "ICD-10 (Clinical Conditions)",
      standard: "WHO International Classification of Diseases",
      usage: "Patient clinical conditions are mapped to standard WHO/CMS codes to ensure medical interoperability and precise safety thresholds.",
      location: "🏥 Conditions Catalog & Patient Profiles"
    },
    {
      title: "21 CFR Part 11",
      standard: "FDA Electronic Records & Signatures",
      usage: "All clinical overrides and master data approvals are electronically signed and authenticated by identified clinical personnel.",
      location: "✍️ RDN Approval Workflows"
    },
    {
      title: "Network Isolation",
      standard: "Zero-Trust Service Architecture",
      usage: "Internal microservices bound to localhost; external access restricted to official origins via closed CORS policies and encrypted TLS tunnels.",
      location: "🌐 Infrastructure & Gateway"
    },
    {
      title: "FDA GSRS (FDA UNII)",
      standard: "Official FDA Substance Identification",
      usage: "Ingredient mapping utilizes FDA Official Substance names and UNII (Unique Ingredient Identifier) codes for pharmaceutical-grade precision.",
      location: "🧪 Ingredient Master & AI Auditor"
    }
  ];

  return (
    <div className="card">
      <div style={{marginBottom: '30px', borderBottom: '2px solid var(--pfb-green-light)', paddingBottom: '15px'}}>
        <h2 style={{margin: 0, color: 'var(--pfb-sidebar)'}}>Compliance & Regulatory Framework</h2>
        <p style={{color: '#666'}}>Documentation of medical and food safety standards integrated into PFB SafeGuard.</p>
      </div>
      <div className="stats-grid">
        {standards.map((s, i) => (
          <div key={i} className="card" style={{borderTop: '4px solid var(--pfb-green)', marginBottom: 0}}>
            <h3 style={{marginTop: 0, color: 'var(--pfb-green-dark)'}}>{s.title}</h3>
            <p style={{fontSize: '0.8rem', fontWeight: 'bold', color: '#999', textTransform: 'uppercase'}}>{s.standard}</p>
            <p style={{fontSize: '0.9rem', margin: '15px 0'}}>{s.usage}</p>
            <div style={{background: '#f8f9fa', padding: '10px', borderRadius: '5px', fontSize: '0.8rem', fontWeight: 'bold'}}>
              Applied in: {s.location}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClinicalIdentityGovernance = ({ onShowManual }: any) => {
  const { toast, confirm } = useUI();
  const [type, setType] = useState('Ingredient');
  const [mode, setMode] = useState('exact');
  const [clusters, setClusters] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<any>(null);
  const [merging, setMerging] = useState(false);

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/graph/de-duplication/clusters`, { params: { type, mode } });
      setClusters(res.data.clusters || []);
    } catch { alert("Failed to scan for duplicates."); }
    setLoading(false);
  }, [type, mode]);

  useEffect(() => { scan(); }, [scan]);

  const executeMerge = (canonicalId: string, redundantIds: string[]) => {
    confirm("Surgical merge", `Consolidates ${redundantIds.length + 1} nodes into ${canonicalId} and re-maps all relationships. The absorbed nodes are permanently deleted.`, async () => {
      setMerging(true);
      try {
        await axios.post(`${API_URL}/graph/de-duplication/merge`, { canonical_id: canonicalId, redundant_ids: redundantIds, type: type });
        toast("Merge successful — graph consolidated.", "success");
        setSelectedCluster(null);
        scan();
      } catch { toast("Merge operation failed.", "error"); }
      setMerging(false);
    }, { danger: true, confirmText: 'Merge' });
  };

  const handleCleanup = () => {
    confirm("Strip stray symbols?", `Removes symbols like ()[]* from ALL ${type} names and IDs in the graph. A surgical maintenance task.`, async () => {
      setLoading(true);
      try {
        await axios.post(`${API_URL}/graph/maintenance/cleanup`, { type });
        toast("Format cleanup successful.", "success");
        scan();
      } catch { toast("Cleanup failed.", "error"); }
      setLoading(false);
    }, { confirmText: 'Clean up' });
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1 style={{margin:0}}>🏛️ Clinical Identity Governance</h1>
        <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
           <button 
             className="btn-secondary" 
             onClick={() => onShowManual('identity-governance')} 
             style={{background:'#fff3cd', color:'#856404', borderColor:'#ffeeba', marginRight:'5px'}}
           >
             ❓ Help
           </button>
           <select value={type} onChange={e => setType(e.target.value)} style={{padding:'10px', borderRadius:'4px'}}>
             <option value="Ingredient">Ingredients Catalog</option>
             <option value="Condition">Medical Conditions (ICD)</option>
           </select>
           <select value={mode} onChange={e => setMode(e.target.value)} style={{padding:'10px', borderRadius:'4px', border: mode === 'messy' ? '1px solid orange' : '1px solid #ccc'}}>
             <option value="exact">Exact Code Match</option>
             <option value="messy">Messy Symbols & Fuzzy Match</option>
           </select>
           <button className="btn-secondary" onClick={handleCleanup} title="Strip symbols from names/IDs" style={{borderColor:'orange', color:'orange'}}>🪄 Cleanup</button>
           <button onClick={scan} disabled={loading} title="Re-scan the graph for duplicate or messy clinical identities and refresh the list below.">🔄 Rescan Graph</button>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns: selectedCluster ? '1fr 1fr' : '1fr', gap:'20px'}}>
         <div className="card">
            <h3>Detected Identity Clusters (Potential Duplicates)</h3>
            <p style={{fontSize:'0.85rem', color:'#666'}}>The system identified these groups of nodes sharing the same clinical identifiers.</p>
            
            <div style={{marginTop:'20px'}}>
               {clusters.length === 0 && !loading && <div style={{textAlign:'center', padding:'40px', color:'#999'}}>✅ No duplicate clusters found. Graph is healthy.</div>}
               {loading && <p>Scanning clinical connections...</p>}
               {clusters.map((c, i) => (
                 <div 
                   key={i} 
                   onClick={() => setSelectedCluster(c)}
                   style={{
                     padding:'15px', 
                     border:'1px solid #eee', 
                     borderRadius:'8px', 
                     marginBottom:'10px', 
                     cursor:'pointer',
                     background: selectedCluster === c ? 'var(--pfb-green-light)' : '#fff',
                     borderLeft: `4px solid ${c.match_reason === 'PRIMARY_CODE' ? 'var(--pfb-sidebar)' : 'var(--pfb-green)'}`
                   }}
                 >
                    <div style={{display:'flex', justifyContent:'space-between'}}>
                       <strong>Key: {c.cluster_key}</strong>
                       <span className="badge" style={{fontSize:'0.6rem'}}>{c.match_reason}</span>
                    </div>
                    <div style={{marginTop:'10px', display:'flex', gap:'5px'}}>
                       {c.items.map((it:any) => <span key={it.id} className="badge" style={{background:'#eee'}}>{it.name}</span>)}
                    </div>
                 </div>
               ))}
            </div>
         </div>

         {selectedCluster && (
           <div className="card" style={{border:'2px solid var(--pfb-green)', animation:'fadeIn 0.3s'}}>
              <h3>Surgical Merge Comparison</h3>
              <p style={{fontSize:'0.8rem', color:'#666'}}>Select the <strong>Canonical Node</strong> (the one that should remain). All others will be absorbed.</p>
              
              <div style={{marginTop:'20px'}}>
                 {selectedCluster.items.map((it:any) => (
                   <div key={it.id} className="card" style={{marginBottom:'10px', border:'1px solid #ddd'}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                         <div>
                            <strong>{it.name}</strong><br/>
                            <code style={{fontSize:'0.7rem'}}>{it.id}</code>
                         </div>
                         <button 
                           className="btn-primary" 
                           style={{fontSize:'0.8rem', padding:'5px 15px'}}
                           disabled={merging}
                           onClick={() => executeMerge(it.id, selectedCluster.items.filter((x:any) => x.id !== it.id).map((x:any) => x.id))}
                         >
                            Set as Canonical & Merge
                         </button>
                      </div>
                      <div style={{marginTop:'10px'}}>
                         <small style={{color:'#666'}}>Associated Codes:</small>
                         <div style={{display:'flex', gap:'5px', marginTop:'4px', flexWrap: 'wrap'}}>
                            {(!it.codes || it.codes.length === 0) ? (
                               <span style={{fontSize:'0.65rem', color:'#999', fontStyle:'italic'}}>None detected</span>
                            ) : (
                               it.codes.map((code:string) => <span key={code} className="badge" style={{fontSize:'0.6rem'}}>{code}</span>)
                            )}
                         </div>
                      </div>
                   </div>
                 ))}
              </div>
              <button className="btn-secondary" style={{width:'100%', marginTop:'20px'}} onClick={()=>setSelectedCluster(null)}>Cancel Comparison</button>
           </div>
         )}
      </div>
    </div>
  );
};

const ServiceAccountsView = () => {
  const { toast, confirm } = useUI();
  const [sas, setSas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingSA, setEditingSA] = useState<any>(null);
  const [newSA, setNewSA] = useState({ client_id: '', secret: '', name: '', allowed_ips: '', expires_at: '', scopes: 'read,check', purpose: '', owner: '' });
  const [createdSecret, setCreatedSecret] = useState<any>(null);

  // CSPRNG, 43 chars, URL-safe alphabet (64 chars divides 2^32 exactly -> no modulo bias).
  // Avoids '+' '/' '=' which break form-urlencoded auth ('+' decodes as a space).
  const generateSecret = () => {
    const abc = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    const arr = new Uint32Array(43);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, (n: number) => abc[n % abc.length]).join('');
  };

  const fetchSAs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/service-accounts`);
      setSas(res.data);
    } catch { alert("Failed to fetch service accounts"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSAs(); }, [fetchSAs]);

  const handleCreate = async (e: any) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/service-accounts`, {
        ...newSA,
        secret: newSA.secret || null,
        expires_at: newSA.expires_at ? `${newSA.expires_at}T00:00:00` : null
      });
      setCreatedSecret(res.data);
      setShowAdd(false);
      fetchSAs();
      setNewSA({ client_id: '', secret: '', name: '', allowed_ips: '', expires_at: '', scopes: 'read,check', purpose: '', owner: '' });
    } catch (err: any) { alert(err.response?.data?.detail || "Failed to create"); }
  };

  const handleUpdate = async (e: any) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/service-accounts/${editingSA.client_id}`, {
        name: editingSA.name,
        is_active: editingSA.is_active,
        allowed_ips: editingSA.allowed_ips,
        scopes: editingSA.scopes,
        purpose: editingSA.purpose,
        owner: editingSA.owner,
        expires_at: editingSA.expires_at ? (editingSA.expires_at.includes('T') ? editingSA.expires_at : `${editingSA.expires_at}T00:00:00`) : null
      });
      setEditingSA(null);
      fetchSAs();
      alert("Service account updated successfully.");
    } catch (err: any) { alert(err.response?.data?.detail || "Update failed"); }
  };

  // Expiry traffic-light: an expired credential is now REJECTED at login,
  // so a silent expiry breaks integrations. Surface it early.
  const expiryInfo = (exp: string | null) => {
    if (!exp) return { text: 'Never', color: '#888', dot: '#bbb' };
    const d = new Date(exp);
    const days = Math.floor((d.getTime() - Date.now()) / 86400000);
    if (days < 0)  return { text: `EXPIRED (${d.toLocaleDateString()})`, color: '#c0392b', dot: '#c0392b' };
    if (days <= 30) return { text: `${d.toLocaleDateString()} — ${days}d left`, color: '#b7791f', dot: '#e0a800' };
    return { text: `${d.toLocaleDateString()} — ${days}d left`, color: '#2d7a4f', dot: '#28a745' };
  };

  const handleDelete = (cid: string) => {
    confirm("Delete service account?", `“${cid}” will lose access immediately. Any integration using it will stop working.`, async () => {
      try {
        await axios.delete(`${API_URL}/service-accounts/${cid}`);
        fetchSAs();
      } catch { toast("Delete failed", "error"); }
    }, { danger: true, confirmText: 'Delete' });
  };

  return (
    <div className="card">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
        <h2 style={{margin:0}}>🔐 Service Accounts (M2M)</h2>
        <button onClick={()=>setShowAdd(true)}>+ Register New System</button>
      </div>

      <p style={{color: '#666', marginBottom: '20px'}}>
        Manage non-interactive access for external systems (B2B). These accounts use <strong>Client Credentials</strong> 
        and require <strong>IP Whitelisting</strong> for HIPAA/SOC2 compliance.
      </p>

      {createdSecret && (
        <div className="card" style={{background:'#fffbe6', border:'2px solid #e0a800', marginBottom:'20px'}}>
          <h3 style={{marginTop:0}}>Credential created — copy the secret NOW</h3>
          <p style={{margin:'6px 0', color:'#856404'}}>
            This is the <strong>only time</strong> the secret is shown. It is stored hashed and cannot be recovered.
            Share it over a secure channel (never email or plain chat).
            {createdSecret.secret_generated && <> It was <strong>auto-generated</strong> (256-bit, URL-safe).</>}
          </p>
          <div style={{margin:'10px 0'}}>
            <div><small style={{color:'#666'}}>Client ID</small></div>
            <code style={{fontSize:'0.95rem'}}>{createdSecret.client_id}</code>
          </div>
          <div style={{margin:'10px 0'}}>
            <div><small style={{color:'#666'}}>Client Secret</small></div>
            <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
              <code style={{fontSize:'0.95rem', background:'#fff', padding:'8px', borderRadius:'4px', border:'1px solid #ddd', flex:1, wordBreak:'break-all'}}>{createdSecret.secret}</code>
              <button type="button" onClick={()=>{navigator.clipboard.writeText(createdSecret.secret); alert('Secret copied to clipboard.');}}>Copy</button>
            </div>
          </div>
          <button type="button" className="btn-secondary" onClick={()=>setCreatedSecret(null)}>I have stored it securely — dismiss</button>
        </div>
      )}

      {showAdd && (
        <div className="card" style={{background: '#f9f9f9', border: '1px solid var(--pfb-green)'}}>
          <h3>Register External System</h3>
          <form onSubmit={handleCreate} style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
            <div className="form-group">
              <label>Client ID (Machine Name)</label>
              <input placeholder="e.g. pfb-mobile-api" value={newSA.client_id} onChange={e=>setNewSA({...newSA, client_id: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Client Secret (leave empty = auto-generate)</label>
              <div style={{display:'flex', gap:'6px'}}>
                <input style={{flex:1}} placeholder="Leave empty and the server generates a strong one"
                       value={newSA.secret} onChange={e=>setNewSA({...newSA, secret: e.target.value})} />
                <button type="button" className="btn-secondary" style={{whiteSpace:'nowrap'}}
                        onClick={()=>setNewSA({...newSA, secret: generateSecret()})}>Generate</button>
              </div>
              <small style={{color:'#666'}}>Min 16 chars. Recommended: leave empty (256-bit, URL-safe).</small>
            </div>
            <div className="form-group">
              <label>Display Name</label>
              <input placeholder="e.g. PFB Mobile Application" value={newSA.name} onChange={e=>setNewSA({...newSA, name: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>IP Whitelist (Comma separated)<HelpTip text="Only these IP addresses (or CIDR ranges) may use this credential. A login from any other IP is rejected. Strongly recommended — it’s a HIPAA/SOC2 control." /></label>
              <input placeholder="e.g. 20.165.202.196, 127.0.0.1" value={newSA.allowed_ips} onChange={e=>setNewSA({...newSA, allowed_ips: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Expiration Date<HelpTip text="After this date the credential stops working (login is rejected). Set one so forgotten integrations don’t stay valid forever; the list shows a traffic-light as it nears expiry." /></label>
              <input type="date" value={newSA.expires_at} onChange={e=>setNewSA({...newSA, expires_at: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Scopes<HelpTip text="What this system is allowed to do, comma-separated. Common: “read” (view data) and “check” (run safety checks). Grant only what the integration needs." /></label>
              <input value={newSA.scopes} onChange={e=>setNewSA({...newSA, scopes: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Owner (accountable team/person)<HelpTip text="The team or person responsible for this credential — so you know who to contact if it needs rotating or revoking." /></label>
              <input placeholder="e.g. Ceres Dev Team - j.perez@..." value={newSA.owner} onChange={e=>setNewSA({...newSA, owner: e.target.value})} required />
            </div>
            <div className="form-group" style={{gridColumn:'span 2'}}>
              <label>Justification / Purpose (required for audit)</label>
              <textarea rows={2} style={{width:'100%'}} placeholder="Why this credential exists, what it accesses, and the justification for each IP/range in the whitelist." value={newSA.purpose} onChange={e=>setNewSA({...newSA, purpose: e.target.value})} required />
            </div>
            <div style={{gridColumn:'span 2', display:'flex', gap:'10px', marginTop:'10px'}}>
              <button type="submit">Create Account</button>
              <button type="button" className="btn-secondary" onClick={()=>setShowAdd(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {editingSA && (
        <div className="card" style={{background: '#fff9f0', border: '1px solid var(--warning)'}}>
          <h3>Edit External System: {editingSA.client_id}</h3>
          <form onSubmit={handleUpdate} style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
            <div className="form-group">
              <label>Display Name</label>
              <input value={editingSA.name} onChange={e=>setEditingSA({...editingSA, name: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={editingSA.is_active} onChange={e=>setEditingSA({...editingSA, is_active: parseInt(e.target.value)})}>
                <option value={1}>Active</option>
                <option value={0}>Disabled</option>
              </select>
            </div>
            <div className="form-group">
              <label>IP Whitelist (Ranges / CIDR supported)</label>
              <input placeholder="e.g. 20.165.202.196, 10.0.0.0/24" value={editingSA.allowed_ips || ''} onChange={e=>setEditingSA({...editingSA, allowed_ips: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Scopes</label>
              <input value={editingSA.scopes} onChange={e=>setEditingSA({...editingSA, scopes: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Expiration Date</label>
              <input type="date" value={editingSA.expires_at?.split('T')[0] || ''} onChange={e=>setEditingSA({...editingSA, expires_at: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Owner (accountable team/person)</label>
              <input value={editingSA.owner || ''} onChange={e=>setEditingSA({...editingSA, owner: e.target.value})} />
            </div>
            <div className="form-group" style={{gridColumn:'span 2'}}>
              <label>Justification / Purpose</label>
              <textarea rows={2} style={{width:'100%'}} value={editingSA.purpose || ''} onChange={e=>setEditingSA({...editingSA, purpose: e.target.value})} />
            </div>
            <div style={{gridColumn:'span 2', display:'flex', gap:'10px', marginTop:'10px'}}>
              <button type="submit" style={{background:'var(--warning)', color:'black'}}>Save Changes</button>
              <button type="button" className="btn-secondary" onClick={()=>setEditingSA(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%', borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'#f4f7f6'}}>
              <th style={{padding:'12px', textAlign:'left'}}>System Name</th>
              <th style={{padding:'12px', textAlign:'left'}}>Client ID</th>
              <th style={{padding:'12px', textAlign:'left'}}>Owner</th>
              <th style={{padding:'12px', textAlign:'left'}}>Justification</th>
              <th style={{padding:'12px', textAlign:'left'}}>IP Whitelist</th>
              <th style={{padding:'12px', textAlign:'left'}}>Expires</th>
              <th style={{padding:'12px', textAlign:'left'}}>Status</th>
              <th style={{padding:'12px', textAlign:'center'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{textAlign:'center', padding:'20px'}}>Loading accounts...</td></tr> : sas.map(sa => (
              <tr key={sa.client_id} style={{borderBottom:'1px solid #eee'}}>
                <td style={{padding:'12px'}}><strong>{sa.name}</strong></td>
                <td style={{padding:'12px'}}><code>{sa.client_id}</code></td>
                <td style={{padding:'12px'}}><small>{sa.owner || <span style={{color:'#c0392b'}}>— missing —</span>}</small></td>
                <td style={{padding:'12px', maxWidth:'260px'}}><small style={{color:'#555'}}>{sa.purpose || <span style={{color:'#c0392b'}}>— missing —</span>}</small></td>
                <td style={{padding:'12px'}}><small>{sa.allowed_ips || <span style={{color:'#c0392b'}}>ANY (Not Recommended)</span>}</small></td>
                <td style={{padding:'12px'}}>
                  <span style={{display:'inline-block', width:'9px', height:'9px', borderRadius:'50%', background: expiryInfo(sa.expires_at).dot, marginRight:'7px'}}></span>
                  <small style={{color: expiryInfo(sa.expires_at).color, fontWeight: 600}}>{expiryInfo(sa.expires_at).text}</small>
                </td>
                <td style={{padding:'12px'}}>
                  <span className={`badge ${sa.is_active ? 'green' : 'red'}`}>{sa.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                </td>
                <td style={{padding:'12px', textAlign:'center'}}>
                   <div style={{display:'flex', gap:'5px', justifyContent:'center'}}>
                      <button className="btn-secondary" style={{padding:'4px 8px', fontSize:'0.7rem'}} onClick={() => setEditingSA(sa)}>Edit</button>
                      <button className="btn-secondary" style={{padding:'4px 8px', fontSize:'0.7rem', color:'var(--danger)', borderColor:'var(--danger)'}} onClick={()=>handleDelete(sa.client_id)}>Revoke</button>
                   </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Forensic Safety-Check Audit ───────────────────────────────────────────
// Every safety check (CSARE /check, forbidden-skus, /batch, allergen-impact,
// note-impact) is persisted one-row-per-member with who/where/when/what-asked/
// what-answered. This is the human search surface over GET /audit/safety-checks.
// It exists because members received food they were allergic to and we must be
// able to reconstruct exactly what the system was asked and what it answered.
const RevealArchiveBox = () => {
  const [open, setOpen] = useState(false);
  const [ct, setCt] = useState(''); const [just, setJust] = useState('');
  const [out, setOut] = useState<any>(null); const [err, setErr] = useState('');
  const reveal = async () => {
    setErr(''); setOut(null);
    try { const r = await axios.post(`${API_URL}/archive/reveal`, { ciphertext: ct.trim(), justification: just.trim() }); setOut(r.data.revealed); }
    catch (e: any) { setErr(e.response?.data?.detail || 'Reveal failed.'); }
  };
  return (
    <div style={{ margin: '8px 0 14px' }}>
      <button className="btn-secondary" style={{ fontSize: '0.78rem' }} onClick={() => setOpen(o => !o)}>
        {open ? '▾' : '▸'} 🔓 Reveal archived record (decrypt the Snowflake 7-year vault)
      </button>
      {open && (
        <div style={{ border: '1px solid #e2e6ee', borderRadius: 10, padding: 12, marginTop: 8, background: '#fafbfc' }}>
          <p style={{ fontSize: '0.75rem', color: '#666', marginTop: 0 }}>Paste the encrypted <code>PAYLOAD_ENC</code> from the Snowflake archive. The key lives only in SafeGuard; every reveal is written to the audit log with your justification.</p>
          <textarea rows={3} value={ct} onChange={e => setCt(e.target.value)} placeholder="enc:v1:…" style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.78rem', boxSizing: 'border-box' }} />
          <input value={just} onChange={e => setJust(e.target.value)} placeholder="Justification (audited) — why you need this record" style={{ width: '100%', marginTop: 6, boxSizing: 'border-box' }} />
          <button className="btn-primary" style={{ marginTop: 8 }} onClick={reveal} disabled={!ct || !just}>🔓 Reveal (audited)</button>
          {err && <div style={{ color: '#b42318', marginTop: 8, fontSize: '0.82rem' }}>{err}</div>}
          {out && <pre style={{ marginTop: 8, background: '#0f1c19', color: '#d8e6e1', padding: 10, borderRadius: 8, fontSize: '0.78rem', overflowX: 'auto' }}>{JSON.stringify(out, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
};

const SafetyAuditView = () => {
  const EMPTY = { member_id: '', actor: '', endpoint: '', decision: '', request_id: '', source_file: '', job_id: '', since: '', until: '' };
  const [filters, setFilters] = useState<any>({ ...EMPTY });
  const [sortBy, setSortBy] = useState('ts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<any>({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [err, setErr] = useState<string>('');

  const ENDPOINTS = [
    { v: '', label: 'All endpoints' },
    { v: '/check', label: 'CSARE — /check (member×SKU)' },
    { v: '/check/forbidden-skus', label: 'Forbidden SKUs (single)' },
    { v: '/check/forbidden-skus/batch', label: 'Forbidden SKUs (batch)' },
    { v: '/check/allergen-impact', label: 'Allergen Impact' },
    { v: '/check/note-impact', label: 'Dietary-Note Impact' },
  ];
  const DECISIONS = ['', 'UNKNOWN_MEMBER', 'BLOCKED', 'ALLOWED_NONE', 'IMPACT', 'NOTE', 'ERROR'];

  const fetchRows = useCallback(async (ovr: any = {}) => {
    setLoading(true); setErr('');
    const f = { ...filters, ...(ovr.filters || {}) };
    const off = ovr.offset !== undefined ? ovr.offset : offset;
    const sb = ovr.sortBy || sortBy;
    const sd = ovr.sortDir || sortDir;
    const params: any = { sort_by: sb, sort_dir: sd, limit, offset: off };
    Object.keys(EMPTY).forEach(k => { if (f[k]) params[k] = f[k]; });
    try {
      const res = await axios.get(`${API_URL}/audit/safety-checks`, { params });
      setData(res.data); setExpanded(null);
    } catch (e: any) {
      setErr(e.response?.status === 403 ? 'Forbidden — this forensic view is restricted to Admin and Auditor roles.'
                                         : (e.response?.data?.detail || 'Search failed.'));
      setData({ total: 0, items: [] });
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, offset, sortBy, sortDir, limit]);

  useEffect(() => { fetchRows({ offset: 0 }); /* initial load */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => { setOffset(0); fetchRows({ offset: 0 }); };
  const reset = () => { setFilters({ ...EMPTY }); setOffset(0); fetchRows({ filters: EMPTY, offset: 0 }); };
  const smokingGun = () => {
    const f = { ...EMPTY, decision: 'UNKNOWN_MEMBER' };
    setFilters(f); setOffset(0); fetchRows({ filters: f, offset: 0 });
  };
  const sortOn = (col: string) => {
    const dir = sortBy === col && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(col); setSortDir(dir); setOffset(0); fetchRows({ sortBy: col, sortDir: dir, offset: 0 });
  };
  const page = (delta: number) => {
    const off = Math.max(0, offset + delta * limit);
    setOffset(off); fetchRows({ offset: off });
  };

  const decColor = (d: string) => {
    if (d === 'UNKNOWN_MEMBER') return { bg: '#fdecea', fg: '#c0392b', bd: '#e6b0aa' };
    if (d === 'BLOCKED') return { bg: '#eafaf1', fg: '#1e8449', bd: '#a9dfbf' };
    if (d === 'ALLOWED_NONE') return { bg: '#fef9e7', fg: '#b7791f', bd: '#f5d76e' };
    return { bg: '#eef2f7', fg: '#34495e', bd: '#d5dbe1' };
  };

  const exportCsv = () => {
    const cols = ['ts_human', 'request_id', 'endpoint', 'actor', 'actor_role', 'actor_ip',
                  'member_id', 'member_name', 'decision', 'forbidden_count', 'latency_ms',
                  'engine_version', 'catalog_version', 'hash'];
    const esc = (v: any) => { const s = v === null || v === undefined ? '' : String(v); return `"${s.replace(/"/g, '""')}"`; };
    const lines = [cols.join(',')].concat(data.items.map((r: any) => cols.map(c => esc(r[c])).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `safeguard_safety_audit_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  const arrow = (col: string) => sortBy === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';
  const th: any = { textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', color: '#5a6b7a', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e1e6eb', whiteSpace: 'nowrap' };
  const thBtn: any = { ...th, cursor: 'pointer', userSelect: 'none' };
  const td: any = { padding: '8px 10px', fontSize: '0.82rem', borderBottom: '1px solid #eef1f4', verticalAlign: 'top' };
  const inp: any = { padding: '7px 9px', borderRadius: '6px', border: '1px solid #cfd6dd', fontSize: '0.82rem', width: '100%' };
  const showing = data.items.length ? `${offset + 1}–${offset + data.items.length}` : '0';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0 }}>🔎 Safety-Check Forensic Audit</h2>
      <RevealArchiveBox />
          <p style={{ color: '#666', margin: '6px 0 0', fontSize: '0.85rem', maxWidth: '760px' }}>
            Every safety-check request the engine received — one row <strong>per member</strong>, even inside a
            1,000-row batch (correlated by <code>request_id</code>). Captures who asked, their IP, a human-readable
            timestamp, exactly what was asked and what was answered, and for which member. Each row carries a
            SHA-256 hash for tamper-evidence.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={smokingGun} title="Members that resolved to nobody → 0 exclusions → 'ship everything'"
                  style={{ background: '#c0392b', color: '#fff' }}>⚠️ Unknown-member checks</button>
          <button onClick={exportCsv} disabled={!data.items.length} className="btn-secondary">⬇️ Export CSV</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', background: '#f7f9fb', padding: '14px', borderRadius: '10px', marginBottom: '14px' }}>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>MEMBER ID</label>
          <input style={inp} value={filters.member_id} onChange={e => setFilters({ ...filters, member_id: e.target.value })} placeholder="e.g. 110015269984" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>ACTOR (user / service)</label>
          <input style={inp} value={filters.actor} onChange={e => setFilters({ ...filters, actor: e.target.value })} placeholder="e.g. admin, ceres-svc" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>ENDPOINT</label>
          <select style={inp} value={filters.endpoint} onChange={e => setFilters({ ...filters, endpoint: e.target.value })}>
            {ENDPOINTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}</select></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>DECISION</label>
          <select style={inp} value={filters.decision} onChange={e => setFilters({ ...filters, decision: e.target.value })}>
            {DECISIONS.map(d => <option key={d} value={d}>{d || 'All decisions'}</option>)}</select></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>REQUEST ID</label>
          <input style={inp} value={filters.request_id} onChange={e => setFilters({ ...filters, request_id: e.target.value })} placeholder="correlate a batch" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>FILE</label>
          <input style={inp} value={filters.source_file} onChange={e => setFilters({ ...filters, source_file: e.target.value })} placeholder="uploaded filename" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>FROM (UTC)</label>
          <input style={inp} type="datetime-local" value={filters.since} onChange={e => setFilters({ ...filters, since: e.target.value })} /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>TO (UTC)</label>
          <input style={inp} type="datetime-local" value={filters.until} onChange={e => setFilters({ ...filters, until: e.target.value })} /></div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <button onClick={applyFilters} style={{ flex: 1 }}>Search</button>
          <button onClick={reset} className="btn-secondary">Clear</button>
        </div>
      </div>

      {err && <div style={{ background: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '0.85rem' }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '0.82rem', color: '#5a6b7a' }}>
        <span>{loading ? 'Searching…' : <>Showing <strong>{showing}</strong> of <strong>{data.total.toLocaleString()}</strong> matching check(s)</>}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          Rows:
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setOffset(0); fetchRows({ offset: 0 }); }}
                  style={{ padding: '4px 6px', borderRadius: '6px', border: '1px solid #cfd6dd' }}>
            {[50, 100, 250, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}</select>
          <button className="btn-secondary" disabled={offset === 0 || loading} onClick={() => page(-1)}>‹ Prev</button>
          <button className="btn-secondary" disabled={offset + data.items.length >= data.total || loading} onClick={() => page(1)}>Next ›</button>
        </span>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #eef1f4', borderRadius: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
          <thead><tr style={{ background: '#fafbfc' }}>
            <th style={th}></th>
            <th style={thBtn} onClick={() => sortOn('ts')}>Timestamp (UTC){arrow('ts')}</th>
            <th style={thBtn} onClick={() => sortOn('actor')}>Actor{arrow('actor')}</th>
            <th style={th}>IP</th>
            <th style={thBtn} onClick={() => sortOn('endpoint')}>Endpoint{arrow('endpoint')}</th>
            <th style={thBtn} onClick={() => sortOn('source_file')}>File{arrow('source_file')}</th>
            <th style={thBtn} onClick={() => sortOn('member_id')}>Member{arrow('member_id')}</th>
            <th style={thBtn} onClick={() => sortOn('decision')}>Decision{arrow('decision')}</th>
            <th style={thBtn} onClick={() => sortOn('forbidden_count')}>Excl.{arrow('forbidden_count')}</th>
            <th style={th}>ms</th>
          </tr></thead>
          <tbody>
            {data.items.map((r: any) => {
              const c = decColor(r.decision);
              const isUnknown = r.decision === 'UNKNOWN_MEMBER';
              const open = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr onClick={() => setExpanded(open ? null : r.id)}
                      style={{ cursor: 'pointer', background: isUnknown ? '#fef6f5' : (open ? '#f4f8fc' : '#fff') }}>
                    <td style={{ ...td, color: '#9aa7b2' }}>{open ? '▾' : '▸'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.ts_human}</td>
                    <td style={td}>{r.actor}<div style={{ color: '#9aa7b2', fontSize: '0.72rem' }}>{r.actor_role}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.actor_ip}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.76rem' }}>{r.endpoint}</td>
                    <td style={{ ...td, fontSize: '0.74rem', color: '#5a6b7a' }}>{r.source_file || <span style={{ color: '#9aa7b2' }}>—</span>}</td>
                    <td style={td}>{r.member_id || <span style={{ color: '#9aa7b2' }}>—</span>}
                      {r.member_name && <div style={{ color: '#9aa7b2', fontSize: '0.72rem' }}>{r.member_name}</div>}</td>
                    <td style={td}><span style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, padding: '2px 8px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {isUnknown && '⚠️ '}{r.decision}</span></td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.forbidden_count ?? '—'}</td>
                    <td style={{ ...td, color: '#9aa7b2', fontVariantNumeric: 'tabular-nums' }}>{r.latency_ms ?? '—'}</td>
                  </tr>
                  {open && (
                    <tr><td colSpan={10} style={{ padding: 0, background: '#f4f8fc', borderBottom: '2px solid #dde6ef' }}>
                      <div style={{ padding: '14px 18px' }}>
                        {isUnknown && <div style={{ background: '#fdecea', color: '#922b21', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '0.82rem' }}>
                          <strong>⚠️ Forensic flag:</strong> this member resolved to nobody in the graph, so the engine returned
                          <strong> 0 exclusions</strong> — i.e. "ship everything". An integrator that treated this as "no restrictions"
                          rather than an error could have sent unsafe food. This is the exact failure mode this audit exists to catch.
                        </div>}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px 20px', marginBottom: '12px', fontSize: '0.8rem' }}>
                          <div><span style={{ color: '#9aa7b2' }}>Request ID</span><br /><code>{r.request_id}</code></div>
                          <div><span style={{ color: '#9aa7b2' }}>Engine</span><br />{r.engine_version || '—'}</div>
                          <div><span style={{ color: '#9aa7b2' }}>Catalog</span><br /><code style={{ fontSize: '0.72rem' }}>{r.catalog_version || '—'}</code></div>
                          <div><span style={{ color: '#9aa7b2' }}>SHA-256 (tamper-evidence)</span><br /><code style={{ fontSize: '0.68rem', wordBreak: 'break-all' }}>{r.hash}</code></div>
                          <div><span style={{ color: '#9aa7b2' }}>Options used</span><br />
                            <code style={{ fontSize: '0.7rem' }}>{r.options ? JSON.stringify(r.options) : '—'}</code></div>
                          {r.job_id && <div><span style={{ color: '#9aa7b2' }}>From upload</span><br />
                            <button className="btn-secondary" style={{ padding: '2px 8px', fontSize: '0.72rem' }}
                              onClick={() => { const f = { ...EMPTY, job_id: String(r.job_id) }; setFilters(f); setOffset(0); fetchRows({ filters: f, offset: 0 }); }}>
                              job #{r.job_id} — show every row
                            </button></div>}
                          {r.payload_pruned_at && <div style={{ gridColumn: '1 / -1', color: '#9a5b00' }}>
                            🗄️ Detail archived on {new Date(r.payload_pruned_at).toLocaleDateString()} — the request and
                            response payloads were freed from the database after being encrypted into the 7-year archive.
                            Use “Reveal archived record” above to read them (the reveal is audited).
                          </div>}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                          <div>
                            <div style={{ fontSize: '0.72rem', color: '#5a6b7a', fontWeight: 700, marginBottom: '4px' }}>WHAT WAS ASKED</div>
                            <pre style={{ background: '#0f1b2b', color: '#c8e1ff', padding: '12px', borderRadius: '8px', fontSize: '0.72rem', maxHeight: '320px', overflow: 'auto', margin: 0 }}>{JSON.stringify(r.request_json, null, 2)}</pre>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.72rem', color: '#5a6b7a', fontWeight: 700, marginBottom: '4px' }}>WHAT WAS ANSWERED</div>
                            <pre style={{ background: '#0f1b2b', color: '#c8ffd4', padding: '12px', borderRadius: '8px', fontSize: '0.72rem', maxHeight: '320px', overflow: 'auto', margin: 0 }}>{JSON.stringify(r.response_json, null, 2)}</pre>
                          </div>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && !data.items.length && (
              <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#9aa7b2' }}>No safety checks match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Data Quality cockpit ──────────────────────────────────────────────────
// RDNs created a mess of duplicate/malformed taxonomy nodes. This read-only
// report categorizes it, flags the CLINICAL RISK (restrictions encoded as junk
// negation nodes that silently don't exclude anything), and offers guided,
// audited fixes — remediation (relink patients onto the real allergen) and safe
// mechanical merges. Fuzzy near-duplicates are review-only (they include
// distinct allergens like Beef~Beets), never auto-merged.
// ── Operational Intake (suggest medical items for RDN review) ─────────────
// Operational staff don't create medical entities directly. Here they SUGGEST a new
// Allergen/Condition — it lands in the RDN review queue (never the graph) exactly
// like allergens auto-detected from a CSV import. Products/Ingredients/Preferences
// they create directly on their own screens (no RDN needed).
const OperationalIntakeView = () => {
  const { toast, confirm } = useUI();
  const [alg, setAlg] = useState('');
  const [cond, setCond] = useState('');
  const [hint, setHint] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState('');
  const timers = React.useRef<Record<string, any>>({});

  const analyze = (type: 'Allergen' | 'Condition', name: string) => {
    if (timers.current[type]) clearTimeout(timers.current[type]);
    if (!name || name.trim().length < 2) { setHint(h => ({ ...h, [type]: null })); return; }
    timers.current[type] = setTimeout(async () => {
      try { const r = await axios.post(`${API_URL}/entities/analyze`, { type, name }); setHint(h => ({ ...h, [type]: r.data })); }
      catch { setHint(h => ({ ...h, [type]: null })); }
    }, 400);
  };

  const suggest = async (type: 'Allergen' | 'Condition', name: string, reset: () => void, force = false) => {
    if (!name.trim()) return;
    const endpoint = type === 'Allergen' ? 'allergens' : 'conditions';
    const id = `${type === 'Allergen' ? 'ALG' : 'COND'}_${name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')}`;
    setBusy(type);
    try {
      await axios.post(`${API_URL}/${endpoint}${force ? '?force=true' : ''}`, { id, name: name.trim() });
      toast(`“${name.trim()}” was sent to the RDN review queue. An RDN will approve, adjust or reject it.`, 'success');
      reset(); setHint(h => ({ ...h, [type]: null }));
    } catch (err: any) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d && d.error === 'ENTITY_QUALITY') {
        if (d.verdict === 'warn') {
          confirm('Similar entry already exists', `${d.suggestion || 'This looks similar to an existing entry.'} Suggest it anyway as a new, distinct item?`,
                  () => suggest(type, name, reset, true), { confirmText: 'Suggest anyway' });
        } else {
          toast(d.suggestion || 'This name was rejected by the data-quality guardrail.', 'error');
        }
      } else {
        toast('Could not submit. Please try again.', 'error');
      }
    }
    setBusy('');
  };

  const hintBanner = (type: string) => {
    const a = hint[type]; if (!a || a.verdict === 'ok') return null;
    const p: any = a.verdict === 'warn'
      ? { bg: '#fef9e7', bd: '#f5d76e', fg: '#b7791f', icon: '⚠️' }
      : { bg: '#fdecea', bd: '#e6b0aa', fg: '#c0392b', icon: a.verdict === 'duplicate' ? '🚫' : '⛔' };
    return (
      <div style={{ marginTop: 8, background: p.bg, border: `1px solid ${p.bd}`, borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', color: p.fg }}>
        {p.icon} {a.suggestion || 'Please review this entry.'}
        {(a.compound_parts || []).length > 1 && <div style={{ marginTop: 4, color: '#555' }}>Suggest each separately: {a.compound_parts.join(', ')}</div>}
      </div>
    );
  };

  const card = (type: 'Allergen' | 'Condition', value: string, setValue: (s: string) => void, placeholder: string) => {
    const blocked = hint[type] && ['block', 'duplicate'].includes(hint[type].verdict);
    return (
      <div style={{ flex: 1, minWidth: 300, background: '#fff', border: '1px solid #eef1f4', borderRadius: 12, padding: 18 }}>
        <h3 style={{ margin: '0 0 4px' }}>{type === 'Allergen' ? '⚠️ Suggest an Allergen' : '🏥 Suggest a Condition'}</h3>
        <p style={{ color: '#666', fontSize: '0.82rem', marginTop: 0 }}>Goes to the RDN review queue — it won’t affect safety checks until an RDN approves it.</p>
        <input value={value} onChange={e => { setValue(e.target.value); analyze(type, e.target.value); }} placeholder={placeholder}
               style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #cfd6dd' }} />
        {hintBanner(type)}
        <button disabled={!value.trim() || busy === type || blocked} onClick={() => suggest(type, value, () => setValue(''))}
                style={{ marginTop: 12 }}>{busy === type ? 'Sending…' : `Send suggestion to RDN review`}</button>
      </div>
    );
  };

  return (
    <div className="card">
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>➕ Suggest a Medical Item</h2>
        <p style={{ color: '#666', margin: '6px 0 0', fontSize: '0.85rem', maxWidth: 760 }}>
          Found a new allergen or condition (e.g. from a shipment, a source feed, or a patient note)? Suggest it here and an
          <strong> RDN reviews it</strong> before it goes live — the same safety gate used when a CSV import detects new allergens.
          <br />Need to add a <strong>Product, Ingredient or Preference</strong>? You can create those directly on their own screens — no RDN needed.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {card('Allergen', alg, setAlg, "e.g. Penicillin, Sesame, Sulfites")}
        {card('Condition', cond, setCond, "e.g. Chronic Kidney Disease, Type 2 Diabetes")}
      </div>
    </div>
  );
};

// ── Change Log (detailed entity-change audit) ─────────────────────────────
// Who changed what (Allergen/Ingredient/Condition/SKU), when, and HOW (before ->
// after). The UI surface over /audit/entity-changes. Distinct from Safety-Check
// Forensics (which logs the checks the engine RAN, not the taxonomy edits).
const ChangeLogView = () => {
  const EMPTY = { entity_type: '', actor: '', action: '', search: '', since: '', until: '' };
  const [filters, setFilters] = useState<any>({ ...EMPTY });
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<any>({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchRows = useCallback(async (ovr: any = {}) => {
    setLoading(true);
    const f = { ...filters, ...(ovr.filters || {}) };
    const off = ovr.offset !== undefined ? ovr.offset : offset;
    const params: any = { sort_by: ovr.sortBy || sortBy, sort_dir: ovr.sortDir || sortDir, limit, offset: off };
    Object.keys(EMPTY).forEach(k => { if (f[k]) params[k] = f[k]; });
    try {
      const res = await axios.get(`${API_URL}/audit/entity-changes`, { params });
      setData(res.data); setExpanded(null);
    } catch { setData({ total: 0, items: [] }); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, offset, sortBy, sortDir, limit]);
  useEffect(() => { fetchRows({ offset: 0 }); /* initial */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => { setOffset(0); fetchRows({ offset: 0 }); };
  const reset = () => { setFilters({ ...EMPTY }); setOffset(0); fetchRows({ filters: EMPTY, offset: 0 }); };
  const sortOn = (c: string) => { const d = sortBy === c && sortDir === 'desc' ? 'asc' : 'desc'; setSortBy(c); setSortDir(d); setOffset(0); fetchRows({ sortBy: c, sortDir: d, offset: 0 }); };
  const page = (delta: number) => { const off = Math.max(0, offset + delta * limit); setOffset(off); fetchRows({ offset: off }); };
  const arrow = (c: string) => sortBy === c ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

  const actionColor = (a: string) => {
    if (/DELETE|PURGE|REJECT/i.test(a)) return { bg: '#fdecea', fg: '#c0392b' };
    if (/CREATE|APPROVE|MERGE|REMEDIATE/i.test(a)) return { bg: '#eafaf1', fg: '#1e8449' };
    if (/UPDATE|SUBMIT/i.test(a)) return { bg: '#fef9e7', fg: '#b7791f' };
    return { bg: '#eef2f7', fg: '#34495e' };
  };
  const th: any = { textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', color: '#5a6b7a', textTransform: 'uppercase', borderBottom: '2px solid #e1e6eb', whiteSpace: 'nowrap' };
  const thBtn: any = { ...th, cursor: 'pointer', userSelect: 'none' };
  const td: any = { padding: '8px 10px', fontSize: '0.82rem', borderBottom: '1px solid #eef1f4' };
  const inp: any = { padding: '7px 9px', borderRadius: 6, border: '1px solid #cfd6dd', fontSize: '0.82rem', width: '100%' };
  const showing = data.items.length ? `${offset + 1}–${offset + data.items.length}` : '0';

  return (
    <div className="card">
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>📝 Change Log</h2>
        <p style={{ color: '#666', margin: '6px 0 0', fontSize: '0.85rem', maxWidth: 760 }}>
          Every create / edit / delete / approval of a clinical entity (Allergen, Ingredient, Condition, SKU) —
          <strong> who</strong>, <strong>when</strong>, and the <strong>before → after</strong>. Expand a row to see exactly what changed.
          (For the safety-<em>checks</em> the engine ran, see <strong>Safety-Check Forensics</strong>.)
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, background: '#f7f9fb', padding: 14, borderRadius: 10, marginBottom: 14 }}>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>ENTITY</label>
          <select style={inp} value={filters.entity_type} onChange={e => setFilters({ ...filters, entity_type: e.target.value })}>
            {['', 'Allergen', 'Ingredient', 'Condition', 'SKU', 'Member', 'ServiceAccount'].map(v => <option key={v} value={v}>{v || 'All entities'}</option>)}
          </select></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>WHO (user)</label>
          <input style={inp} value={filters.actor} onChange={e => setFilters({ ...filters, actor: e.target.value })} placeholder="e.g. admin, system:*" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>ACTION</label>
          <input style={inp} value={filters.action} onChange={e => setFilters({ ...filters, action: e.target.value })} placeholder="e.g. UPDATE, DELETE" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>SEARCH (target/user)</label>
          <input style={inp} value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="entity id / user / action" /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>FROM (UTC)</label>
          <input style={inp} type="datetime-local" value={filters.since} onChange={e => setFilters({ ...filters, since: e.target.value })} /></div>
        <div><label style={{ fontSize: '0.7rem', color: '#5a6b7a', fontWeight: 600 }}>TO (UTC)</label>
          <input style={inp} type="datetime-local" value={filters.until} onChange={e => setFilters({ ...filters, until: e.target.value })} /></div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button onClick={apply} style={{ flex: 1 }}>Search</button>
          <button onClick={reset} className="btn-secondary">Clear</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: '0.82rem', color: '#5a6b7a' }}>
        <span>{loading ? 'Searching…' : <>Showing <strong>{showing}</strong> of <strong>{data.total.toLocaleString()}</strong> change(s)</>}</span>
        <span style={{ display: 'flex', gap: 10 }}>
          <button className="btn-secondary" disabled={offset === 0 || loading} onClick={() => page(-1)}>‹ Prev</button>
          <button className="btn-secondary" disabled={offset + data.items.length >= data.total || loading} onClick={() => page(1)}>Next ›</button>
        </span>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #eef1f4', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead><tr style={{ background: '#fafbfc' }}>
            <th style={th}></th>
            <th style={thBtn} onClick={() => sortOn('timestamp')}>Timestamp (UTC){arrow('timestamp')}</th>
            <th style={thBtn} onClick={() => sortOn('user_id')}>Who{arrow('user_id')}</th>
            <th style={thBtn} onClick={() => sortOn('action')}>Action{arrow('action')}</th>
            <th style={thBtn} onClick={() => sortOn('entity_type')}>Entity{arrow('entity_type')}</th>
            <th style={thBtn} onClick={() => sortOn('entity_id')}>Target{arrow('entity_id')}</th>
          </tr></thead>
          <tbody>
            {data.items.map((r: any) => {
              const open = expanded === r.id; const c = actionColor(r.action || '');
              const sys = String(r.user_id || '').startsWith('system');
              return (
                <React.Fragment key={r.id}>
                  <tr onClick={() => setExpanded(open ? null : r.id)} style={{ cursor: 'pointer', background: open ? '#f4f8fc' : '#fff' }}>
                    <td style={{ ...td, color: '#9aa7b2' }}>{open ? '▾' : '▸'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.ts_human}</td>
                    <td style={td}><span className="badge" style={{ fontSize: '0.72rem', background: sys ? '#eef2f7' : '#e8f4ec', color: sys ? '#5a6b7a' : '#1e8449' }}>{sys ? '⚙️ ' : '👤 '}{r.user_id}</span></td>
                    <td style={td}><span style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.action}</span></td>
                    <td style={td}>{r.entity_type || '—'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.76rem' }}>{r.entity_id || '—'}</td>
                  </tr>
                  {open && (
                    <tr><td colSpan={6} style={{ padding: 0, background: '#f4f8fc', borderBottom: '2px solid #dde6ef' }}>
                      <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#5a6b7a', fontWeight: 700, marginBottom: 4 }}>BEFORE</div>
                          <pre style={{ background: '#0f1b2b', color: '#ffd7d0', padding: 12, borderRadius: 8, fontSize: '0.72rem', maxHeight: 300, overflow: 'auto', margin: 0 }}>{r.payload_before ? JSON.stringify(r.payload_before, null, 2) : '— (creation / no prior state)'}</pre>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#5a6b7a', fontWeight: 700, marginBottom: 4 }}>AFTER</div>
                          <pre style={{ background: '#0f1b2b', color: '#c8ffd4', padding: 12, borderRadius: 8, fontSize: '0.72rem', maxHeight: 300, overflow: 'auto', margin: 0 }}>{r.payload_after ? JSON.stringify(r.payload_after, null, 2) : '— (deletion)'}</pre>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && !data.items.length && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9aa7b2' }}>No changes match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DataQualityView = () => {
  const { toast, confirm } = useUI();
  const [type, setType] = useState<'Allergen' | 'Ingredient'>('Allergen');
  const [rep, setRep] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/graph/quality-report`, { params: { type } });
      setRep(res.data);
    } catch { toast('Failed to load quality report', 'error'); }
    setLoading(false);
  }, [type, toast]);
  useEffect(() => { load(); }, [load]);

  // "NO BEEF" -> "Beef": the likely intended allergen for a negation node.
  const deNegate = (name: string) =>
    name.replace(/^\s*(no|non|not|without|avoid|sin)\s+/i, '')
        .replace(/[-\s]*free\b/i, '')
        .replace(/[_]/g, ' ').replace(/\s+/g, ' ').trim()
        .split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w).join(' ');

  const remediate = async (node: any, action: 'RELINK' | 'QUARANTINE', target?: string) => {
    const label = action === 'RELINK'
      ? `Relink ${node.usage_count} patient(s) from “${node.name}” onto allergen “${target}”, then delete the junk node?`
      : `Delete “${node.name}”? Any attached patient loses this (mis-encoded) restriction.`;
    confirm(action === 'RELINK' ? 'Relink & remediate' : 'Quarantine node', label, async () => {
      setBusy(node.id);
      try {
        await axios.post(`${API_URL}/graph/remediate-node`, { junk_id: node.id, action, target_name: target, type });
        toast('Remediated + audited', 'success');
        load();
      } catch (e: any) { toast(e.response?.data?.detail || 'Remediation failed', 'error'); }
      setBusy('');
    });
  };

  const mergeGroup = async (group: any) => {
    // canonical = the cleanest name (no underscores, Title Case, longest tie-break)
    const sorted = [...group.nodes].sort((a: any, b: any) => {
      const score = (n: any) => (n.name.includes('_') ? 0 : 2) + (/^[A-Z]/.test(n.name) ? 1 : 0);
      return score(b) - score(a) || b.name.length - a.name.length;
    });
    const canonical = sorted[0];
    const redundant = sorted.slice(1);
    confirm('Merge duplicates',
      `Merge ${redundant.map((n: any) => `“${n.name}”`).join(', ')} into “${canonical.name}”? Relationships are preserved and the merge is audited.`,
      async () => {
        setBusy(group.key);
        try {
          await axios.post(`${API_URL}/graph/de-duplication/merge`,
            { canonical_id: canonical.id, redundant_ids: redundant.map((n: any) => n.id), type });
          toast('Merged + audited', 'success');
          load();
        } catch (e: any) { toast(e.response?.data?.detail || 'Merge failed', 'error'); }
        setBusy('');
      });
  };

  if (loading || !rep) return <div className="card">Scanning taxonomy…</div>;
  const s = rep.summary;
  const card = (n: number, label: string, color: string) => (
    <div style={{ flex: 1, minWidth: 120, background: '#fff', border: '1px solid #eef1f4', borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color }}>{n}</div>
      <div style={{ fontSize: '0.75rem', color: '#5a6b7a' }}>{label}</div>
    </div>
  );
  const usageLabel = type === 'Allergen' ? 'patient(s)' : 'product(s)';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0 }}>🧹 Data Quality — {rep.total} {type} nodes</h2>
          <p style={{ color: '#666', margin: '6px 0 0', fontSize: '0.85rem', maxWidth: 760 }}>
            Duplicates, negation phrases, compounds and malformed names. Fuzzy near-duplicates are
            <strong> review-only</strong> — they include genuinely-distinct allergens, so they are never auto-merged.
          </p>
        </div>
        <select value={type} onChange={e => setType(e.target.value as any)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cfd6dd', fontWeight: 600 }}>
          <option value="Allergen">Allergens</option>
          <option value="Ingredient">Ingredients</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        {card(s.at_risk_usage, `⚠️ ${usageLabel} at risk`, '#c0392b')}
        {card(s.negation, 'negation nodes', '#c0392b')}
        {card(s.compound, 'compound nodes', '#b7791f')}
        {card(s.symbols, 'symbol-malformed', '#b7791f')}
        {card(s.exact_duplicate_groups, 'exact dup groups', '#1e8449')}
        {card(s.fuzzy_pairs, 'fuzzy pairs (review)', '#5a6b7a')}
      </div>

      {/* CLINICAL RISK — negation nodes with patients attached */}
      {rep.negation.length > 0 && (
        <section style={{ marginBottom: 22 }}>
          <h3 style={{ color: '#c0392b' }}>🚨 Restrictions encoded as junk (silently inactive)</h3>
          <p style={{ fontSize: '0.82rem', color: '#666', marginTop: 0 }}>
            A patient linked to “NO BEEF” is <strong>not</strong> matched against Beef products — the restriction does nothing.
            Relink them onto the real allergen, or quarantine if it isn’t an allergy.
          </p>
          <div style={{ overflowX: 'auto', border: '1px solid #eef1f4', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr style={{ background: '#fafbfc' }}>
                {['Junk node', usageLabel, 'Suggested allergen', 'Action'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', color: '#5a6b7a', textTransform: 'uppercase', borderBottom: '2px solid #e1e6eb' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {rep.negation.map((n: any) => {
                  const target = deNegate(n.name);
                  return (
                    <tr key={n.id} style={{ background: n.usage_count > 0 ? '#fef6f5' : '#fff' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #eef1f4' }}><strong>{n.name}</strong></td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #eef1f4', fontWeight: 700, color: n.usage_count > 0 ? '#c0392b' : '#9aa7b2' }}>{n.usage_count}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #eef1f4' }}>{target || <em style={{ color: '#9aa7b2' }}>—</em>}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #eef1f4', whiteSpace: 'nowrap' }}>
                        {target && <button disabled={busy === n.id} onClick={() => remediate(n, 'RELINK', target)} style={{ fontSize: '0.75rem', padding: '4px 10px', marginRight: 6 }}>Relink → {target}</button>}
                        <button disabled={busy === n.id} onClick={() => remediate(n, 'QUARANTINE')} className="btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>Quarantine</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* SAFE MERGES — exact duplicate groups */}
      {rep.exact_duplicates.length > 0 && (
        <section style={{ marginBottom: 22 }}>
          <h3 style={{ color: '#1e8449' }}>✅ Safe merges — exact duplicates</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rep.exact_duplicates.map((g: any) => (
              <div key={g.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid #eef1f4', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {g.nodes.map((n: any) => <span key={n.id} className="badge" style={{ background: '#eee', color: '#333' }}>{n.name}</span>)}
                </div>
                <button disabled={busy === g.key} onClick={() => mergeGroup(g)} style={{ fontSize: '0.75rem', padding: '4px 12px', whiteSpace: 'nowrap' }}>Merge {g.nodes.length}</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* REVIEW-ONLY sections */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
        {[['Compound nodes', rep.compound], ['Symbol-malformed', rep.symbols]].map(([title, list]: any) => (
          <section key={title}>
            <h4 style={{ marginBottom: 8 }}>{title} <span style={{ color: '#9aa7b2', fontWeight: 400 }}>({list.length})</span></h4>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eef1f4', borderRadius: 8 }}>
              {list.map((n: any) => (
                <div key={n.id} style={{ padding: '6px 10px', borderBottom: '1px solid #f2f4f6', fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{n.name}</span>{n.usage_count > 0 && <span style={{ color: '#c0392b', fontSize: '0.72rem' }}>{n.usage_count} {usageLabel}</span>}
                </div>
              ))}
              {list.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#9aa7b2', fontSize: '0.8rem' }}>None 🎉</div>}
            </div>
          </section>
        ))}
        <section>
          <h4 style={{ marginBottom: 8 }}>Fuzzy near-duplicates <span style={{ color: '#9aa7b2', fontWeight: 400 }}>({rep.fuzzy_pairs.length}) — review manually</span></h4>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eef1f4', borderRadius: 8 }}>
            {rep.fuzzy_pairs.map((p: any, i: number) => (
              <div key={i} style={{ padding: '6px 10px', borderBottom: '1px solid #f2f4f6', fontSize: '0.8rem' }}>{p.a} <span style={{ color: '#9aa7b2' }}>~</span> {p.b}</div>
            ))}
          </div>
          <p style={{ fontSize: '0.72rem', color: '#9aa7b2', marginTop: 6 }}>These include distinct allergens (Beef~Beets). Merge only real duplicates, via Knowledge Graph tools.</p>
        </section>
      </div>
    </div>
  );
};

const DataLifecycleView = () => {
  const { toast, confirm, prompt } = useUI();
  const [purgeId, setPurgeId] = useState('');
  const [retentionYears, setRetentionYears] = useState(7);
  const [loading, setLoading] = useState(false);

  const handleManualPurge = () => {
    if (!purgeId) return;
    // Guarded: the user must type the exact phrase to enable the destructive action.
    prompt(`Permanently delete patient ${purgeId}?`,
      `This erases ALL health records for this patient from every store. It cannot be undone.\n\nType PERMANENT PURGE to confirm:`,
      async () => {
        setLoading(true);
        try {
          await axios.delete(`${API_URL}/members/${encodeURIComponent(purgeId)}/purge`);
          toast("Patient PHI permanently purged.", "success");
          setPurgeId('');
        } catch { toast("Purge failed. Verify the ID or your permissions.", "error"); }
        setLoading(false);
      },
      { placeholder: 'PERMANENT PURGE', requireExact: 'PERMANENT PURGE', confirmText: 'Purge PHI', danger: true });
  };

  const handleBulkPurge = () => {
    confirm('Bulk retention purge',
      `This permanently deletes ALL patient records older than ${retentionYears} years. It cannot be undone.`,
      async () => {
        setLoading(true);
        try {
          const res = await axios.post(`${API_URL}/system/maintenance/purge-expired`, null, { params: { years: retentionYears } });
          toast(`Purge complete — ${res.data.purged_count} record(s) removed.`, "success");
        } catch { toast("Bulk purge failed.", "error"); }
        setLoading(false);
      },
      { danger: true, confirmText: 'Purge records' });
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <h1>♻️ Data Lifecycle & HIPAA Compliance</h1>
      <p style={{color:'#666'}}>Manage long-term data retention and the 'Right to be Forgotten'. <strong>Caution:</strong> Deletions here are permanent and bypass the clinical review queue.</p>

      <div className="stats-grid">
         <div className="card" style={{borderTop:'4px solid var(--danger)'}}>
            <h3>Surgical PHI Purge (Manual Request)</h3>
            <p style={{fontSize:'0.85rem'}}>Permanently delete a specific patient profile and all its clinical links (Allergies, Conditions, Prefs).</p>
            <div className="form-group" style={{marginTop:'20px'}}>
               <label>Patient ID (Member ID)</label>
               <div style={{display:'flex', gap:'10px'}}>
                  <input placeholder="e.g. 110010031884" value={purgeId} onChange={e=>setPurgeId(e.target.value)} title="The patient's Member ID. Everything about them (allergies, conditions, preferences) will be permanently erased." />
                  <button onClick={handleManualPurge} disabled={loading || !purgeId} style={{background:'var(--danger)', border:'none'}} title="PERMANENTLY delete this patient and all their clinical data — for a legal 'right to be forgotten' request. This cannot be undone; it is double-confirmed and audited.">Purge PHI</button>
               </div>
            </div>
         </div>

         <div className="card" style={{borderTop:'4px solid var(--pfb-sidebar)'}}>
            <h3>Bulk Retention Policy</h3>
            <p style={{fontSize:'0.85rem'}}>Automated cleanup of legacy records based on the HIPAA 7-year retention rule.</p>
            <div className="form-group" style={{marginTop:'20px'}}>
               <label>Records older than (Years)</label>
               <div style={{display:'flex', gap:'10px'}}>
                  <input type="number" value={retentionYears} onChange={e=>setRetentionYears(parseInt(e.target.value))} style={{width:'80px'}} title="Age threshold in years. Records older than this are eligible for cleanup. HIPAA typically requires keeping records 7 years." />
                  <button className="btn-secondary" onClick={handleBulkPurge} disabled={loading} style={{borderColor:'var(--pfb-sidebar)', color:'var(--pfb-sidebar)'}} title="Delete records older than the threshold above, in bulk. Irreversible and audited — use with care.">Run Retention Cleanup</button>
               </div>
            </div>
         </div>
      </div>

      <div className="card" style={{background:'#fcfdfc', border:'1px solid #eee'}}>
         <h4>⚖️ Legal Note</h4>
         <p style={{fontSize:'0.75rem', color:'#888'}}>Pursuant to <strong>HIPAA § 164.312</strong>, all purge actions are recorded in the immutable audit trail (Action: <code>PURGE_PHI</code>). The audit entry will retain the ID of the purged entity for forensic purposes but will NOT contain any clinical data or patient names.</p>
      </div>
    </div>
  );
};

const HelpView = () => {
  const { toast } = useUI();
  const [helpData, setHelpHelpData] = useState<any>(null);

  useEffect(() => {
    axios.get(`${API_URL}/help`).then(res => setHelpHelpData(res.data)).catch(() => toast("Failed to load system help", "error"));
  }, [toast]);

  if (!helpData) return <div>Loading help...</div>;

  return (
    <div style={{maxWidth:'800px', margin:'0 auto', display:'flex', flexDirection:'column', gap:'30px'}}>
      <div className="card" style={{borderLeft:'8px solid var(--pfb-green)'}}>
        <h1>📖 {helpData.title}</h1>
        <p style={{fontSize:'1.1rem', color:'#666'}}>{helpData.description}</p>
        <div className="badge info">Version {helpData.version}</div>
      </div>

      <div className="card">
        <h3>🛡️ Selectable Safety Matching</h3>
        <p>The system allows granular control over which clinical rules are applied during safety checks.</p>
        <div style={{background:'#f8f9fa', padding:'20px', borderRadius:'8px', marginTop:'15px'}}>
          <h4 style={{marginTop:0}}>API Parameters:</h4>
          <ul style={{lineHeight:'1.8'}}>
            {helpData.selectable_matching.parameters.map((p:string, i:number) => <li key={i}><code>{p}</code></li>)}
          </ul>
          <div style={{marginTop:'15px', padding:'10px', background:'var(--pfb-green-light)', borderLeft:'4px solid var(--pfb-green)', fontSize:'0.9rem'}}>
            <strong>Rule Precedence:</strong> {helpData.selectable_matching.precedence}
          </div>
        </div>
      </div>

      <div className="card">
        <h3>🔌 Main API Endpoints</h3>
        <div style={{display:'grid', gridTemplateColumns:'1fr 2fr', gap:'15px', marginTop:'15px'}}>
          {Object.entries(helpData.endpoints).map(([k, v]: any) => (
            <React.Fragment key={k}>
              <div style={{fontWeight:'bold'}}><code>{k}</code></div>
              <div style={{color:'#555'}}>{v}</div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{textAlign:'center', color:'#888', fontSize:'0.9rem', marginTop:'20px'}}>
        {helpData.support}
      </div>
    </div>
  );
};

const Settings = () => {
  const [config, setConfig] = useState<any>({
    AI_PROVIDER: 'AZURE',
    AZURE_OPENAI_API_KEY: '',
    AZURE_OPENAI_ENDPOINT: '',
    AZURE_OPENAI_VERSION: '2024-05-01-preview',
    AZURE_OPENAI_DEPLOYMENT_CLINICAL: 'gpt-4o',
    AZURE_OPENAI_DEPLOYMENT_FAST: 'gpt-4o',
    risk_threshold: 'Strict',
    audit_retention: '365 days',
    db_host: 'pfb-db',
    db_port: '5432',
    db_user: 'pfb_admin',
    db_password: '',
    db_name: 'pfb_allergens',
    redis_url: 'redis://pfb-redis:6379',
    neo4j_url: 'bolt://pfb-neo4j:7687',
    neo4j_user: 'neo4j',
    neo4j_password: '',
    mfa_enforced: 'No',
    localization: 'EN-US',
    SNOWFLAKE_ACCOUNT: '',
    SNOWFLAKE_USER: '',
    SNOWFLAKE_PRIVATE_KEY: '',
    SNOWFLAKE_KEY_PASSPHRASE: '',
    SNOWFLAKE_DB: '',
    SNOWFLAKE_SCHEMA: '',
    SNOWFLAKE_WH: '',
    SNOWFLAKE_ROLE: '',
    SNOWFLAKE_MEMBER_QUERY: 'SELECT ID, NAME FROM MEMBERS',
    AINCIDENT_BASE_URL: '',
    AINCIDENT_API_KEY: ''
  });
  const [loading, setLoading] = useState(true);
  const [deployments, setDeployments] = useState<string[]>([]);
  const [discovering, setDiscovering] = useState(false);
  const [aincSeverity, setAincSeverity] = useState('info');
  const [aincBusy, setAincBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/system/settings`).then(res => {
      const data = res.data;
      setConfig((prev: any) => ({...prev, ...data}));
      setLoading(false);
      // Auto-discover if we have credentials
      if (data.AZURE_OPENAI_ENDPOINT && data.AZURE_OPENAI_API_KEY) {
        fetchDeployments(data.AZURE_OPENAI_ENDPOINT, data.AZURE_OPENAI_API_KEY);
      }
    }).catch(() => setLoading(false));
  }, []);

  const fetchDeployments = async (endpoint: string, key: string) => {
    if (!endpoint || !key) return;
    setDiscovering(true);
    try {
      const res = await axios.get(`${API_URL}/ai/azure/deployments`, { params: { endpoint, api_key: key } });
      setDeployments(res.data.deployments || []);
    } catch { console.error("Auto-discovery failed"); }
    setDiscovering(false);
  };

  const handleDiscovery = () => fetchDeployments(config.AZURE_OPENAI_ENDPOINT, config.AZURE_OPENAI_API_KEY);

  const saveSettings = async () => {
    try {
      await axios.post(`${API_URL}/system/settings`, config);
      alert('Global settings saved successfully.');
    } catch { alert('Failed to save settings.'); }
  };

  const testAiConnection = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai/test-connection`);
      if (res.data.status === 'success') {
        alert(`✅ Connection Successful!\nProvider: ${res.data.provider}\nResponse: ${res.data.response.response || JSON.stringify(res.data.response)}`);
      } else {
        throw new Error(res.data.message || "Unknown error");
      }
    } catch (err: any) {
      alert(`❌ Connection Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const sendAincTest = async () => {
    setAincBusy(true);
    try {
      await axios.post(`${API_URL}/system/settings`, config);   // persist current fields first
      const res = await axios.post(`${API_URL}/system/aincident/test`, null, { params: { severity: aincSeverity } });
      const d = res.data;
      if (!d.configured) alert(`⚠️ ${d.detail || 'Incident Manager not configured. Save the Base URL + API Key first.'}`);
      else if (d.sent) alert(`✅ Test alert sent to team "${d.service_key}" (severity: ${d.severity}, test mode — no one is paged).\nincident_id: ${d.incident_id || '—'}   deduped: ${d.deduped}`);
      else if (d.config_error) alert(`❌ service_key "${d.service_key}" is unknown upstream (404) — the alert was dropped. Fix the routing table.`);
      else alert(`❌ Not sent: ${d.reason || 'unknown error'}`);
    } catch (err: any) { alert(`❌ Test failed: ${err.response?.data?.detail || err.message}`); }
    setAincBusy(false);
  };

  const validateAinc = async () => {
    setAincBusy(true);
    try {
      await axios.post(`${API_URL}/system/settings`, config);   // persist current fields first
      const res = await axios.get(`${API_URL}/system/aincident/services`);
      const d = res.data;
      if (!d.configured) alert('⚠️ Not configured. Save the Base URL + API Key first.');
      else if (!d.reachable) alert('❌ Could not reach the incident manager (/v1/services).');
      else if (d.ok) alert(`✅ Recipients valid. ${d.services.length} teams available:\n${d.services.join(', ')}`);
      else alert(`❌ Routing points at unknown team(s): ${d.missing_service_keys.join(', ')}\nAlerts to them would be silently dropped — fix the routing table.`);
    } catch (err: any) { alert(`❌ Validation failed: ${err.response?.data?.detail || err.message}`); }
    setAincBusy(false);
  };

  if (loading) return <div className="card">Loading settings...</div>;

  return (
    <div className="card">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #eee', paddingBottom:'15px', marginBottom:'30px'}}>
        <h2 style={{margin:0}}>System & AI Governance Settings</h2>
        <div style={{display:'flex', gap:'10px'}}>
          <button className="btn-secondary" onClick={testAiConnection} title="Check that the AI provider keys entered here actually work, before saving.">🧪 Test AI Connection</button>
          <button onClick={saveSettings} style={{padding:'10px 30px'}} title="Save these system-wide settings. Changes (e.g. AI keys, thresholds) take effect across the whole platform.">💾 Save Configuration</button>
        </div>
      </div>

      <div className="stats-grid" style={{gridTemplateColumns:'1fr 1fr'}}>
        {/* COLUMN 1: AI INTELLIGENCE */}
        <div className="card" style={{borderTop:'4px solid var(--pfb-sidebar)'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
             <h3 style={{margin:0}}>Intelligence Provider</h3>
             <select 
               disabled
               value={'AZURE'} 
               style={{background: '#f0f7ff', border: '1px solid #ccc', padding: '5px 10px', borderRadius:'4px', fontWeight:'bold'}}
             >
               <option value="AZURE">Microsoft Azure OpenAI (HIPAA Compliant)</option>
             </select>
          </div>

          <div style={{animation: 'fadeIn 0.3s'}}>
            <div className="form-group">
              <label>Azure API Key</label>
              <input type="password" value={config.AZURE_OPENAI_API_KEY || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_API_KEY: e.target.value})} placeholder="Leave blank to use Managed Identity" />
            </div>
            <div className="form-group">
              <label style={{display:'flex', justifyContent:'space-between'}}>
                Azure Endpoint
                <button 
                  className="btn-secondary" 
                  style={{padding:'2px 8px', fontSize:'0.7rem', height:'auto'}}
                  onClick={handleDiscovery}
                  disabled={discovering}
                >
                  {discovering ? '🔍 Discovering...' : '🔄 Discover Deployments'}
                </button>
              </label>
              <input value={config.AZURE_OPENAI_ENDPOINT || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_ENDPOINT: e.target.value})} placeholder="https://your-resource.openai.azure.com/" />
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
              <div className="form-group">
                <label>Clinical Deployment</label>
                {deployments.length > 0 ? (
                  <select value={config.AZURE_OPENAI_DEPLOYMENT_CLINICAL || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_DEPLOYMENT_CLINICAL: e.target.value})}>
                    <option value="">-- Select Deployment --</option>
                    {deployments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : (
                  <input value={config.AZURE_OPENAI_DEPLOYMENT_CLINICAL || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_DEPLOYMENT_CLINICAL: e.target.value})} placeholder="Enter name or click Discover" />
                )}
              </div>
              <div className="form-group">
                <label>Fast Deployment</label>
                {deployments.length > 0 ? (
                  <select value={config.AZURE_OPENAI_DEPLOYMENT_FAST || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_DEPLOYMENT_FAST: e.target.value})}>
                    <option value="">-- Select Deployment --</option>
                    {deployments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : (
                  <input value={config.AZURE_OPENAI_DEPLOYMENT_FAST || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_DEPLOYMENT_FAST: e.target.value})} placeholder="Enter name or click Discover" />
                )}
              </div>
            </div>
            <div className="form-group">
              <label>API Version</label>
              <input value={config.AZURE_OPENAI_VERSION || ''} onChange={e=>setConfig({...config, AZURE_OPENAI_VERSION: e.target.value})} placeholder="2024-05-01-preview" />
            </div>
            <div style={{background: '#f0f7ff', padding: '10px', borderRadius: '8px', border: '1px solid #cee0ff', fontSize: '0.8rem'}}>
               🛡️ <strong>HIPAA Compliance:</strong> All data stays within Microsoft MSFT-PFB BAA-covered environment.
            </div>
          </div>
          <div className="form-group" style={{marginTop:'15px'}}><label>Localization</label><select value={config.localization} onChange={e=>setConfig({...config, localization:e.target.value})}><option>EN-US</option><option>ES-ES</option><option>PT-BR</option></select></div>
        </div>

        {/* COLUMN 2: DATA & INFRASTRUCTURE */}
        <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
          <div className="card" style={{borderTop:'4px solid #2196f3'}}>
            <h3 style={{margin:'0 0 20px'}}>❄️ Snowflake Data Connector</h3>
            <div className="form-group">
              <label>Snowflake Account (locator)</label>
              <input value={config.SNOWFLAKE_ACCOUNT || ''} onChange={e=>setConfig({...config, SNOWFLAKE_ACCOUNT: e.target.value})} placeholder="xy12345.us-east-1" />
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
              <div className="form-group">
                <label>User (Service Account)</label>
                <input value={config.SNOWFLAKE_USER || ''} onChange={e=>setConfig({...config, SNOWFLAKE_USER: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Key Passphrase (optional)</label>
                <input type="password" value={config.SNOWFLAKE_KEY_PASSPHRASE || ''} onChange={e=>setConfig({...config, SNOWFLAKE_KEY_PASSPHRASE: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label>Private Key (PEM format)<HelpTip text="Stored encrypted on the server and never sent back to the browser. When already set it shows •••• — leave it as-is to keep the current key, or paste a new one to replace it." /></label>
              <textarea
                value={config.SNOWFLAKE_PRIVATE_KEY || ''}
                onChange={e=>setConfig({...config, SNOWFLAKE_PRIVATE_KEY: e.target.value})}
                placeholder="-----BEGIN RSA PRIVATE KEY----- ..."
                style={{width:'100%', height:'100px', fontFamily:'monospace', fontSize:'0.7rem'}}
              />
              <small style={{color:'#666'}}>🔒 Never displayed in clear text. Shows •••• when a key is stored — replace only by pasting a new one.</small>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
              <div className="form-group">
                <label>Database</label>
                <input value={config.SNOWFLAKE_DB || ''} onChange={e=>setConfig({...config, SNOWFLAKE_DB: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Role</label>
                <input value={config.SNOWFLAKE_ROLE || ''} onChange={e=>setConfig({...config, SNOWFLAKE_ROLE: e.target.value})} placeholder="e.g. ACCOUNTADMIN" />
              </div>
            </div>
            <div className="form-group">
              <label>Warehouse</label>
              <input value={config.SNOWFLAKE_WH || ''} onChange={e=>setConfig({...config, SNOWFLAKE_WH: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Member Extraction Query</label>
              <textarea 
                value={config.SNOWFLAKE_MEMBER_QUERY || ''} 
                onChange={e=>setConfig({...config, SNOWFLAKE_MEMBER_QUERY: e.target.value})}
                style={{width:'100%', height:'120px', fontFamily:'monospace', fontSize:'0.8rem'}}
                placeholder="SELECT ID, EXT_ID, NAME, ALLERGIES, CONDITIONS FROM ..."
              />
              <p style={{fontSize:'0.65rem', color:'#666', marginTop:'5px'}}>
                * Expected columns: <strong>ID</strong> (internal), <strong>EXT_ID</strong> (external), <strong>NAME</strong>. <br/>
                * Optional: <strong>ALLERGIES</strong>, <strong>CONDITIONS</strong> (comma-separated strings).
              </p>
            </div>
          </div>

          <div className="card" style={{borderTop:'4px solid var(--pfb-green)'}}>
            <h3>Infrastructure & Security Policy</h3>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
            <div className="form-group"><label>Risk Threshold</label><select value={config.risk_threshold} onChange={e=>setConfig({...config, risk_threshold:e.target.value})}><option>Strict</option><option>Standard</option></select></div>
            <div className="form-group"><label>Audit Retention</label><select value={config.audit_retention} onChange={e=>setConfig({...config, audit_retention:e.target.value})}><option>365 days</option><option>7 years</option></select></div>
          </div>
          
          <h4 style={{borderBottom:'1px solid #eee', paddingBottom:'5px', marginTop:'20px'}}>Relational Persistence (Postgres)</h4>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:'10px'}}>
            <div className="form-group"><label>Host</label><input value={config.db_host} onChange={e=>setConfig({...config, db_host:e.target.value})} /></div>
            <div className="form-group"><label>Port</label><input value={config.db_port} onChange={e=>setConfig({...config, db_port:e.target.value})} /></div>
          </div>
          
          <h4 style={{borderBottom:'1px solid #eee', paddingBottom:'5px', marginTop:'20px'}}>Knowledge Graph & Cache</h4>
          <div className="form-group"><label>Neo4j Bolt URL</label><input value={config.neo4j_url} onChange={e=>setConfig({...config, neo4j_url:e.target.value})} /></div>
          <div className="form-group"><label>Redis URL</label><input value={config.redis_url} onChange={e=>setConfig({...config, redis_url:e.target.value})} /></div>
          
          <div className="form-group" style={{marginTop:'20px'}}><label>MFA Enforced</label><select value={config.mfa_enforced} onChange={e=>setConfig({...config, mfa_enforced:e.target.value})}><option>No</option><option>Yes (All Users)</option></select></div>
        </div>

          {/* SAFEGUARD ASSISTANT */}
          <AssistantSettingsCard />
          <OpsCenterSettingsCard />

          {/* BATCH VALIDATION PROMPTS (admin-editable, no rebuild) */}
          <div className="card" style={{ borderTop: '4px solid #0f766e' }}>
            <h3 style={{ margin: 0 }}>🧪 Batch validation prompts (AI)</h3>
            <p style={{ fontSize: '0.75rem', color: '#666' }}>
              Prompts the AI uses to review uploaded batch files (input), generated results (output) and to strip PHI
              from delivery notes in Operational Checks. Leave blank to use the built-in defaults. Runtime placeholders:
              <code>__HEADERS__ __SAMPLE__ __HINT__</code> (input), <code>__EXPECTED_COLS__ __TOTAL__ __WEIRD__ __SAMPLE__</code> (output)
              and <code>__NOTES__</code> (note cleaning) — keep them if you edit.
            </p>
            <div className="form-group"><label>Input-file review prompt</label>
              <textarea rows={5} value={config.BATCH_INPUT_VALIDATION_PROMPT || ''} onChange={e => setConfig({ ...config, BATCH_INPUT_VALIDATION_PROMPT: e.target.value })} placeholder="(using built-in default)" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} /></div>
            <div className="form-group"><label>Output review prompt</label>
              <textarea rows={5} value={config.BATCH_OUTPUT_VALIDATION_PROMPT || ''} onChange={e => setConfig({ ...config, BATCH_OUTPUT_VALIDATION_PROMPT: e.target.value })} placeholder="(using built-in default)" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} /></div>
            <div className="form-group"><label>Delivery-note cleaning prompt (CLEAN_PACKAGE_NOTES)</label>
              <textarea rows={5} value={config.BATCH_CLEAN_NOTES_PROMPT || ''} onChange={e => setConfig({ ...config, BATCH_CLEAN_NOTES_PROMPT: e.target.value })} placeholder="(using built-in default)" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
              <small style={{ color: '#666' }}>Decides what a warehouse/courier may see. Whatever this prompt returns still passes a
                non-editable filter that drops emails, phone numbers, long digit runs, URLs, street addresses and unit numbers.</small></div>
            <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '14px 0' }} />
            <h4 style={{ margin: '0 0 4px' }}>🗄️ Encrypted 7-year archive (Snowflake)</h4>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
              <input type="checkbox" checked={String(config.SAFETY_ARCHIVE_ENABLED || '').toLowerCase() === 'true'}
                onChange={e => setConfig({ ...config, SAFETY_ARCHIVE_ENABLED: e.target.checked ? 'true' : 'false' })} />
              Periodically archive safety-check results to Snowflake, PHI encrypted (needs Snowflake configured above)
            </label>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label>Keep full detail in the database for (days)</label>
              <input type="number" min={7} value={config.SAFETY_HOT_WINDOW_DAYS || ''} placeholder="90"
                onChange={e => setConfig({ ...config, SAFETY_HOT_WINDOW_DAYS: e.target.value })} />
              <small style={{ color: '#666' }}>
                After this many days, a row that has been archived frees its request/response payload locally.
                Who asked, when, with which options, under which engine version, the decision and the row hash
                stay queryable forever; the full payload is recovered from the archive with “Reveal”, which is audited.
                Minimum 7 days. Nothing is ever freed unless archiving is on and the row is confirmed archived.
              </small>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#888', margin: '4px 0 0' }}>
              The clinical payload is encrypted with SafeGuard's key — unreadable in Snowflake without a tracked reveal (Forensics → Reveal archived record).
            </p>
            <button className="btn-primary" style={{ marginTop: 10 }} onClick={async () => { try { await axios.post(`${API_URL}/system/settings`, config); alert('✓ Saved'); } catch { alert('Save failed'); } }}>Save</button>
          </div>

          {/* INCIDENT MANAGER (AIncident) */}
          <div className="card" style={{borderTop:'4px solid #d9534f'}}>
            <h3 style={{margin:0}}>🚨 Incident Manager (AIncident)</h3>
            <p style={{fontSize:'0.75rem', color:'#666'}}>
              Where SafeGuard sends operational alerts. Alerts route to the responsible team by condition (config).
              HIPAA: messages carry technical conditions only — never patient data.
            </p>
            <div className="form-group">
              <label>Base URL</label>
              <input value={config.AINCIDENT_BASE_URL || ''} onChange={e=>setConfig({...config, AINCIDENT_BASE_URL: e.target.value})} placeholder="https://incidents.projectfoodbox.org" />
            </div>
            <div className="form-group">
              <label>API Key (X-API-Key)</label>
              <input type="password" value={config.AINCIDENT_API_KEY || ''} onChange={e=>setConfig({...config, AINCIDENT_API_KEY: e.target.value})} placeholder="Leave blank to keep current" />
            </div>
            <p style={{fontSize:'0.7rem', color:'#888', marginTop:'-4px'}}>💾 Save first — Validate and Test read the saved configuration.</p>
            <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap', marginTop:'10px'}}>
              <button className="btn-secondary" onClick={validateAinc} disabled={aincBusy} title="Fetch the valid recipient teams and check the routing table points only at teams that exist.">🔎 Validate recipients</button>
              <span style={{flex:1}} />
              <label style={{fontSize:'0.8rem'}}>Test severity</label>
              <select value={aincSeverity} onChange={e=>setAincSeverity(e.target.value)} style={{padding:'6px'}}>
                <option value="info">info</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
              <button onClick={sendAincTest} disabled={aincBusy} title="Send a test alert (test mode: escalates but pages no one).">{aincBusy ? '…' : '📨 Send Test'}</button>
            </div>
          </div>
      </div>
    </div>
  </div>
);
};

const MfaSetup = ({ onComplete }: { onComplete: () => void }) => {
  const { toast, confirm } = useUI();
  const [setup, setSetup] = useState<any>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<any>(null);
  const [qrUrl, setQrUrl] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/me`).then(res => setCurrentStatus(res.data)).catch(() => setCurrentStatus({ mfa_enabled: false }));
  }, []);

  // Generate the QR locally (in the browser) so the TOTP secret NEVER leaves the app
  // — it must not be sent to any external QR-image service.
  useEffect(() => {
    if (setup?.otpauth_uri) {
      QRCode.toDataURL(setup.otpauth_uri, { width: 200, margin: 1 })
        .then(setQrUrl).catch(() => setQrUrl(''));
    } else setQrUrl('');
  }, [setup]);

  const startSetup = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.get(`${API_URL}/me/mfa/setup`);
      if (res.data.status === 'already_enabled') { toast('MFA is already active on this account.', 'info'); setCurrentStatus({ ...currentStatus, mfa_enabled: true }); }
      else setSetup(res.data);
    } catch { toast('Could not start MFA setup. Please try again.', 'error'); }
    setBusy(false);
  };

  const verifyAndEnable = async () => {
    if (code.length !== 6) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/me/mfa/enable`, { code });
      toast('✅ Two-factor authentication is now active on your account.', 'success');
      onComplete();
    } catch { setError('That code didn’t match. Codes rotate every 30 seconds — check your app and try the current one.'); }
    setBusy(false);
  };

  const disableMfa = () => {
    confirm(
      'Disable two-factor authentication?',
      'This removes the extra security layer from your account. You can turn it back on any time.',
      async () => {
        try { await axios.post(`${API_URL}/me/mfa/disable`); toast('MFA has been disabled.', 'info'); onComplete(); }
        catch { toast('Failed to disable MFA.', 'error'); }
      },
      { danger: true, confirmText: 'Disable MFA' }
    );
  };

  const copySecret = () => {
    try { navigator.clipboard.writeText(setup.secret); toast('Setup key copied.', 'success'); }
    catch { toast('Copy failed — select and copy it manually.', 'error'); }
  };

  if (!currentStatus) return <div className="card" style={{maxWidth:520, margin:'20px auto'}}>Loading security status…</div>;

  const step: any = { display:'flex', gap:10, alignItems:'flex-start', marginBottom:16 };
  const num: any = { flex:'0 0 26px', width:26, height:26, borderRadius:'50%', background:'var(--pfb-sidebar)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', fontWeight:700 };

  return (
    <div className="card" style={{maxWidth: 520, margin: '20px auto'}}>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:20}}>
        <span style={{fontSize:'1.5rem'}}>🔐</span>
        <h3 style={{margin:0}}>Two-Factor Authentication (2FA)</h3>
      </div>

      {currentStatus.mfa_enabled ? (
        <div style={{textAlign:'center', padding:20, background:'var(--pfb-green-light)', borderRadius:10, border:'1px solid var(--pfb-green)'}}>
           <div style={{fontSize:'3rem', marginBottom:10}}>✅</div>
           <h4 style={{margin:0, color:'var(--pfb-green-dark)'}}>2FA is ACTIVE</h4>
           <p style={{fontSize:'0.9rem', color:'#666', marginTop:10}}>Your account asks for a code from your phone at every login.</p>
           <button onClick={disableMfa} style={{marginTop:20, background:'none', border:'none', color:'var(--danger)', textDecoration:'underline', cursor:'pointer'}}>
             Disable two-factor authentication
           </button>
        </div>
      ) : !setup ? (
        <div>
          <p style={{color:'#555'}}>Add a second step at login: your password <strong>plus</strong> a rotating code from an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, Authy…). It takes about a minute to set up.</p>
          <button onClick={startSetup} disabled={busy} style={{width:'100%', padding:15, fontSize:'1rem'}}>
            {busy ? 'Starting…' : '🚀 Set up two-factor authentication'}
          </button>
        </div>
      ) : (
        <div>
          <div style={step}>
            <div style={num}>1</div>
            <div style={{flex:1}}>
              <strong>Scan this QR code</strong> with your authenticator app.
              <div style={{textAlign:'center', margin:'12px 0'}}>
                <div style={{background:'#fff', padding:10, display:'inline-block', border:'1px solid #ddd', borderRadius:8}}>
                  {qrUrl
                    ? <img src={qrUrl} alt="2FA QR code" width={190} height={190} />
                    : <div style={{width:190, height:190, display:'flex', alignItems:'center', justifyContent:'center', color:'#9aa7b2', fontSize:'0.8rem'}}>Generating QR…</div>}
                </div>
              </div>
              <div style={{fontSize:'0.8rem', color:'#666'}}>Can’t scan? Enter this key manually:
                <div style={{display:'flex', gap:8, alignItems:'center', marginTop:6}}>
                  <code style={{background:'#f4f6f8', padding:'6px 10px', borderRadius:6, wordBreak:'break-all', flex:1}}>{setup.secret}</code>
                  <button className="btn-secondary" style={{padding:'4px 10px', fontSize:'0.75rem'}} onClick={copySecret}>Copy</button>
                </div>
              </div>
            </div>
          </div>

          <div style={step}>
            <div style={num}>2</div>
            <div style={{flex:1}}>
              <strong>Enter the 6-digit code</strong> your app shows.
              <div style={{marginTop:10}}>
                <input value={code}
                       onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                       onKeyDown={e => { if (e.key === 'Enter') verifyAndEnable(); }}
                       inputMode="numeric" autoFocus placeholder="000000"
                       style={{textAlign:'center', fontSize:'1.6rem', letterSpacing:8, width:200, padding:'8px 10px'}} />
              </div>
              {error && <p style={{color:'var(--danger)', marginTop:10, fontSize:'0.85rem'}}>{error}</p>}
            </div>
          </div>

          <button onClick={verifyAndEnable} disabled={busy || code.length !== 6} style={{width:'100%', padding:14, background:'var(--pfb-sidebar)'}}>
            {busy ? 'Verifying…' : 'Verify & turn on 2FA'}
          </button>
          <button className="btn-secondary" onClick={() => { setSetup(null); setCode(''); setError(''); }} style={{marginTop:10, width:'100%'}}>Cancel</button>
          <p style={{fontSize:'0.72rem', color:'#9aa7b2', marginTop:12, textAlign:'center'}}>Tip: if you set up 2FA before, delete the old “PFB SafeGuard” entry in your app — only this new one works now.</p>
        </div>
      )}
    </div>
  );
};

const AIAuditorView = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/sentinel/reports`);
      // Map DB schema to the view format
      const mapped = res.data.map((r: any) => {
        const data = typeof r.report_data === 'string' ? JSON.parse(r.report_data) : r.report_data;
        return {
          id: r.id,
          filename: `db_${r.id}`, // compatibility
          timestamp: r.timestamp,
          status: r.overall_status,
          findings_count: Object.keys(data).reduce((acc, cat) => acc + (data[cat].findings?.length || 0), 0),
          full_data: data
        };
      });
      setReports(mapped);
    } catch { }
  };
  useEffect(() => { fetchReports(); }, []);

  const triggerAudit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/sentinel/trigger`);
      alert("AI Auditor has been triggered. Please wait a few moments for the report to appear.");
      setTimeout(fetchReports, 5000);
    } catch { alert("Failed to trigger audit."); }
    setLoading(false);
  };

  const openReport = (report: any) => {
    // Transform full_data into the detail format
    const findings: any[] = [];
    Object.keys(report.full_data).forEach(cat => {
      const catData = report.full_data[cat];
      if (catData.findings) {
        catData.findings.forEach((f: any) => {
          findings.push({
            type: cat,
            severity: catData.status === 'CRITICAL' ? 'HIGH' : 'MEDIUM',
            description: f,
            affected_ids: [] // DB reports don't have IDs yet, just strings
          });
        });
      }
    });

    setSelectedReport({
      report_timestamp: new Date(report.timestamp).toLocaleString(),
      status: report.status,
      total_analyzed: 'System-wide',
      findings: findings
    });
  };

  return (
    <div className="card">
      <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
        <h2>AI Clinical Auditor Reports</h2>
        <button onClick={triggerAudit} disabled={loading} title="Run the AI clinical audit now instead of waiting for the daily schedule. It reviews recent clinical data and decisions and reports any issues it finds.">{loading ? 'Processing...' : '🚀 Trigger Daily Audit'}</button>
      </div>
      
      {selectedReport ? (
        <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '10px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <h3>Report: {selectedReport.report_timestamp}</h3>
            <button className="btn-secondary" onClick={()=>setSelectedReport(null)}>Back to List</button>
          </div>
          <div className={`badge ${selectedReport.status === 'CRITICAL' ? 'red' : selectedReport.status === 'WARNING' ? 'yellow' : 'green'}`} style={{fontSize: '1rem', padding: '10px 20px'}}>
            Overall Status: {selectedReport.status}
          </div>
          <p><strong>Total Events Analyzed:</strong> {selectedReport.total_analyzed}</p>
          
          <h4 style={{marginTop:'30px'}}>Findings & Anomalies</h4>
          {selectedReport.findings.length === 0 ? <p>No anomalies detected. System is within safety parameters.</p> : (
            selectedReport.findings.map((f: any, i: number) => (
              <div key={i} className="card" style={{borderLeft: `5px solid ${f.severity === 'HIGH' ? 'var(--danger)' : 'var(--pfb-orange)'}`}}>
                <div style={{display:'flex', justifyContent:'space-between'}}>
                  <strong>{f.type}</strong>
                  <span className={`badge ${f.severity === 'HIGH' ? 'red' : 'yellow'}`}>{f.severity}</span>
                </div>
                <p>{f.description}</p>
                {f.affected_ids && f.affected_ids.length > 0 && <p><small>Affected IDs: {f.affected_ids.join(', ')}</small></p>}
              </div>
            ))
          )}
        </div>
      ) : (
        <table>
          <thead><tr><th>Date</th><th>Status</th><th>Findings</th><th>Actions</th></tr></thead>
          <tbody>
            {reports.map(r => (
              <tr key={r.id}>
                <td>{new Date(r.timestamp).toLocaleString()}</td>
                <td><span className={`badge ${r.status === 'CRITICAL' ? 'red' : r.status === 'WARNING' ? 'yellow' : 'green'}`}>{r.status}</span></td>
                <td>{r.findings_count} anomalies</td>
                <td><button onClick={()=>openReport(r)}>View Details</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ClinicalExplorer = () => {
  const { toast } = useUI();
  const [mode, setMode] = useState<'patient' | 'product' | 'trace'>('patient');
  const [trace, setTrace] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Safety Match Flags
  const [chkAllergens, setCheckAllergens] = useState(true);
  const [chkConditions, setCheckConditions] = useState(true);
  const [chkPreferences, setCheckPreferences] = useState(true);

  // Trace selection state
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [selectedSku, setSelectedSku] = useState<any>(null);

  const selectTarget = async (item: any) => {
    if (!item) { setTrace(null); return; }
    setLoading(true);
    setTrace(null);
    try {
      const endpoint = mode === 'patient' ? `member/${item.id}` : `sku/${item.id}`;
      const res = await axios.get(`${API_URL}/graph/explorer/${endpoint}`);
      setTrace(res.data);
    } catch { toast("Failed to fetch traceability map.", "error"); }
    setLoading(false);
  };

  const handleTraceSelect = (type: 'patient' | 'sku', item: any) => {
    if (type === 'patient') {
      setSelectedPatient(item);
      if (item && selectedSku) runTrace(item.id, selectedSku.id);
      else setTrace(null);
    } else {
      setSelectedSku(item);
      if (selectedPatient && item) runTrace(selectedPatient.id, item.id);
      else setTrace(null);
    }
  };

  const runTrace = async (mid: string, sid: string) => {
    setLoading(true);
    try {
       const res = await axios.get(`${API_URL}/graph/explorer/trace/${mid}/${sid}`, {
         params: {
           check_allergens: chkAllergens,
           check_conditions: chkConditions,
           check_preferences: chkPreferences
         }
       });
       setTrace(res.data);
    } catch { toast("Conflict trace failed.", "error"); }
    setLoading(false);
  };

  const resetTrace = () => {
    setSelectedPatient(null);
    setSelectedSku(null);
    setTrace(null);
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div className="card">
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <h2>🛡️ Clinical Traceability Explorer</h2>
          <div className="btn-group">
            <button className={mode === 'patient' ? 'active' : ''} onClick={() => {setMode('patient'); setTrace(null);}} style={{padding:'8px 20px', borderRadius:'4px 0 0 4px', border:'1px solid #ddd', background: mode === 'patient' ? 'var(--pfb-sidebar)' : '#fff', color: mode === 'patient' ? '#fff' : '#666'}}>Patient Audit</button>
            <button className={mode === 'product' ? 'active' : ''} onClick={() => {setMode('product'); setTrace(null);}} style={{padding:'8px 20px', border:'1px solid #ddd', borderLeft:'none', background: mode === 'product' ? 'var(--pfb-sidebar)' : '#fff', color: mode === 'product' ? '#fff' : '#666'}}>Product Audit</button>
            <button className={mode === 'trace' ? 'active' : ''} onClick={() => {setMode('trace'); resetTrace();}} style={{padding:'8px 20px', borderRadius:'0 4px 4px 0', border:'1px solid #ddd', borderLeft:'none', background: mode === 'trace' ? 'var(--pfb-sidebar)' : '#fff', color: mode === 'trace' ? '#fff' : '#666'}}>Patient + Product</button>
          </div>
        </div>
        
        <div style={{position:'relative', marginTop:'20px'}}>
           <div style={{display:'grid', gridTemplateColumns: mode === 'trace' ? '1fr 1fr' : '1fr', gap:'20px'}}>
             {(mode === 'patient' || mode === 'trace') && (
               <SearchableSelector 
                 type="members" 
                 label={mode === 'trace' ? "1. Select Patient" : "Search Patient"} 
                 placeholder="Type name..." 
                 onSelect={(m: any) => {
                   if (mode === 'trace') handleTraceSelect('patient', m);
                   else selectTarget(m);
                 }} 
               />
             )}
             {(mode === 'product' || (mode === 'trace' && selectedPatient)) && (
               <SearchableSelector 
                 type="skus" 
                 label={mode === 'trace' ? "2. Select Product" : "Search Product"} 
                 placeholder="Type SKU name..." 
                 onSelect={(s: any) => {
                    if (mode === 'trace') handleTraceSelect('sku', s);
                    else selectTarget(s);
                 }} 
               />
             )}
           </div>

           <div className="form-group" style={{marginTop:'15px', background:'#f8f9fa', padding:'10px 15px', borderRadius:'8px', border:'1px solid #eee'}}>
              <div style={{display:'flex', gap:'25px', fontSize:'0.9rem'}}>
                <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                  <input type="checkbox" checked={chkAllergens} onChange={e => setCheckAllergens(e.target.checked)} />
                  <span>Check Allergens</span>
                </label>
                <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                  <input type="checkbox" checked={chkConditions} onChange={e => setCheckConditions(e.target.checked)} />
                  <span>Check Conditions</span>
                </label>
                <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                  <input type="checkbox" checked={chkPreferences} onChange={e => setCheckPreferences(e.target.checked)} />
                  <span>Check Preferences</span>
                </label>
              </div>
            </div>
        </div>
      </div>

      {loading && <p>Traversing knowledge graph clinical paths...</p>}

      {trace && mode === 'trace' && (
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', animation:'fadeIn 0.5s'}}>
           <div className="card">
              <h3>Clinical Bridge Visualization</h3>
              <div style={{height:'500px', background:'#f8f9fa', borderRadius:'8px', border:'1px solid #eee', marginTop:'15px', overflow:'hidden'}}>
                 {trace.nodes?.length > 0 ? (
                    <ForceGraph2D
                      graphData={trace}
                      height={500}
                      width={600}
                      nodeRelSize={8}
                      nodeLabel="name"
                      linkLabel="type"
                      nodeCanvasObject={(node: any, ctx, globalScale) => {
                        const label = node.name || node.biz_id;
                        const fontSize = 12/globalScale;
                        ctx.font = `${fontSize}px Sans-Serif`;
                        ctx.fillStyle = node.color;
                        ctx.beginPath(); ctx.arc(node.x, node.y, 5, 0, 2 * Math.PI, false); ctx.fill();
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = '#333';
                        ctx.fillText(label, node.x, node.y + 10);
                      }}
                      linkDirectionalArrowLength={3}
                      linkDirectionalArrowRelPos={1}
                    />
                 ) : (
                    <div style={{display:'flex', height:'100%', alignItems:'center', justifyContent:'center', color:'var(--pfb-green-dark)'}}>
                       ✅ No direct clinical risk paths found for this combination.
                    </div>
                 )}
              </div>
           </div>
           
           <div className="card">
              <h3>Conflict Details</h3>
              <div style={{marginTop:'15px'}}>
                 {trace.nodes?.length > 0 ? (
                    <div>
                       <p style={{fontSize:'0.9rem', color:'#666'}}>The graph shows a direct connection between the patient and product through the following nodes:</p>
                       <div style={{display:'flex', flexDirection:'column', gap:'10px', marginTop:'20px'}}>
                          {trace.nodes.filter((n:any) => n.label !== 'Member' && n.label !== 'SKU').map((n:any) => (
                             <div key={n.id} style={{padding:'10px', background:'#fff', border:`1px solid ${n.color}`, borderLeft:`5px solid ${n.color}`, borderRadius:'4px'}}>
                                <strong>{n.label}:</strong> {n.name} <small>({n.biz_id})</small>
                             </div>
                          ))}
                       </div>
                    </div>
                 ) : (
                    <div style={{textAlign:'center', padding:'40px'}}>
                       <div style={{fontSize:'3rem'}}>🥗</div>
                       <h4>Clinically Safe</h4>
                       <p style={{color:'#666'}}>The system analyzed all ingredients, sub-ingredients, and allergens and found no intersections with the patient's condition profile.</p>
                    </div>
                 )}
              </div>
           </div>
        </div>
      )}

      {trace && mode === 'patient' && (
        <div style={{animation:'fadeIn 0.5s'}}>
          <h3>Risk Chain for: <span style={{color:'var(--pfb-green-dark)'}}>{trace.member_name}</span></h3>
          
          <div className="stats-grid" style={{marginTop:'20px'}}>
            <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
               <div className="card" style={{borderTop:'4px solid var(--danger)'}}>
                  <h4>🚫 Mandatory Clinical Exclusions (RED)</h4>
                  <div style={{display:'flex', flexDirection:'column', gap:'15px', marginTop:'15px'}}>
                     {trace.not_suitable_allergies?.length > 0 && (
                        <div style={{padding:'10px', background:'#fff5f5', borderRadius:'8px', border:'1px solid #fed7d7'}}>
                           <strong>Due to Allergies:</strong>
                           <div className="multi-item-scroll" style={{maxHeight:'150px', marginTop:'10px'}}>
                              {trace.not_suitable_allergies.map((s:any) => <div key={s.id} style={{fontSize:'0.8rem', padding:'5px', borderBottom:'1px solid #eee'}}>📦 <strong>{s.id}</strong>: {s.name} <span className="badge red" style={{fontSize:'0.6rem'}}>Cause: {s.cause}</span></div>)}
                           </div>
                        </div>
                     )}
                     {trace.not_suitable_conditions?.length > 0 && (
                        <div style={{padding:'10px', background:'#fff5f5', borderRadius:'8px', border:'1px solid #fed7d7'}}>
                           <strong>Due to Medical Conditions:</strong>
                           <div className="multi-item-scroll" style={{maxHeight:'150px', marginTop:'10px'}}>
                              {trace.not_suitable_conditions.map((s:any) => <div key={s.id} style={{fontSize:'0.8rem', padding:'5px', borderBottom:'1px solid #eee'}}>📦 <strong>{s.id}</strong>: {s.name} <span className="badge red" style={{fontSize:'0.6rem'}}>Condition: {s.cause}</span></div>)}
                           </div>
                        </div>
                     )}
                  </div>
               </div>

               <div className="card" style={{borderTop:'4px solid var(--pfb-orange)'}}>
                  <h4>🍴 Personal Exclusions (YELLOW)</h4>
                  {trace.not_suitable_preferences?.length > 0 ? (
                    <div style={{padding:'10px', background:'#fffaf0', borderRadius:'8px', border:'1px solid #fef3c7', marginTop:'15px'}}>
                       <strong>Rejected by Preference:</strong>
                       <div className="multi-item-scroll" style={{maxHeight:'150px', marginTop:'10px'}}>
                          {trace.not_suitable_preferences.map((s:any) => <div key={s.id} style={{fontSize:'0.8rem', padding:'5px', borderBottom:'1px solid #eee'}}>📦 <strong>{s.id}</strong>: {s.name} <span className="badge yellow" style={{fontSize:'0.6rem'}}>Pref: No {s.cause}</span></div>)}
                       </div>
                    </div>
                  ) : <p style={{color:'#999', fontSize:'0.85rem', marginTop:'15px'}}>No preference-based rejections found.</p>}
               </div>
            </div>

            <div className="card" style={{borderTop:'4px solid var(--pfb-sidebar)'}}>
               <h4>📜 Clinical Knowledge Mapping</h4>
               <div style={{display:'flex', flexDirection:'column', gap:'15px', marginTop:'15px'}}>
                  {trace.conditions.map((c: any, i: number) => (
                    <div key={i} style={{background:'#fcfdfc', border:'1px solid #eee', padding:'15px', borderRadius:'8px'}}>
                       <div style={{fontWeight:'bold', color:'var(--pfb-sidebar)', marginBottom:'10px'}}>Condition: {c.name}</div>
                       {c.allergens.length === 0 ? <em style={{fontSize:'0.8rem', color:'#999'}}>No specific allergen blocks defined.</em> : (
                         <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                            {c.allergens.map((a: any, j: number) => (
                              <div key={j} style={{marginLeft:'15px', borderLeft:'2px solid #ddd', paddingLeft:'15px'}}>
                                 <div style={{fontSize:'0.85rem'}}>🚫 Blocked Allergen: <strong>{a.name}</strong></div>
                                 <div style={{fontSize:'0.75rem', color:'#666', marginTop:'5px'}}>
                                    Affected Ingredients: {a.ingredients.length > 0 ? a.ingredients.join(', ') : 'None yet mapped'}
                                 </div>
                              </div>
                            ))}
                         </div>
                       )}
                    </div>
                  ))}

                  {trace.direct_allergies.map((a: any, i: number) => (
                    <div key={i} style={{background:'#fff9f4', border:'1px solid #ffeeba', padding:'15px', borderRadius:'8px'}}>
                       <div style={{fontWeight:'bold', color:'#856404', marginBottom:'5px'}}>Declared Allergy: {a.allergen}</div>
                       <div style={{fontSize:'0.75rem', color:'#666'}}>
                          Affected Ingredients: {a.ings.length > 0 ? a.ings.join(', ') : 'None yet mapped'}
                       </div>
                    </div>
                  ))}

                  {trace.preferences.map((p: any, i: number) => (
                    <div key={i} style={{background:'#f9f9f9', border:'1px solid #ddd', padding:'10px', borderRadius:'8px'}}>
                       <div style={{fontWeight:'bold', color:'#666', fontSize:'0.85rem'}}>Preference: No {p.pref}</div>
                       <div style={{fontSize:'0.7rem', color:'#999'}}>Impacted ingredients: {p.ings.length > 0 ? p.ings.join(', ') : 'N/A'}</div>
                    </div>
                  ))}
               </div>
            </div>
          </div>
        </div>
      )}

      {trace && mode === 'product' && (
        <div style={{animation:'fadeIn 0.5s'}}>
          <h3>Clinical Deconstruction: <span style={{color:'var(--pfb-sidebar)'}}>{trace.sku_name}</span></h3>
          <div className="stats-grid" style={{marginTop:'20px'}}>
             <div className="card">
                <h4>🌳 Ingredient & Allergen Tree</h4>
                <div style={{marginTop:'15px'}}>
                   <table style={{width:'100%', borderCollapse:'collapse'}}>
                      <thead>
                        <tr style={{background:'#f4f7f6', fontSize:'0.85rem'}}>
                          <th style={{padding:'10px', textAlign:'left'}}>Ingredient Hierarchy</th>
                          <th style={{padding:'10px', textAlign:'left'}}>Detected Allergens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trace.ingredients.map((ing: any, i: number) => (
                          <tr key={i} style={{borderBottom:'1px solid #eee', background: ing.allergens.length > 0 ? '#fff5f5' : 'transparent'}}>
                             <td style={{padding:'10px'}}>
                                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                   {Array.from({length: ing.depth - 1}).map((_, idx) => <span key={idx} style={{width:'20px', height:'1px', background:'#ccc', display:'inline-block'}}></span>)}
                                   <span style={{fontSize:'0.9rem', fontWeight: ing.depth === 1 ? 'bold' : 'normal'}}>{ing.ingredient}</span>
                                </div>
                             </td>
                             <td style={{padding:'10px'}}>
                                {ing.allergens.map((a: any) => (
                                  <span key={a} className="badge red" style={{fontSize:'0.65rem', marginRight:'5px'}}>{a}</span>
                                ))}
                                {ing.allergens.length === 0 && <span style={{color:'#999', fontSize:'0.7rem'}}>Safe / No Allergens</span>}
                             </td>
                          </tr>
                        ))}
                      </tbody>
                   </table>
                </div>
             </div>

             <div className="card" style={{borderTop:'4px solid var(--pfb-sidebar)'}}>
                <h4>🏥 Clinical Risk Profile</h4>
                <p style={{fontSize:'0.85rem', color:'#666'}}>Medical conditions that would cause this product to be rejected:</p>
                <div style={{marginTop:'15px', display:'flex', flexDirection:'column', gap:'10px'}}>
                   {trace.potential_exclusions?.length > 0 ? (
                      trace.potential_exclusions.map((ex: any, i: number) => (
                         <div key={i} style={{padding:'10px', background:'#fff5f5', border:'1px solid #feb2b2', borderRadius:'8px'}}>
                            <strong>{ex.name}</strong>
                            <div style={{fontSize:'0.75rem', color:'#c53030'}}>Reason: Contains {ex.reason} ({ex.type})</div>
                         </div>
                      ))
                   ) : <p style={{color:'var(--pfb-green-dark)', fontWeight:'bold'}}>✅ No standard clinical exclusions found.</p>}
                </div>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AIIngredientLab = () => {
  const [name, setName] = useState('');
  const [analysis, setAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const analyze = async () => {
    if (!name) return;
    setLoading(true);
    setAnalysis(null);
    try {
      const res = await axios.get(`${API_URL}/ai/map-ingredient`, { params: { name } });
      setAnalysis(res.data);
    } catch { alert("AI analysis failed."); }
    setLoading(false);
  };

  const saveToMaster = async () => {
    setSaving(true);
    try {
      // Validate UNII: should be short and no spaces (avoid AI explanations in the field)
      const isUniiValid = analysis.unii_code && analysis.unii_code.length < 20 && !analysis.unii_code.includes(' ');
      const id = isUniiValid ? analysis.unii_code : `ING-${name.toUpperCase().replace(/\s+/g, '_')}`;
      
      await axios.post(`${API_URL}/ingredients`, {
        id,
        name: analysis.standardized_name || name,
        unii_code: isUniiValid ? analysis.unii_code : null,
        fdc_id: analysis.fdc_id,
        allergens: analysis.allergens.map((a: any) => a.id || a.name.toUpperCase())
      });
      alert("Ingredient discovery sent to the Clinical Review Queue for RDN approval.");
    } catch { alert("Failed to send to review queue."); }
    setSaving(false);
  };

  return (
    <div className="card">
      <h2>🤖 AI Ingredient Lab (Discovery)</h2>
      <p style={{color: '#666'}}>Use this lab to discover and map new ingredients using Google Gemini. The AI will suggest standardized naming, UNII codes, and allergen relationships.</p>
      
      <div style={{display:'flex', gap:'10px', marginTop:'20px'}}>
        <input 
          style={{flex:1, fontSize:'1.1rem'}} 
          placeholder="Enter raw ingredient name (e.g., 'Malt Extract', 'Sodium Caseinate'...)" 
          value={name} 
          onChange={e=>setName(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && analyze()}
        />
        <button onClick={analyze} disabled={loading} title="Ask the AI to identify this ingredient and the allergens it likely contains, before you add it to the catalog. Results still go to the review queue for a dietitian to approve.">{loading ? 'Analyzing...' : '🔍 Analyze with AI'}</button>
      </div>

      {analysis && analysis.status === 'ERROR' && (
        <div className="card" style={{borderLeft: '5px solid var(--danger)', marginTop:'20px'}}>
          <h3 style={{color: 'var(--danger)'}}>AI Analysis Failed</h3>
          <p>{analysis.message || "An unexpected error occurred during clinical mapping."}</p>
        </div>
      )}

      {analysis && analysis.status !== 'ERROR' && (
        <div style={{marginTop:'30px', animation: 'fadeIn 0.5s'}}>
          <div className="stats-grid">
            <div className="card" style={{borderTop:'4px solid var(--pfb-sidebar)'}}>
              <h3>AI Classification</h3>
              <p><strong>Standardized Name:</strong> {analysis.standardized_name}</p>
              <p><strong>UNII Code:</strong> {analysis.unii_code || 'N/A'}</p>
              <p><strong>FDC ID:</strong> {analysis.fdc_id || 'N/A'}</p>
              <p><strong>Confidence Score:</strong> {analysis.confidence ? (analysis.confidence * 100).toFixed(1) : '0.0'}%</p>
            </div>
            <div className="card" style={{borderTop:'4px solid var(--danger)'}}>
              <h3>Suggested Allergens</h3>
              {(!analysis.allergens || analysis.allergens.length === 0) ? <p>No allergens detected.</p> : (
                <div style={{display:'flex', flexWrap:'wrap', gap:'5px'}}>
                  {analysis.allergens.map((a: any, i: number) => (
                    <span key={i} className="badge red" title={a.clinical_justification || a.reasoning}>
                      {a.name} {a.category && a.category !== a.name && <small style={{opacity:0.8}}>({a.category})</small>}
                    </span>
                  ))}
                </div>
              )}
              <h4 style={{marginTop:'15px'}}>AI Justification</h4>
              <p style={{fontSize:'0.85rem', fontStyle:'italic'}}>{analysis.clinical_justification}</p>
            </div>
          </div>
          
          <div style={{marginTop:'20px', padding:'20px', background:'var(--pfb-green-light)', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div>
              <strong>Ready to add to Clinical Knowledge Graph?</strong>
              <br/><small>This will make the ingredient available for all future safety checks.</small>
            </div>
            <button onClick={saveToMaster} disabled={saving}>{saving ? 'Saving...' : '✅ Approve & Add to Master'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const SystemHealthMonitor = () => {
  const [health, setHealth] = useState<any>(null);
  const [traffic, setTraffic] = useState<any>(null);
  const [range, setRange] = useState<'24h' | '1w'>('24h');
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(() => {
    axios.get(`${API_URL}/system/health`).then(res => setHealth(res.data)).catch(err => {
      console.error("Health fetch failed:", err);
      setError("System Health API unreachable");
    });
    axios.get(`${API_URL}/stats/traffic`, { params: { range } }).then(res => setTraffic(res.data)).catch(err => {
      console.error("Traffic fetch failed:", err);
      setError("Traffic Metrics API unreachable");
    });
  }, [range]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchAll();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  if (error) return (
    <div className="card" style={{borderLeft:'5px solid var(--danger)'}}>
      <h3 style={{color:'var(--danger)', margin:0}}>Observability Offline</h3>
      <p style={{fontSize:'0.8rem', color:'#666'}}>{error}. Check backend logs for api_metrics table.</p>
    </div>
  );

  if (!health || !traffic) return <div className="card" style={{textAlign:'center', padding:'40px'}}><p>Initializing SafeGuard Observability Dashboard...</p></div>;

  const COLORS = ['#4da64d', '#2c3e50', '#e74c3c', '#f1c40f', '#3498db'];

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      {/* ROW 1: MISSION CRITICAL RESOURCES */}
      <div className="stats-grid">
        <div className="card" style={{borderTop:'4px solid var(--pfb-green)'}}>
          <h4 style={{margin:0, opacity:0.6, fontSize:'0.7rem', textTransform:'uppercase'}}>Compute & Memory</h4>
          <div style={{display:'flex', flexDirection:'column', gap:'15px', marginTop:'15px'}}>
             <div style={{display:'flex', justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:'1.8rem', fontWeight:'bold'}}>{health.cpu}%</div>
                  <div style={{fontSize:'0.7rem', color:'#666', textTransform:'uppercase'}}>CPU Usage</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:'1.8rem', fontWeight:'bold'}}>{health.cpu_load?.toFixed(2) || '0.00'}</div>
                  <div style={{fontSize:'0.7rem', color:'#666', textTransform:'uppercase'}}>Load Avg</div>
                </div>
             </div>
             
             <div style={{display:'flex', justifyContent:'space-between', borderTop:'1px solid #f0f0f0', paddingTop:'10px'}}>
                <div>
                  <div style={{fontSize:'1.1rem', fontWeight:'bold'}}>{health.disk.percent}%</div>
                  <div style={{fontSize:'0.7rem', color:'#666', textTransform:'uppercase'}}>Disk Filled</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:'1.1rem', fontWeight:'bold'}}>{((health.disk_iops || 0) / 1000).toFixed(1)}k</div>
                  <div style={{fontSize:'0.7rem', color:'#666', textTransform:'uppercase'}}>Disk IOPS</div>
                </div>
             </div>

             <div style={{width:'100%', height:'6px', background:'#eee', borderRadius:'3px', overflow:'hidden'}}>
                <div style={{width:`${health.memory.percent}%`, height:'100%', background:'var(--pfb-green)'}}></div>
             </div>
             <div style={{fontSize:'0.75rem', textAlign:'center', color:'#888'}}>RAM: {Math.round(health.memory.used / 1024 / 1024 / 1024 * 10) / 10}GB / {Math.round(health.memory.total / 1024 / 1024 / 1024)}GB</div>
          </div>
        </div>

        <div className="card" style={{borderTop:'4px solid var(--pfb-sidebar)'}}>
          <h4 style={{margin:0, opacity:0.6, fontSize:'0.7rem', textTransform:'uppercase'}}>Storage Architecture</h4>
          <div style={{display:'flex', flexDirection:'column', gap:'10px', marginTop:'15px'}}>
             <div style={{display:'flex', justifyContent:'space-between'}}>
                <span>Transactional (Postgres)</span>
                <strong>{health.db_transactional_size}</strong>
             </div>
             <div style={{display:'flex', justifyContent:'space-between'}}>
                <span>Knowledge Graph (Neo4j)</span>
                <strong>{health.db_graph_nodes} Nodes</strong>
             </div>
             <div style={{display:'flex', justifyContent:'space-between'}}>
                <span>System Uptime</span>
                <strong>{health.uptime_days} Days</strong>
             </div>
          </div>
        </div>

        <div className="card" style={{borderTop:'4px solid #e74c3c'}}>
          <h4 style={{margin:0, opacity:0.6, fontSize:'0.7rem', textTransform:'uppercase'}}>Top Traffic Sources (Client IP)</h4>
          <div style={{marginTop:'10px'}}>
             {(traffic.ips || []).slice(0, 3).map((t:any, i:number) => (
               <div key={i} style={{display:'flex', justifyContent:'space-between', fontSize:'0.85rem', padding:'5px 0', borderBottom:'1px solid #f9f9f9'}}>
                  <code style={{color:'#e74c3c'}}>{t.client_ip}</code>
                  <strong>{t.count} reqs</strong>
               </div>
             ))}
          </div>
        </div>
      </div>

      {/* ROW 2: VISUAL ANALYTICS */}
      <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:'20px'}}>
        <div className="card">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
            <h3 style={{margin:0}}>Traffic Intelligence</h3>
            <div style={{display:'flex', gap:'5px'}}>
               <button className={`btn-secondary ${range === '24h' ? 'active' : ''}`} style={{padding:'4px 10px', fontSize:'0.7rem'}} onClick={()=>setRange('24h')}>24h</button>
               <button className={`btn-secondary ${range === '1w' ? 'active' : ''}`} style={{padding:'4px 10px', fontSize:'0.7rem'}} onClick={()=>setRange('1w')}>1w</button>
            </div>
          </div>
          
          <div style={{height:'300px', width:'100%'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={traffic.api_series}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis 
                   dataKey="t" 
                   axisLine={false} tickLine={false} tick={{fontSize:10}} 
                   tickFormatter={(val) => range === '24h' ? new Date(val).getHours() + ':00' : new Date(val).toLocaleDateString()}
                />
                <YAxis axisLine={false} tickLine={false} tick={{fontSize:10}} />
                <Tooltip 
                  contentStyle={{borderRadius:'8px', border:'none', boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}
                />
                <Legend iconType="circle" wrapperStyle={{fontSize:'10px', paddingTop:'20px'}} />
                {/* API Calls in Green */}
                <Line type="monotone" name="API Calls" dataKey="total" stroke="var(--pfb-green)" strokeWidth={3} dot={false} />
                {/* Errors in Red */}
                <Line type="monotone" name="Errors" dataKey="errors" stroke="var(--danger)" strokeWidth={2} dot={false} />
                {/* Tokens in Blue */}
                <Line type="monotone" name="AI Tokens" dataKey="tokens" stroke="#3498db" strokeWidth={2} dot={false} data={traffic.token_series} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h3 style={{marginTop:0}}>Usage by Endpoint</h3>
          <div style={{height:'300px', width:'100%'}}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={traffic.paths.map((p:any) => ({ name: p.path, value: p.count }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {traffic.paths.map((entry:any, index:number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{marginTop:'10px'}}>
             {traffic.paths.map((e:any, i:number) => (
               <div key={i} style={{display:'flex', alignItems:'center', gap:'8px', fontSize:'0.7rem', marginBottom:'4px'}}>
                 <div style={{width:'8px', height:'8px', borderRadius:'50%', background:COLORS[i%COLORS.length]}}></div>
                 <span style={{flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{e.path}</span>
                 <strong>{e.count}</strong>
               </div>
             ))}
          </div>
        </div>
      </div>

      {/* ROW 3: AI ENGINE PERFORMANCE */}
      <div className="card" style={{borderTop:'4px solid #f1c40f', background:'#fffdf0'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
           <h3 style={{margin:0, color:'#d4ac0d'}}>🤖 Clinical AI Intelligence Engine Performance</h3>
        </div>
        <div className="stats-grid" style={{marginTop:'20px', gridTemplateColumns:'repeat(3, 1fr)'}}>
           <div style={{textAlign:'center', borderRight:'1px solid #eee'}}>
              <div style={{fontSize:'1.8rem', fontWeight:'bold', color:'var(--pfb-sidebar)'}}>{traffic.ai_performance?.avg_latency || 0}ms</div>
              <div style={{fontSize:'0.75rem', color:'#666', textTransform:'uppercase'}}>Average Response Latency</div>
           </div>
           <div style={{textAlign:'center', borderRight:'1px solid #eee'}}>
              <div style={{fontSize:'1.8rem', fontWeight:'bold', color:'var(--pfb-sidebar)'}}>{traffic.ai_performance?.total_calls || 0}</div>
              <div style={{fontSize:'0.75rem', color:'#666', textTransform:'uppercase'}}>Total Clinical Reasoning Tasks</div>
           </div>
           <div style={{textAlign:'center'}}>
              <div style={{fontSize:'1.8rem', fontWeight:'bold', color: (traffic.ai_performance?.error_rate || 0) > 5 ? 'var(--danger)' : 'var(--pfb-green)'}}>
                {traffic.ai_performance?.error_rate || 0}%
              </div>
              <div style={{fontSize:'0.75rem', color:'#666', textTransform:'uppercase'}}>AI Task Failure Rate</div>
           </div>
        </div>
        <div style={{marginTop:'15px', fontSize:'0.75rem', color:'#888', fontStyle:'italic', textAlign:'center'}}>
           Metrics aggregate clinical mapping, condition analysis, and graph repair operations.
        </div>
      </div>
    </div>
  );
};

const SentinelView = () => {
  const [configs, setConfigs] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [status, setStatus] = useState<any>({status: 'IDLE'});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [cfg, rep, st] = await Promise.all([
        axios.get(`${API_URL}/sentinel/config`),
        axios.get(`${API_URL}/sentinel/reports`),
        axios.get(`${API_URL}/sentinel/status`)
      ]);
      setConfigs(cfg.data);
      setReports(rep.data);
      setStatus(st.data);
      if (st.data.status === 'RUNNING') setTimeout(fetchData, 3000);
    } catch { console.error("Sentinel fetch failed"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const triggerSentinel = async () => {
    try {
      await axios.post(`${API_URL}/sentinel/trigger`);
      setStatus({status: 'RUNNING', last_item: 'Starting...'});
      setTimeout(fetchData, 1000);
    } catch { alert("Failed to trigger Sentinel"); }
  };

  const updateConfig = async (cfg: any) => {
    try {
      await axios.post(`${API_URL}/sentinel/config`, cfg);
      alert(`${cfg.category} config updated.`);
    } catch { alert("Update failed"); }
  };

  if (loading) return <div>Loading AI Sentinel...</div>;

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1 style={{margin:0}}>🤖 AI Sentinel: Autonomous Guardian</h1>
        <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
          {status.status === 'RUNNING' && (
            <div style={{color:'var(--warning)', fontWeight:'bold', animation:'pulse 1.5s infinite'}}>
              ⚡ Sentinel Active: {status.last_item}
            </div>
          )}
          <button onClick={triggerSentinel} disabled={status.status === 'RUNNING'} style={{background:'var(--pfb-sidebar)'}}>
            🚀 Run Manual Audit Now
          </button>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns: '2fr 1fr', gap: '20px'}}>
        <div className="card">
          <h2>⚙️ Audit Configuration & Prompts</h2>
          <p style={{fontSize:'0.9rem', color:'#666', marginBottom:'20px'}}>Configure unique AI behaviors and alerting webhooks for each system vertical.</p>
          
          <table style={{fontSize:'0.85rem'}}>
            <thead>
              <tr>
                <th>Category</th>
                <th>Custom AI Instructions (Prompt)</th>
                <th>Webhook Alert URL</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((cfg, idx) => (
                <tr key={cfg.category}>
                  <td style={{fontWeight:'bold', color:'var(--pfb-green-dark)'}}>{cfg.category}</td>
                  <td>
                    <textarea 
                      style={{fontSize:'0.8rem', padding:'5px', height:'60px'}} 
                      value={cfg.prompt} 
                      onChange={e => {
                        const newCfgs = [...configs];
                        newCfgs[idx].prompt = e.target.value;
                        setConfigs(newCfgs);
                      }}
                    />
                  </td>
                  <td>
                    <input 
                      style={{fontSize:'0.8rem', padding:'5px'}} 
                      value={cfg.webhook_url || ''} 
                      onChange={e => {
                        const newCfgs = [...configs];
                        newCfgs[idx].webhook_url = e.target.value;
                        setConfigs(newCfgs);
                      }}
                      placeholder="https://hooks.slack.com/..."
                    />
                  </td>
                  <td style={{textAlign:'center'}}>
                    <input 
                      type="checkbox" 
                      checked={cfg.is_enabled} 
                      onChange={e => {
                        const newCfgs = [...configs];
                        newCfgs[idx].is_enabled = e.target.checked;
                        setConfigs(newCfgs);
                      }}
                    />
                  </td>
                  <td>
                    <button className="btn-secondary" style={{padding:'4px 8px', fontSize:'0.7rem'}} onClick={() => updateConfig(cfg)} title="Save this Sentinel setting (schedule, AI model or alert webhook) used by the daily automated audit.">Save</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
          {/* AI SENTINEL AVATAR */}
          <div className="card" style={{
            padding: 0, 
            overflow: 'hidden', 
            height: '240px', 
            background: '#1e3d1e', 
            border: '2px solid rgba(255,255,255,0.1)',
            borderRadius: '12px'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              backgroundImage: 'url("https://img.magnific.com/fotos-premium/medical-robot-with-stethoscope-futuristic-healthcare-ai-concept_1294447-5536.jpg")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'flex-end'
            }}>
               <div style={{background: 'rgba(0,0,0,0.7)', padding: '12px', width: '100%', textAlign: 'center'}}>
                  <span style={{color: 'white', fontWeight: 'bold', fontSize: '0.8rem', letterSpacing: '2px'}}>SENTINEL CORE ALPHA-1</span>
               </div>
            </div>
          </div>

          <div className="card" style={{flex: 1}}>
            <h2>📋 Recent Audit Reports</h2>
            <div style={{maxHeight:'600px', overflowY:'auto'}}>
              {reports.map(r => (
                <div key={r.id} className="account-info" style={{
                  background: r.overall_status === 'HEALTHY' ? 'var(--pfb-green-light)' : 
                              r.overall_status === 'CRITICAL' ? '#fff5f5' : '#fff9f0',
                  borderLeft: `4px solid ${r.overall_status === 'HEALTHY' ? 'var(--pfb-green)' : 
                                         r.overall_status === 'CRITICAL' ? 'var(--danger)' : 'var(--warning)'}`,
                  padding: '12px', marginBottom:'10px'
                }}>
                  <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px'}}>
                    <strong>{new Date(r.timestamp).toLocaleString()}</strong>
                    <span className="badge" style={{
                      background: r.overall_status === 'HEALTHY' ? '#c6f6d5' : 
                                  r.overall_status === 'CRITICAL' ? '#fed7d7' : '#fefcbf'
                    }}>{r.overall_status}</span>
                  </div>
                  <div style={{fontSize:'0.8rem'}}>
                    {Object.keys(r.report_data).map(cat => {
                      const findings = r.report_data[cat].findings || [];
                      const fullText = findings.join('\n• ');
                      return (
                        <div key={cat} style={{marginTop:'5px', cursor:'help'}} title={`FULL FINDINGS FOR ${cat}:\n• ${fullText}`}>
                          <span style={{fontWeight:'bold'}}>{cat}:</span> {r.report_data[cat].status} - {findings.length > 0 ? findings[0].slice(0, 45) + '...' : 'No issues'}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {reports.length === 0 && <div style={{textAlign:'center', color:'#999', padding:'20px'}}>No reports generated yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ stats, userRole }: { stats: any[], userRole?: string }) => {
  const [selectedAllergen, setSelectedAllergen] = useState<string | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  // Operations sees the aggregate risk counts but may not drill into patient records (PHI);
  // the API denies it anyway — this keeps the UI from offering a click that only 403s.
  const canSeePatients = userRole !== 'Operations';

  const fetchAllergenMembers = async (name: string) => {
    if (!canSeePatients) return;
    setSelectedAllergen(name);
    setLoadingMembers(true);
    try {
      const res = await axios.get(`${API_URL}/stats/allergies/${name}/members`);
      setMembers(res.data);
    } catch { console.error("Failed to fetch members"); }
    setLoadingMembers(false);
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'30px'}}>
       <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <h1 style={{margin:0}}>SafeGuard Command Center</h1>
          <div className="badge green" style={{fontSize:'0.9rem', padding:'8px 15px'}}>System Status: Operational</div>
       </div>

       <div style={{display:'grid', gridTemplateColumns:'1fr 3fr', gap:'20px'}}>
          <div className="card" style={{height:'fit-content'}}>
             <h3 style={{marginTop:0}}>Clinical Risk Distribution</h3>
             <p style={{fontSize:'0.8rem', color:'#666', marginBottom:'20px'}}>Aggregated patient population by primary allergen risk.{canSeePatients ? ' Click to view patients.' : ''}</p>
             {stats.filter(s => s.count > 0).map((s,i)=>(
               <div 
                 key={i} 
                 onClick={() => fetchAllergenMembers(s.allergen)}
                 className="nav-item-compact"
                 title={canSeePatients ? undefined : 'Patient records are not available to the Operations role'}
                 style={{
                   display:'flex', justifyContent:'space-between', padding:'10px', 
                   cursor: canSeePatients ? 'pointer' : 'default', borderRadius:'8px', borderBottom:'1px solid #f0f0f0',
                   transition: 'background 0.2s'
                 }}
                 onMouseOver={(e) => e.currentTarget.style.background = '#f9f9f9'}
                 onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
               >
                 <span className="badge red" style={{fontSize:'0.7rem'}}>{s.allergen}</span> 
                 <strong style={{fontSize:'0.9rem'}}>{s.count} patients</strong>
               </div>
             ))}
             {stats.filter(s => s.count > 0).length === 0 && <p style={{textAlign:'center', color:'#999'}}>No active clinical risks.</p>}
          </div>
          <SystemHealthMonitor />
       </div>

       {selectedAllergen && (
         <div className="manual-overlay" onClick={() => setSelectedAllergen(null)}>
           <div className="card" style={{width:'500px', maxHeight:'80vh', overflowY:'auto'}} onClick={e => e.stopPropagation()}>
             <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
               <h2 style={{margin:0}}>Patients with {selectedAllergen} Allergy</h2>
               <button onClick={() => setSelectedAllergen(null)}>✕</button>
             </div>
             {loadingMembers ? <p>Loading list...</p> : (
               <table>
                 <thead><tr><th>ID</th><th>Name</th></tr></thead>
                 <tbody>
                   {members.map(m => (
                     <tr key={m.id}>
                       <td><code>{m.id}</code></td>
                       <td>{m.name}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             )}
           </div>
         </div>
       )}
    </div>
  );
};

const TaskMonitor = ({ userRole }: { userRole: string }) => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [expanded, setExpanded] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/system/jobs?limit=5`);
      setJobs(res.data);
    } catch { console.error("Job fetch failed"); }
  }, []);

  const clearJobs = async () => {
    try {
      await axios.post(`${API_URL}/system/jobs/clear`);
      fetchJobs();
    } catch { console.error("Clear jobs failed"); }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10000); 
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const activeJobs = jobs.filter(j => j.status === 'RUNNING' || j.status === 'PENDING');
  
  return (
    <div className="task-monitor-floating" style={{
      position: 'fixed', bottom: '20px', right: '20px', zIndex: 10000,
      width: expanded ? '320px' : '220px', transition: 'all 0.3s'
    }}>
      <div className="card" style={{ 
          margin: 0, padding: '12px', 
          borderTop: '4px solid ' + (activeJobs.length > 0 ? 'var(--pfb-green)' : '#ddd'), 
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          background: '#fff'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
          <h4 style={{ margin: 0, fontSize: '0.85rem', color: activeJobs.length > 0 ? 'var(--pfb-green-dark)' : '#666' }}>
            {activeJobs.length > 0 ? `⚡ ${activeJobs.length} Active Tasks` : '📋 System Tasks'}
          </h4>
          <span style={{ fontSize: '0.7rem' }}>{expanded ? '▼' : '▲'}</span>
        </div>

        {expanded && (
          <div style={{ marginTop: '12px', maxHeight: '350px', overflowY: 'auto', borderTop: '1px solid #eee', paddingTop: '10px' }}>
            {jobs.length === 0 && <div style={{ fontSize: '0.75rem', color: '#888', padding: '10px 0' }}>No recent background tasks.</div>}
            {jobs.filter(j => {
              // Rule: Don't show COMPLETED Snowflake Syncs if they were automated (launched by 'system')
              if (j.job_type === 'SNOWFLAKE_SYNC' && j.status === 'COMPLETED' && j.launched_by === 'system') return false;
              return true;
            }).map(j => (
              <div key={j.id} style={{ marginBottom: '12px', borderBottom: '1px solid #f9f9f9', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontWeight: 'bold' }}>
                  <span style={{color: '#444'}}>{j.job_type.replace(/_/g, ' ')}</span>
                  <span className={`badge ${j.status === 'COMPLETED' ? 'green' : j.status === 'FAILED' ? 'red' : ''}`} style={{fontSize: '0.6rem'}}>
                    {j.status}
                  </span>
                </div>
                {(j.status === 'RUNNING' || j.status === 'PENDING') && (
                  <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '3px', marginTop: '6px', overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      height: '100%', background: 'var(--pfb-green)', width: `${j.progress_pct || 5}%`,
                      transition: 'width 0.8s ease'
                    }}></div>
                  </div>
                )}
                <div style={{ fontSize: '0.65rem', color: '#777', marginTop: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {j.last_message || 'Initializing...'}
                </div>
                {userRole === 'Admin' && (
                  <div style={{ fontSize: '0.6rem', color: '#bbb', marginTop: '2px' }}>Launched by: {j.launched_by}</div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
              <button className="btn-secondary" style={{ flex: 1, padding: '6px', fontSize: '0.65rem' }} onClick={fetchJobs}>
                Refresh List
              </button>
              {jobs.some(j => j.status === 'COMPLETED' || j.status === 'FAILED') && (
                <button className="btn-secondary" style={{ flex: 1, padding: '6px', fontSize: '0.65rem', border: '1px solid #ff7675', color: '#d63031' }} onClick={clearJobs}>
                  Clear History
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NotificationsView = () => {
  const { toast, confirm } = useUI();
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [showAddHook, setShowAddHook] = useState(false);
  const [newHook, setNewHook] = useState({ name: '', url: '', hook_type: 'Teams', is_active: 1 });
  const [viewPayload, setViewPayload] = useState<any>(null);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/teams`);
      setTeams(res.data);
      if (selectedTeam) {
        const updated = res.data.find((t: any) => t.id === selectedTeam.id);
        if (updated) setSelectedTeam(updated);
      }
    } catch { toast("Failed to fetch teams", "error"); }
  }, [selectedTeam, toast]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/logs`, { params: { limit, offset: page * limit } });
      setLogs(res.data.items);
      setLogTotal(res.data.total);
    } catch { toast("Failed to fetch logs", "error"); }
  }, [page, limit, toast]);

  useEffect(() => { fetchTeams(); fetchLogs(); }, [page, fetchTeams, fetchLogs]);

  const handleCreateTeam = async () => {
    if (!newTeam.name) return toast("Name is required", "error");
    try {
      await axios.post(`${API_URL}/notifications/teams`, newTeam);
      setNewTeam({ name: '', description: '' });
      setShowAddTeam(false);
      fetchTeams();
      toast("Team created", "success");
    } catch { toast("Failed to create team", "error"); }
  };

  const handleDeleteTeam = (id: number) => {
    confirm("Delete this team?", "The team and all its delivery webhooks will be removed. Alerts routed to it will stop.", async () => {
      try {
        await axios.delete(`${API_URL}/notifications/teams/${id}`);
        if (selectedTeam?.id === id) setSelectedTeam(null);
        fetchTeams();
      } catch { toast("Delete failed", "error"); }
    }, { danger: true, confirmText: 'Delete team' });
  };

  const handleAddHook = async () => {
    if (!newHook.name || !newHook.url) return toast("Name and URL required", "error");
    try {
      await axios.post(`${API_URL}/notifications/teams/${selectedTeam.id}/webhooks`, newHook);
      setNewHook({ name: '', url: '', hook_type: 'Teams', is_active: 1 });
      setShowAddHook(false);
      fetchTeams();
      toast("Webhook added", "success");
    } catch { toast("Failed to add webhook", "error"); }
  };

  const handleDeleteHook = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/notifications/webhooks/${id}`);
      fetchTeams();
    } catch { toast("Delete failed", "error"); }
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1 style={{margin:0}}>Notification Management</h1>
        <button onClick={() => setShowAddTeam(true)} className="btn-primary" title="Create a team that will receive alerts (e.g. Clinical On-Call, IT). You then attach delivery webhooks to it.">🌐 Create New Team</button>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'300px 1fr', gap:'20px'}}>
        <div className="card" style={{height:'fit-content', padding:'10px'}}>
          <h3 style={{margin:'10px'}}>Groups & Teams</h3>
          {teams.map(t => (
            <div 
              key={t.id} 
              onClick={() => setSelectedTeam(t)}
              style={{
                padding:'15px', borderRadius:'8px', cursor:'pointer', marginBottom:'5px',
                background: selectedTeam?.id === t.id ? 'var(--pfb-sidebar)' : 'transparent',
                color: selectedTeam?.id === t.id ? 'white' : 'inherit',
                border: '1px solid ' + (selectedTeam?.id === t.id ? 'transparent' : '#eee')
              }}
            >
              <div style={{fontWeight:'bold'}}>{t.name}</div>
              <div style={{fontSize:'0.7rem', opacity:0.8}}>{t.webhooks.length} Active Webhooks</div>
              {selectedTeam?.id === t.id && (
                <button 
                  style={{background:'none', border:'none', color:'#ff7675', padding:0, fontSize:'0.7rem', marginTop:'5px', cursor:'pointer'}}
                  onClick={(e) => { e.stopPropagation(); handleDeleteTeam(t.id); }}
                >
                  Delete Team
                </button>
              )}
            </div>
          ))}
        </div>

        <div>
          {selectedTeam ? (
            <div className="card" style={{animation:'fadeIn 0.3s'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                <div>
                  <h2 style={{margin:0}}>{selectedTeam.name}</h2>
                  <p style={{margin:0, color:'#666', fontSize:'0.9rem'}}>{selectedTeam.description}</p>
                </div>
                <button onClick={() => setShowAddHook(true)} title="Add a delivery channel (e.g. a Slack/Teams incoming webhook URL) so this team actually receives the alerts.">🔌 Add Webhook</button>
              </div>

              <table className="registry-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Endpoint URL</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTeam.webhooks.map((h: any) => (
                    <tr key={h.id}>
                      <td style={{fontWeight:'bold'}}>{h.name}</td>
                      <td><span className="badge gray">{h.hook_type}</span></td>
                      <td style={{fontSize:'0.75rem', color:'#666', maxWidth:'300px', overflow:'hidden', textOverflow:'ellipsis'}}>{h.url}</td>
                      <td><span className={`badge ${h.is_active ? 'green' : 'red'}`}>{h.is_active ? 'ACTIVE' : 'DISABLED'}</span></td>
                      <td>
                        <div style={{display:'flex', gap:'5px'}}>
                          <button 
                            className="btn-secondary" 
                            style={{padding:'4px 8px', color:'var(--pfb-green)'}} 
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API_URL}/notifications/webhooks/${h.id}/test`);
                                toast(`Test result: ${res.data.status} (HTTP ${res.data.http_code})`, "info");
                                fetchLogs();
                              } catch { toast("Test request failed", "error"); }
                            }}
                          >
                            Test
                          </button>
                          <button className="btn-secondary" style={{padding:'4px 8px', color:'#d63031'}} onClick={() => handleDeleteHook(h.id)}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card" style={{textAlign:'center', padding:'80px', background:'#f9f9f9', border:'2px dashed #ddd'}}>
               <p style={{fontSize:'1.2rem', color:'#999'}}>Select a Notification Team to manage webhooks.</p>
            </div>
          )}

          <div className="card" style={{marginTop:'20px'}}>
            <h3 style={{marginTop:0}}>Notification Audit Trail</h3>
            <table className="registry-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Destination</th>
                  <th>Event Type</th>
                  <th>Result</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td style={{fontSize:'0.8rem'}}>{new Date(l.timestamp).toLocaleString()}</td>
                    <td>{l.webhook_name}</td>
                    <td><span className="badge gray">{l.event_type}</span></td>
                    <td><span className={`badge ${l.status_code === 200 || l.status_code === 202 ? 'green' : 'red'}`}>{l.status_code}</span></td>
                    <td>
                      <button className="btn-secondary" style={{padding:'4px 10px'}} onClick={() => setViewPayload(l.payload)}>View Data</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
              <span>Page {page + 1} of {Math.ceil(logTotal / limit)}</span>
              <button disabled={(page + 1) * limit >= logTotal} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </div>
        </div>
      </div>

      {showAddTeam && (
        <div className="modal-overlay">
          <div className="modal-content card" style={{maxWidth:'400px'}}>
            <h2>New Notification Team</h2>
            <div className="form-group">
              <label>Team Name</label>
              <input value={newTeam.name} onChange={e => setNewTeam({...newTeam, name: e.target.value})} placeholder="e.g. Clinical Ops, Tech Alerts" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={newTeam.description} onChange={e => setNewTeam({...newTeam, description: e.target.value})} />
            </div>
            <div style={{display:'flex', gap:'10px', marginTop:'20px'}}>
              <button onClick={handleCreateTeam} style={{flex:1}}>Create Team</button>
              <button onClick={() => setShowAddTeam(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAddHook && (
        <div className="modal-overlay">
          <div className="modal-content card" style={{maxWidth:'500px'}}>
            <h2>Add Webhook to {selectedTeam.name}</h2>
            <div className="form-group">
              <label>Hook Name</label>
              <input value={newHook.name} onChange={e => setNewHook({...newHook, name: e.target.value})} placeholder="e.g. MS Teams Channel" />
            </div>
            <div className="form-group">
              <label>Webhook URL</label>
              <input value={newHook.url} onChange={e => setNewHook({...newHook, url: e.target.value})} placeholder="https://outlook.office.com/webhook/..." />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select value={newHook.hook_type} onChange={e => setNewHook({...newHook, hook_type: e.target.value})}>
                <option value="Teams">Microsoft Teams</option>
                <option value="Opsgenie">Opsgenie</option>
                <option value="Generic">Generic JSON Webhook</option>
              </select>
            </div>
            <div style={{display:'flex', gap:'10px', marginTop:'20px'}}>
              <button onClick={handleAddHook} style={{flex:1}}>Connect Hook</button>
              <button onClick={() => setShowAddHook(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {viewPayload && (
        <div className="modal-overlay" onClick={() => setViewPayload(null)}>
          <div className="modal-content card" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <h2 style={{margin:0}}>Event Payload</h2>
              <button onClick={() => setViewPayload(null)} className="btn-secondary">Close</button>
            </div>
            <pre style={{background:'#f4f4f4', padding:'15px', borderRadius:'8px', marginTop:'20px', fontSize:'0.8rem', maxHeight:'400px', overflow:'auto'}}>
              {JSON.stringify(viewPayload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const AR_KNOWN_TEAMS = ['auth','billing','callcenter','carenet','data','delivery','escalations','executives','finance','intakes','marketing','outreach','packing','support','tech'];
const AR_SEVERITIES = ['critical','high','medium','low','info'];

const AlertRoutingView = ({ userRole }: any) => {
  const { toast } = useUI();
  const [rows, setRows] = useState<any[]>([]);
  const [teams, setTeams] = useState<string[]>(AR_KNOWN_TEAMS);
  const [validTeams, setValidTeams] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canManage = userRole === 'Admin';

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/system/aincident/routes`)
      .then(res => {
        const r = res.data.routes || {};
        setRows(Object.keys(r).sort().map(c => ({ condition: c, service_key: r[c].service_key, severity: r[c].severity })));
      })
      .catch(() => toast('Failed to load routing table', 'error'))
      .finally(() => setLoading(false));
    axios.get(`${API_URL}/system/aincident/services`)
      .then(res => {
        if (res.data.reachable && res.data.services?.length) { setTeams(res.data.services); setValidTeams(new Set(res.data.services)); }
      }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const update = (i: number, field: string, val: string) => setRows(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addRow = () => setRows([...rows, { condition: '', service_key: teams[0] || 'tech', severity: 'high' }]);
  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    const routes: any = {};
    for (const r of rows) {
      const c = (r.condition || '').trim();
      if (!c) { toast('Every row needs a condition name', 'error'); return; }
      if (routes[c]) { toast(`Duplicate condition "${c}"`, 'error'); return; }
      routes[c] = { service_key: r.service_key, severity: r.severity };
    }
    setSaving(true);
    try { const res = await axios.post(`${API_URL}/system/aincident/routes`, { routes }); toast(`Saved ${res.data.count} routes.`, 'success'); load(); }
    catch (e: any) { toast(e.response?.data?.detail || 'Save failed', 'error'); }
    setSaving(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
        <h2 style={{ margin: 0 }}>🚨 Alert Routing</h2>
        {canManage && <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-secondary" onClick={addRow}>➕ Add condition</button>
          <button onClick={save} disabled={saving}>{saving ? '…' : '💾 Save routing'}</button>
        </div>}
      </div>
      <div className="card" style={{ marginBottom: 15, fontSize: '0.85rem', color: '#555' }}>
        Each <strong>condition</strong> SafeGuard can detect routes to the <strong>team</strong> that must respond, at a <strong>severity</strong>
        {' '}(critical/high open an incident and page; medium/low/info are logged only). Team names must match the incident manager’s list —
        unknown teams are rejected on save so alerts are never silently dropped. <em>Thresholds (e.g. review-queue &gt; 48h) live in the engine and aren’t edited here yet.</em>
        {validTeams === null && <div style={{ color: '#a60', marginTop: 6 }}>⚠️ Couldn’t reach the incident manager to load live team names — showing the standard list. Set the API key in Settings, then reopen to validate.</div>}
      </div>
      {loading ? <p>Loading…</p> : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
              <th style={{ padding: 8 }}>Condition</th><th>Team (service_key)</th><th>Severity</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const bad = validTeams && !validTeams.has(r.service_key);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f2f2f2' }}>
                    <td style={{ padding: 6 }}><input value={r.condition} onChange={e => update(i, 'condition', e.target.value)} disabled={!canManage} style={{ width: '100%' }} placeholder="e.g. disk_full" /></td>
                    <td><select value={r.service_key} onChange={e => update(i, 'service_key', e.target.value)} disabled={!canManage} style={{ padding: 5, borderColor: bad ? '#d9534f' : undefined }}>
                      {teams.map(t => <option key={t} value={t}>{t}</option>)}
                      {bad && <option value={r.service_key}>{r.service_key} (unknown!)</option>}
                    </select></td>
                    <td><select value={r.severity} onChange={e => update(i, 'severity', e.target.value)} disabled={!canManage} style={{ padding: 5 }}>
                      {AR_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select></td>
                    <td>{canManage && <span onClick={() => removeRow(i)} title="Remove" style={{ cursor: 'pointer', color: '#d9534f', fontWeight: 700, padding: '0 8px' }}>×</span>}</td>
                  </tr>);
              })}
              {rows.length === 0 && <tr><td colSpan={4} style={{ padding: 12, color: '#999' }}>No routes defined.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DietsView = ({ userRole }: any) => {
  const { toast, confirm, prompt } = useUI();
  const [diets, setDiets] = useState<any[]>([]);
  const [allergens, setAllergens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addSel, setAddSel] = useState<Record<string, string>>({});
  const canManage = userRole === 'Admin' || userRole === 'RDN';

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/diets`)
      .then(res => setDiets(res.data || []))
      .catch(() => toast('Failed to load diets', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    axios.get(`${API_URL}/allergens`, { params: { limit: 1000 } })
      .then(res => setAllergens((res.data.items || []).filter((a: any) => !a.diet)))
      .catch(() => {});
  }, []);

  const createDiet = () => {
    prompt('New diet / policy', 'Name of the composite exclusion list (e.g. Vegetarian, Kosher, Halal).',
      async (name: string) => {
        if (!name.trim()) return;
        try { await axios.post(`${API_URL}/diets`, { name: name.trim() }); toast('Diet created', 'success'); load(); }
        catch (e: any) { toast(e.response?.data?.detail || 'Failed to create diet', 'error'); }
      }, { placeholder: 'e.g. Vegetarian', confirmText: 'Create' });
  };

  const addRoot = async (dietId: string) => {
    const child = addSel[dietId];
    if (!child) { toast('Pick an allergen first', 'error'); return; }
    try {
      await axios.post(`${API_URL}/diets/${encodeURIComponent(dietId)}/members`, { child_id: child });
      toast('Added', 'success'); setAddSel(s => ({ ...s, [dietId]: '' })); load();
    } catch (e: any) { toast(e.response?.data?.detail || 'Failed to add', 'error'); }
  };

  const removeRoot = (dietId: string, child: any) => {
    confirm('Remove from diet?', `“${child.name}” will no longer be excluded by this diet. Patients on this diet will stop being blocked from it (and its derivatives, unless excluded elsewhere).`,
      async () => {
        try { await axios.delete(`${API_URL}/diets/${encodeURIComponent(dietId)}/members`, { params: { child_id: child.id } }); load(); }
        catch { toast('Failed to remove', 'error'); }
      }, { danger: true, confirmText: 'Remove' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
        <h2 style={{ margin: 0 }}>🥗 Diets &amp; Policy Lists</h2>
        {canManage && <button onClick={createDiet}>➕ New Diet</button>}
      </div>
      <div className="card" style={{ marginBottom: 15, fontSize: '0.9rem', color: '#555' }}>
        A <strong>diet</strong> is a composite exclusion list. A patient assigned a diet is blocked from every allergen listed below <strong>and all of its derivatives</strong> (via the allergen hierarchy — e.g. excluding Milk also covers cheese, whey, butter). Managed by RDN/Admin; every change is audited and re-checks are recalculated.
      </div>
      {loading ? <p>Loading…</p> : diets.length === 0 ? <p style={{ color: '#888' }}>No diets defined yet.</p> :
        diets.map(d => (
          <div key={d.id} className="card" style={{ marginBottom: 15 }}>
            <h3 style={{ margin: '0 0 8px' }}>{d.name} <span style={{ fontSize: '0.7rem', color: '#999' }}>({(d.roots || []).length} exclusions)</span></h3>
            <div className="multi-item-scroll">
              {(d.roots || []).map((r: any) => (
                <span key={r.id} className="badge" style={{ background: '#eef2ff', color: '#3b4a8f' }}>
                  {r.name}{canManage && <span onClick={() => removeRoot(d.id, r)} title="Remove from diet" style={{ cursor: 'pointer', marginLeft: 5, fontWeight: 700 }}>×</span>}
                </span>
              ))}
              {(d.roots || []).length === 0 && <span style={{ color: '#aaa', fontSize: '0.85rem' }}>No exclusions yet.</span>}
            </div>
            {canManage && (
              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={addSel[d.id] || ''} onChange={e => setAddSel(s => ({ ...s, [d.id]: e.target.value }))} style={{ padding: '6px', minWidth: 240 }}>
                  <option value="">— add an allergen to exclude —</option>
                  {allergens.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <button className="btn-secondary" onClick={() => addRoot(d.id)}>Add exclusion</button>
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
};

const App = () => {
  const { toast, alert } = useUI();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || 'Admin');
  const [activeTab, setActiveTab] = useState('dash');
  const [showManual, setShowManual] = useState(false);
  const [manualSection, setManualSection] = useState<string | undefined>(undefined);
  const [showSyncReview, setShowSyncReview] = useState(false);
  const [stats, setStats] = useState<any[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // --- HIPAA COMPLIANCE: AUTOMATIC SESSION TERMINATION (30 MIN of inactivity) ---
  // Kept within the clinical 10-30 min norm for the §164.312(a)(2)(iii) automatic-logoff
  // safeguard. Activity (mouse/keyboard) resets it, so active work never triggers it; the
  // 8h JWT (rdn-api ACCESS_TOKEN_EXPIRE_MINUTES) is the separate absolute session cap.
  useEffect(() => {
    if (!token) return;
    let timeout: any;

    const resetTimer = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        alert("Session Expired", "Your session has expired due to inactivity for HIPAA compliance.");
        handleLogout();
      }, 30 * 60 * 1000); // 30 minutes
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    resetTimer();

    return () => {
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      if (timeout) clearTimeout(timeout);
    };
  }, [token, alert]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setToken(null);
  };

  useEffect(() => {
    if (token) {
      axios.get(`${API_URL}/stats/allergies`)
        .then(res => setStats(res.data))
        .catch(err => console.error("FAILED to fetch stats:", err.message));
    }
  }, [token]);

  useEffect(() => { setSelectedItem(null); }, [activeTab]);

  const handleShowManual = (section?: string) => {
    setManualSection(section);
    setShowManual(true);
  };

  const handleReviewClick = () => setActiveTab('sync');

  const handleExport = async (type: string) => {
    try {
      const res = await axios.get(`${API_URL}/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pfb_${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast("Export started successfully", "success");
    } catch { toast("Export failed", "error"); }
  };

  const handleLogin = async (e: any) => {
    e.preventDefault();
    const p = new URLSearchParams();
    p.append('username', username);
    p.append('password', password);
    if (mfaRequired) p.append('mfa_code', mfaCode);

    try {
      const res = await axios.post(`${API_URL}/token`, p);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('role', res.data.role);
      setToken(res.data.access_token);
      setUserRole(res.data.role);
      setMfaRequired(false);
      toast("Welcome back!", "success");
    } catch (err: any) {
      if (err.response?.status === 403 && err.response?.data?.detail === "MFA_REQUIRED") {
        setMfaRequired(true);
      } else {
        toast("Login failed. Please check your credentials.", "error");
      }
    }
  };

  if (!token) return (
    <div className="container" style={{display:'flex', height:'100vh', flexDirection:'column', justifyContent:'center', alignItems:'center', background: '#f4f7f6'}}>
      <div style={{textAlign: 'center', marginBottom: '30px'}}>
        <img src="/PFBSafeGuard_smlogo.png" alt="PFB SafeGuard" style={{width: '180px'}} />
        <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--pfb-sidebar)', marginTop: '10px', letterSpacing: '2px'}}>SAFEGUARD</div>
      </div>
      <div className="card" style={{width:'380px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)'}}>
        <form onSubmit={handleLogin}>
          <h2 style={{textAlign: 'center', marginBottom: '30px', color: 'var(--pfb-sidebar)'}}>Clinical Portal Access</h2>
          {!mfaRequired ? (
            <>
              <div className="form-group">
                <label>Username</label>
                <input placeholder="Enter your clinical ID" onChange={e=>setUsername(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" placeholder="Enter password" onChange={e=>setPassword(e.target.value)} />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label>MFA Verification Code</label>
              <input 
                placeholder="000 000" 
                onChange={e=>setMfaCode(e.target.value)} 
                autoFocus 
                style={{textAlign: 'center', fontSize: '1.5rem', letterSpacing: '5px'}}
                maxLength={6}
              />
              <p style={{fontSize: '0.8rem', color: '#666', marginTop: '10px'}}>Open your authenticator app to get the code.</p>
            </div>
          )}
          <button style={{width:'100%', marginTop: '15px', padding: '15px'}}>
            {mfaRequired ? 'Verify & Login' : 'Authenticate & Enter'}
          </button>
          {mfaRequired && (
             <button type="button" className="btn-secondary" style={{width:'100%', marginTop: '10px'}} onClick={()=>setMfaRequired(false)}>Back to Password</button>
          )}
        </form>

        <div style={{marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{textAlign: 'center', flex: 1}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--pfb-green)', marginBottom: '5px'}}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <path d="M8 11h8"></path>
              <path d="M12 7v8"></path>
            </svg>
            <div style={{fontSize: '0.6rem', fontWeight: 'bold', color: '#666'}}>HIPAA</div>
            <div style={{fontSize: '0.5rem', color: '#999'}}>COMPLIANT</div>
          </div>
          <div style={{textAlign: 'center', flex: 1, borderLeft: '1px solid #eee', borderRight: '1px solid #eee'}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--pfb-green)', marginBottom: '5px'}}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <div style={{fontSize: '0.6rem', fontWeight: 'bold', color: '#666'}}>SOC2 TYPE II</div>
            <div style={{fontSize: '0.5rem', color: '#999'}}>CERTIFIED</div>
          </div>
          <div style={{textAlign: 'center', flex: 1}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--pfb-green)', marginBottom: '5px'}}>
              <polyline points="16 18 22 12 16 6"></polyline>
              <polyline points="8 6 2 12 8 18"></polyline>
            </svg>
            <div style={{fontSize: '0.6rem', fontWeight: 'bold', color: '#666'}}>AES-256</div>
            <div style={{fontSize: '0.5rem', color: '#999'}}>ENCRYPTION</div>
          </div>
        </div>
      </div>
      <p style={{marginTop: '20px', fontSize: '0.8rem', color: '#999'}}>PFB SafeGuard v2.4 | Clinical Decision Support System</p>
    </div>
  );

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src="/PFBSafeGuard_smlogo.png" alt="PFB" style={{width: '100px', marginBottom: '5px'}} />
          <div style={{fontSize: '0.7rem', opacity: 0.6, letterSpacing: '2px'}}>SAFEGUARD</div>
        </div>
        <nav className="sidebar-nav">
          <div style={{color:'rgba(255,255,255,0.4)', fontSize:'0.6rem', padding:'10px 15px 2px', fontWeight:'bold', letterSpacing:'1px'}}>OPERATIONS</div>
          <div className="nav-item" title={SCREEN_HELP.dash?.nav} onClick={()=>setActiveTab('dash')}>📊 Dashboard</div>
          <div className="nav-item" title={SCREEN_HELP.tests?.nav} onClick={()=>setActiveTab('tests')}>🛡️ Operational Checks</div>
          {(userRole === 'Admin' || userRole === 'RDN' || userRole === 'User' || userRole === 'Operations') && (
            <>
              {/* Operations is a query-only role with NO access to patient records (PHI) */}
              {userRole !== 'Operations' && <div className="nav-item" title={SCREEN_HELP.members?.nav} onClick={()=>setActiveTab('members')}>👥 Patients</div>}
              <div className="nav-item" title={SCREEN_HELP.skus?.nav} onClick={()=>setActiveTab('skus')}>📦 Products (SKU)</div>
              <div className="nav-item" title={SCREEN_HELP.partners?.nav} onClick={()=>setActiveTab('partners')}>🏢 Partners</div>
              {/* Operational staff also manage ingredients directly and suggest medical items for RDN review */}
              {userRole === 'User' && <div className="nav-item" title={SCREEN_HELP.ingredients?.nav} onClick={()=>setActiveTab('ingredients')}>🧪 Ingredients</div>}
              <div className="nav-item" title={SCREEN_HELP.op_intake?.nav} onClick={()=>setActiveTab('op_intake')}>➕ Suggest Allergen / Condition</div>
            </>
          )}

          {(userRole === 'Admin' || userRole === 'RDN') && (
            <>
              <div style={{color:'rgba(255,255,255,0.4)', fontSize:'0.6rem', padding:'10px 15px 2px', fontWeight:'bold', letterSpacing:'1px'}}>CLINICAL MASTERS</div>
              <div className="nav-item" title={SCREEN_HELP.explorer?.nav} onClick={()=>setActiveTab('explorer')}>🛡️ Clinical Checks</div>
              <div className="nav-item" title={SCREEN_HELP.sync?.nav} onClick={()=>{setActiveTab('sync'); setShowSyncReview(true);}}>📋 Clinical Review Queue</div>
              <div className="nav-item" title={SCREEN_HELP.ingredients?.nav} onClick={()=>setActiveTab('ingredients')}>🧪 Ingredients</div>
              <div className="nav-item" title={SCREEN_HELP.allergens?.nav} onClick={()=>setActiveTab('allergens')}>⚠️ Allergens</div>
              <div className="nav-item" title={SCREEN_HELP.conditions?.nav} onClick={()=>setActiveTab('conditions')}>🏥 Conditions</div>
              <div className="nav-item" title={SCREEN_HELP.diets?.nav} onClick={()=>setActiveTab('diets')}>🥗 Diets &amp; Policies</div>
            </>
          )}

          {(userRole === 'Admin' || userRole === 'Auditor' || userRole === 'RDN') && (
            <>
              <div style={{color:'rgba(255,255,255,0.4)', fontSize:'0.6rem', padding:'10px 15px 2px', fontWeight:'bold', letterSpacing:'1px'}}>AI & AUTOMATIZATIONS</div>
              <div className="nav-item" title={SCREEN_HELP.ai_lab?.nav} onClick={()=>setActiveTab('ai_lab')}>🧪 AI Ingredient Lab</div>
              <div className="nav-item" title={SCREEN_HELP.ai_graph?.nav} onClick={()=>setActiveTab('ai_graph')}>⚙️ AI Graph Engineering</div>
              <div className="nav-item" title={SCREEN_HELP.ai_sentinel?.nav} onClick={()=>setActiveTab('ai_sentinel')}>🤖 AI Sentinel (Daily Audit)</div>
              <div className="nav-item" title={SCREEN_HELP.ai_audit?.nav} onClick={()=>setActiveTab('ai_audit')}>🤖 AI Clinical Audit</div>

              <div style={{color:'rgba(255,255,255,0.4)', fontSize:'0.6rem', padding:'10px 15px 2px', fontWeight:'bold', letterSpacing:'1px'}}>GOVERNANCE & DATA</div>
              {(userRole === 'Admin' || userRole === 'RDN') && <div className="nav-item" title="Pull clinical data from connected external systems" onClick={()=>{setActiveTab('sync'); setShowSyncReview(false);}}>🔄 External Sync</div>}
              <div className="nav-item" title={SCREEN_HELP.graph?.nav} onClick={()=>setActiveTab('graph')}>🕸️ Knowledge Graph</div>
              <div className="nav-item" title={SCREEN_HELP.governance?.nav} onClick={()=>setActiveTab('governance')}>🏛️ Identity Governance</div>
              <div className="nav-item" title={SCREEN_HELP.compliance?.nav} onClick={()=>setActiveTab('compliance')}>🛡️ Compliance</div>
              <div className="nav-item" title={SCREEN_HELP.logs?.nav} onClick={()=>setActiveTab('logs')}>📋 Audit Logs</div>
              <div className="nav-item" title={SCREEN_HELP.change_log?.nav} onClick={()=>setActiveTab('change_log')}>📝 Change Log (who/what/before→after)</div>
              <div className="nav-item" title={SCREEN_HELP.safety_audit?.nav} onClick={()=>setActiveTab('safety_audit')}>🔎 Safety-Check Forensics</div>
              {(userRole === 'Admin' || userRole === 'RDN') && <div className="nav-item" title={SCREEN_HELP.data_quality?.nav} onClick={()=>setActiveTab('data_quality')}>🧹 Data Quality</div>}
              <div className="nav-item" title={SCREEN_HELP.notifications?.nav} onClick={()=>setActiveTab('notifications')}>🔔 Notifications</div>
              {userRole === 'Admin' && <div className="nav-item" title={SCREEN_HELP.lifecycle?.nav} onClick={()=>setActiveTab('lifecycle')}>♻️ Data Lifecycle</div>}
            </>
          )}

          <div style={{color:'rgba(255,255,255,0.4)', fontSize:'0.6rem', padding:'10px 15px 2px', fontWeight:'bold', letterSpacing:'1px'}}>SYSTEM</div>
          {userRole === 'Admin' && <div className="nav-item" title={SCREEN_HELP.users?.nav} onClick={()=>setActiveTab('users')}>👤 User Access</div>}
          {userRole === 'Admin' && <div className="nav-item" title={SCREEN_HELP.service_accounts?.nav} onClick={()=>setActiveTab('service_accounts')}>🔐 Service Accounts</div>}
          <div className="nav-item" title={SCREEN_HELP.mfa_setup?.nav} onClick={()=>setActiveTab('mfa_setup')}>🔐 My Security (MFA)</div>
          <a href={`${API_URL}/docs`} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none', color:'inherit'}}>
            <div className="nav-item" title="Interactive API reference for developers (opens in a new tab)">🔌 API Documentation</div>
          </a>
          {userRole === 'Admin' && <div className="nav-item" title={SCREEN_HELP.alert_routing?.nav} onClick={()=>setActiveTab('alert_routing')}>🚨 Alert Routing</div>}
          {userRole === 'Admin' && <div className="nav-item" title={SCREEN_HELP.settings?.nav} onClick={()=>setActiveTab('settings')}>⚙️ Settings</div>}
          <div className="nav-item" title={SCREEN_HELP.help?.nav} onClick={()=>setActiveTab('help')}>❓ System Help</div>
          <div className="nav-item" title="Open the full written user manual" onClick={()=>setShowManual(true)}>📖 System Manual</div>
        </nav>
        <button onClick={()=>{localStorage.removeItem('token'); localStorage.removeItem('role'); setToken(null);}} style={{marginTop:'auto'}} className="btn-logout">Logout</button>
      </aside>
      <main className="main-content">
        <AssistantLauncher activeTab={activeTab} onNav={setActiveTab} />
        <TaskMonitor userRole={userRole} />
        <ScreenHelp active={activeTab} />
        {showManual && <SystemManual onClose={()=>{setShowManual(false); setManualSection(undefined);}} initialSection={manualSection} />}
        {activeTab === 'dash' && <Dashboard stats={stats} userRole={userRole} />}
        {activeTab === 'explorer' && <ClinicalExplorer />}
        {activeTab === 'tests' && <><TestsView userRole={userRole} /><BatchFileCheckView /></>}
        
        {activeTab === 'members' && (
          selectedItem ? <MemberDetail 
                            member={selectedItem} 
                            onBack={() => setSelectedItem(null)} 
                            onEdit={(m) => {
                               // A small hack: set a flag or just use the same logic that triggers TableManager Edit
                               // For now, let's just alert that it's linked
                               alert("To edit " + m.name + ", please go back to the Patient List and click the 'Edit' button on their row. The 'Food Preferences' field is also available there now.");
                            }} 
                         /> :
          <MemberMasterView userRole={userRole} onSelect={setSelectedItem} onShowManual={handleShowManual} onReviewClick={handleReviewClick} />
        )}


        {activeTab === 'partners' && (
          <TableManager title="Clinical Partners" type="partners" userRole={userRole} columns={[{key:'id', label:'ID'}, {key:'name', label:'Name', help:'The supplier / partner organization that provides products in your catalog.'}, {key:'contact_email', label:'Contact Email', help:'Who to reach at this partner about their products.'}]} />
        )}

        {activeTab === 'skus' && (
          <SkuMasterView userRole={userRole} handleExport={handleExport} onReviewClick={handleReviewClick} />

        )}
        
        {activeTab === 'ingredients' && (
          selectedItem ? <IngredientEvidenceView ingredient={selectedItem} onBack={() => setSelectedItem(null)} /> :
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
             <div style={{display:'flex', justifyContent:'flex-end'}}>
                <label style={{display:'flex', alignItems:'center', gap:'8px', fontSize:'0.85rem', cursor:'pointer', background:'#fff', padding:'8px 15px', borderRadius:'8px', border:'1px solid #ddd'}}>
                   <input type="checkbox" onChange={(e) => { 
                      // Use a trick to force refresh TableManager via key or just let it re-render if we put state here
                      window.dispatchEvent(new CustomEvent('pfb-filter-used', {detail: e.target.checked}));
                   }} />
                   Show only ingredients used in active Products (SKUs)
                </label>
             </div>
             <IngredientsViewWrapper userRole={userRole} onSelect={setSelectedItem} onReviewClick={handleReviewClick} />
          </div>
        )}
        {activeTab === 'ai_lab' && <AIIngredientLab />}

        {activeTab === 'allergens' && <AllergensViewWrapper userRole={userRole} onReviewClick={handleReviewClick} />}
        {activeTab === 'diets' && <DietsView userRole={userRole} />}
        {activeTab === 'conditions' && <TableManager title="Clinical Conditions" type="conditions" userRole={userRole} onReviewClick={handleReviewClick} columns={[
          {key:'id', label:'ID', width: '120px'},
          {key:'name', label:'Name'},
          {key:'max_calories', label:'Max Cal /Meal', type: 'number', help:'Maximum calories allowed per meal for a patient with this condition. A product above this is blocked. Leave blank for no calorie limit.'},
          {key:'max_sodium_mg', label:'Max Na /Meal', type: 'number', help:'Maximum sodium (salt) in milligrams per meal — key for kidney disease, hypertension and heart failure. Blank = no limit.'},
          {key:'max_carbs_per_meal', label:'Max Carbs /Meal', type: 'number', help:'Maximum carbohydrates (grams) per meal — key for diabetes. Blank = no limit.'},
          {key:'max_potassium_mg', label:'Max K /Meal', type: 'number', help:'Maximum potassium (mg) per meal — key for kidney disease (CKD). Blank = no limit.'},
          {key:'max_protein_g', label:'Target Pro /Meal', type: 'number', help:'Protein target/limit (grams) per meal. Blank = no limit.'},
          {key:'max_fat_g', label:'Max Fat /Meal', type: 'number', help:'Maximum fat (grams) per meal. Blank = no limit.'},
          {key:'max_sugar_g', label:'Max Sugar /Meal', type: 'number', help:'Maximum sugar (grams) per meal. Blank = no limit.'},
          {key:'max_fiber_g', label:'Fiber Policy', type: 'number', help:'Fiber threshold (grams) per meal. Blank = no policy.'},
          {key:'restricted_ingredients', label:'Restricted Ingredients', type: 'multi-select-ingredients', help:'Specific ingredients a patient with this condition must avoid, on top of the nutrient limits above.'},
          {key:'updated_at', label:'Last Updated', width: '150px', help:'When this condition was last changed. Click to sort.'},
          {key:'updated_by', label:'By', width: '140px', help:'Who last changed it (👤 person / ⚙️ system).'}
        ]} />}
        {activeTab === 'safety_audit' && <SafetyAuditView />}
        {activeTab === 'op_intake' && <OperationalIntakeView />}
        {activeTab === 'change_log' && <ChangeLogView />}
        {activeTab === 'data_quality' && <DataQualityView />}
        {activeTab === 'graph' && <GraphViz />}
        {activeTab === 'ai_graph' && <AIGraphEngineering />}
        {activeTab === 'governance' && <ClinicalIdentityGovernance onShowManual={handleShowManual} />}
        {activeTab === 'users' && <TableManager title="User Access" type="users" userRole={userRole} columns={[{key:'username', label:'Username'}, {key:'password', label:'New Password', help:'Set a password for a new user, or type a new one to reset an existing user’s password. Leave blank to keep the current password.'}, {key:'role', label:'Role', help:'Controls what this person can see and do — Admin (everything), RDN (clinical data + approvals), Auditor (read-only + audit trails), User (basic checks + operational catalog), Operations (query-only: checks and the product catalog, may suggest allergens for RDN review, NO access to patient records).'}]} />}
        {activeTab === 'mfa_setup' && <MfaSetup onComplete={()=>setActiveTab('dash')} />}
        {activeTab === 'sync' && (
          showSyncReview ? <SyncReview onBack={() => setShowSyncReview(false)} /> :
          <SyncDashboard onEnterReview={() => setShowSyncReview(true)} handleExport={handleExport} />
        )}
        {activeTab === 'compliance' && <ComplianceView />}
        {activeTab === 'logs' && (
          <TableManager 
            title="System Audit Logs" 
            type="audit-logs" 
            userRole={userRole} 
            columns={[
              {key:'timestamp', label:'Time', width: '180px', help:'When the action happened (server time).'},
              {key:'user_id', label:'User', help:'Who performed it — a person, or an automated process (system:…).'},
              {key:'action', label:'Action', help:'What happened (e.g. CREATE, UPDATE, DELETE, LOGIN, APPROVE). For a friendlier before→after view of clinical edits, use Change Log.'},
              {key:'entity_type', label:'Entity', help:'The kind of record affected (Allergen, Ingredient, Member, ServiceAccount…).'},
              {key:'entity_id', label:'Target ID', help:'The specific record that was affected.'},
              {key:'member_id', label:'Patient', help:'The patient involved, if the action related to one.'},
              {key:'sku_id', label:'SKU', help:'The product involved, if any.'}
            ]} 
          />
        )}
        {activeTab === 'notifications' && <NotificationsView />}
        {activeTab === 'ai_sentinel' && <SentinelView />}
        {activeTab === 'ai_audit' && <AIAuditorView />}
        {activeTab === 'lifecycle' && <DataLifecycleView />}
        {activeTab === 'service_accounts' && <ServiceAccountsView />}
        {activeTab === 'alert_routing' && <AlertRoutingView userRole={userRole} />}
        {activeTab === 'settings' && <Settings />}
        {activeTab === 'help' && <HelpView />}
        </main>

    </div>
  );
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <UIProvider>
    <App />
  </UIProvider>
);
